import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, ComposedChart,
  PieChart, Pie, Cell, LabelList,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine, ReferenceArea
} from "recharts";
import { motion, AnimatePresence, animate } from "framer-motion";
import {
  PiggyBank, ChevronDown, LogOut, Thermometer, Zap, Droplets, SlidersHorizontal, Radio, BarChart3, CloudSun, Euro, Leaf, Bell, MapPin, Building2,
  Fuel, Car, Globe2, FileText, Target, CheckCircle2, AlertTriangle, Wrench, Award, Medal, Trophy, Sprout,
  TreeDeciduous, Trees, CalendarCheck, TrendingUp, TrendingDown, Plug, Snowflake, Info, Router as RouterIcon,
} from "lucide-react";
import api from "./services/api";
import useApi from "./hooks/useApi";
import { LangProvider, useT, useLang, SUPPORTED_LANGS } from "./i18n";
import LandingPage from "./LandingPage";
import InstallPage from "./InstallPage";
import DiagnosticoPage from "./DiagnosticoPage";
import { DEMO_USERS, findUser, filterAccessible, mergeWithDemoClients } from "./demoUsers";

// ═══════════════════════════════════════════════════════════════
// EasyRural IoT — Frontend v9 (conectado a Node-RED API)
// Backend: Node-RED + InfluxDB Cloud + APIs externas (AEMET, ESIOS, Datadis)
// ═══════════════════════════════════════════════════════════════

// ─── COLORS ─────────────────────────────────────────────────────
// Paleta EXACTA del CSS de easyrural.net (extraída de app-44f05988.css)
// --brand: #406020 (verde oliva oscuro)
// --bdark: #283226 (texto principal)
// --lightbrand: #EBF2E4 (fondo verde muy claro)
// --dark: #283226 (footer)
const COLORS = {
  brand: "#406020",        // verde oliva del logo y CTAs
  brandDark: "#406020",    // hover / sidebar
  brandLight: "#6F8F4E",   // tono más claro para charts
  bdark: "#283226",        // texto principal del SaaS
  lightbrand: "#EBF2E4",   // verde muy claro de secciones
  darkBg: "#283226",       // gris oscuro del footer
  // Aliases legacy para no romper código existente
  primary: "#406020",
  primaryLight: "#6F8F4E",
  primaryDark: "#283226",
  accent: "#B9CBA3",
  warning: "#C08B2C",
  danger: "#B4532F",
  info: "#3D6E8E",
  wine: "#8E3C20",
  wineLight: "#B4532F",
  cream: "#FAFAFA",
  mustard: "#C08B2C",
  brown: "#283226",
  // Franjas tarifarias (mantener verdes para "buena" / rojo para "mala")
  punta: "#8E3C20", llano: "#C08B2C", valle: "#6F8F4E",
  faseA: "#6F8F4E", faseB: "#C08B2C", faseC: "#3D6E8E",
};

// ─── PRIMITIVAS DE DISEÑO ───────────────────────────────────────
// Marca de estado sobria: cuadradito de color + etiqueta en versalitas (sin emojis ni burbujas).
const TONE = { good: "#6F8F4E", warn: "#C08B2C", bad: "#B4532F", info: "#3D6E8E", off: "#A9AEA2" };
function StatusMark({ tone = "off", label, size = 8 }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
      <span aria-hidden style={{ width: size, height: size, background: TONE[tone] || TONE.off, borderRadius: 1.5, display: "inline-block" }} />
      {label && <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: 0.8, textTransform: "uppercase", color: "#4B5347" }}>{label}</span>}
    </span>
  );
}
// Icono en recuadro con tinta suave
function IconTile({ icon: C, tone = "#406020", bg = "#EBF2E4", size = 40 }) {
  return (
    <span aria-hidden style={{ width: size, height: size, borderRadius: 12, background: bg, color: tone, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {C ? <C size={Math.round(size * 0.48)} strokeWidth={1.6} /> : null}
    </span>
  );
}
// Número que cuenta al aparecer (respeta formato: "1.234", "€12.5", "64 €")
function CountUp({ value, duration = 0.9 }) {
  const ref = useRef(null);
  const str = String(value ?? "");
  const m = str.match(/-?\d[\d.,]*/);
  useEffect(() => {
    const el = ref.current;
    if (!el || !m) return;
    const raw = m[0];
    const dec = raw.includes(",") && !raw.includes(".") ? raw.split(",")[1]?.length || 0 : (raw.split(".")[1] || "").length;
    const target = parseFloat(raw.replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", "."));
    if (!Number.isFinite(target)) { el.textContent = str; return; }
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) { el.textContent = str; return; }
    const ctl = animate(0, target, { duration, ease: [0.2, 0.7, 0.2, 1], onUpdate: (v) => {
      const txt = raw.includes(",") && !raw.includes(".") ? v.toFixed(dec).replace(".", ",") : v.toFixed(dec);
      el.textContent = str.replace(raw, txt);
    } });
    return () => ctl.stop();
    // eslint-disable-next-line
  }, [str]);
  return <span ref={ref}>{str}</span>;
}

const tooltipStyle = {
  background: "rgba(30,35,29,0.96)",
  border: "1px solid #283226",
  borderRadius: 10,
  fontSize: 12,
  color: "#fff",
};
// Estilos auxiliares para items/labels del tooltip dark de recharts
// (recharts pinta los items y el label en negro por defecto, ignorando
// el `color` del contentStyle).
const tooltipItemStyle = { color: "#fff" };
const tooltipLabelStyle = { color: "#B9CBA3", fontWeight: 600, marginBottom: 4 };

// ─── ICONS ──────────────────────────────────────────────────────
const I = {
  status: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
  map: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  dash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  bar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>,
  doc: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  predict: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18M9 17l3-3 4 4 5-7"/></svg>,
  leaf: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z"/></svg>,
  chev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>,
  cal: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  home: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>,
  key: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.78 7.78 5.5 5.5 0 0 1 7.78-7.78zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>,
  bell: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  tool: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  grid: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L4 5v7c0 5 3.4 9.4 8 10 4.6-.6 8-5 8-10V5l-8-3z"/><path d="M9 12l2 2 4-4"/></svg>,
};

// Items navegables — la `label` se resuelve con t() en render para soportar i18n
const NAV_ACTIVE_ITEMS = [
  { id: "adminOverview", labelKey: "nav.adminOverview", icon: I.grid, adminOnly: true, section: "Gestión" },
  { id: "users", labelKey: "nav.users", icon: I.shield, adminOnly: true, section: "Gestión" },
  { id: "map", labelKey: "nav.map", icon: I.map, section: "Gestión" },
  { id: "newClient", labelKey: "nav.newClient", icon: I.home, section: "Gestión" },
  { id: "dashboard", labelKey: "nav.dashboard", icon: I.dash, section: "Alojamiento" },
  { id: "status", labelKey: "nav.status", icon: I.status, section: "Alojamiento" },
  { id: "consumption", labelKey: "nav.consumption", icon: I.bar, section: "Alojamiento" },
  { id: "forecast", labelKey: "nav.forecast", icon: I.predict, section: "Alojamiento" },
  { id: "contract", labelKey: "nav.contract", icon: I.doc, section: "Alojamiento" },
  { id: "savings", labelKey: "nav.savings", icon: <PiggyBank size={20} strokeWidth={1.6} />, section: "Alojamiento" },
  { id: "install", labelKey: "nav.install", icon: <RouterIcon size={20} strokeWidth={1.6} />, section: "Equipos" },
  { id: "actuators", labelKey: "nav.actuators", icon: I.tool, section: "Equipos" },
  { id: "alarms", labelKey: "nav.alarms", icon: I.bell, section: "Equipos" },
];
const NAV_DECO_ITEMS = [
  { labelKey: "nav.agenda", icon: I.cal },
  { labelKey: "nav.guests", icon: I.users },
  { labelKey: "nav.access", icon: I.key },
];

// ─── STYLES ─────────────────────────────────────────────────────
// Móvil: ancho <= 820 px (iPhone, Android y tablets en vertical)
function useIsMobile(bp = 820) {
  const q = `(max-width: ${bp}px)`;
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia ? window.matchMedia(q).matches : false);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, [q]);
  return m;
}

const S = {
  title: { fontSize: 34, fontWeight: 700, color: "#406020", marginBottom: 6, fontFamily: "var(--font-serif)", letterSpacing: 0, lineHeight: 1.15 },
  sub: { color: "#7D8478", fontSize: 15, marginBottom: 24, fontFamily: "var(--font-sans)" },
  rowTitle: { fontSize: 10.5, fontWeight: 500, color: "#7D8478", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #E4E7E0", fontFamily: "var(--font-mono)" },
};

// ─── DATA TRANSFORMERS (backend → recharts) ────────────────────

// Agrupa series de sensores clima por hora y sensorID
function transformSensorsClimaToHourly(series) {
  if (!Array.isArray(series)) return [];
  const byHour = {};
  series.forEach((p) => {
    const d = new Date(p._time || p.time);
    const key = d.toISOString().slice(0, 13); // hasta hora
    const hourLabel = d.getHours().toString().padStart(2, "0") + ":00";
    const sid = p.sensorID || "Sensor";
    if (!byHour[key]) byHour[key] = { hour: hourLabel, _ts: d.getTime() };
    byHour[key][sid] = Number(p.temperature) || byHour[key][sid];
    byHour[key]["hum_" + sid] = Number(p.humidity) || byHour[key]["hum_" + sid];
  });
  return Object.values(byHour).sort((a, b) => a._ts - b._ts);
}

// Une series de sensores energía por hora
function transformSensorsEnergiaToHourly(series) {
  if (!Array.isArray(series)) return [];
  const byHour = {};
  series.forEach((p) => {
    const d = new Date(p._time || p.time);
    const key = d.toISOString().slice(0, 13);
    const hourLabel = d.getHours().toString().padStart(2, "0") + ":00";
    if (!byHour[key]) byHour[key] = { hour: hourLabel, _ts: d.getTime() };
    if (p.tipo_red === "monofasica" || p.power !== undefined) {
      byHour[key].monofasica = Number(p.power) || byHour[key].monofasica || 0;
    }
    if (p.power_a !== undefined) byHour[key].fase_a = Number(p.power_a);
    if (p.power_b !== undefined) byHour[key].fase_b = Number(p.power_b);
    if (p.power_c !== undefined) byHour[key].fase_c = Number(p.power_c);
  });
  return Object.values(byHour).sort((a, b) => a._ts - b._ts);
}

// Une PVPC + CO2 hora a hora
function mergePreciosEmisiones(precios, emisiones) {
  const pSeries = (precios && precios.series) || [];
  const eSeries = (emisiones && emisiones.series) || [];
  const map = {};
  const hourOf = (p) => p.hour !== undefined ? p.hour : new Date(p.time).getHours();
  pSeries.forEach((p) => {
    const h = hourOf(p);
    if (!map[h]) map[h] = { hourNum: h, hour: String(h).padStart(2, "0") };
    map[h].precio = Number(p.precio_eur_kwh);
  });
  eSeries.forEach((p) => {
    const h = hourOf(p);
    if (!map[h]) map[h] = { hourNum: h, hour: String(h).padStart(2, "0") };
    map[h].co2 = Number(p.co2_kg_kwh);
  });
  return Object.values(map).sort((a, b) => a.hourNum - b.hourNum);
}

// AEMET → formato frontend
function transformMeteo(days) {
  if (!Array.isArray(days)) return [];
  return days.map((d) => {
    const dt = new Date(d.time);
    return {
      date: dt.toLocaleDateString("es-ES", { day: "2-digit", month: "short" }),
      tMax: d.tMax, tMin: d.tMin, humMax: d.humedadMax, humMin: d.humedadMin,
    };
  });
}

// Consumo diario Datadis → agrupar por mes
function transformConsumptionToMonthly(series) {
  if (!Array.isArray(series)) return [];
  const byMonth = {};
  series.forEach((p) => {
    const iso = p._time || p.time;
    const val = Number.isFinite(Number(p._value)) ? Number(p._value)
              : Number.isFinite(Number(p.consumo_kwh)) ? Number(p.consumo_kwh)
              : 0;
    if (!iso) return;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return;
    const kUnique = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0");
    const label = d.toLocaleDateString("es-ES", { month: "short", year: "2-digit" });
    if (!byMonth[kUnique]) byMonth[kUnique] = { month: label, consumoKWh: 0, days: new Set(), _ts: new Date(d.getFullYear(), d.getMonth(), 1).getTime() };
    byMonth[kUnique].consumoKWh += val;
    byMonth[kUnique].days.add(d.getDate());
  });
  const now = new Date();
  const nowYM = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");
  return Object.entries(byMonth)
    .map(([k, m]) => ({
      ...m,
      key: k,
      consumoKWh: +m.consumoKWh.toFixed(1),
      coste: +(m.consumoKWh * 0.156).toFixed(2),
      daysWithData: m.days.size,
      isCurrent: k === nowYM,
    }))
    // Ocultamos meses casi vacíos (<3 días con datos = punto suelto que estropea el chart)
    .filter(m => m.daysWithData >= 3 && m.consumoKWh > 1)
    .sort((a, b) => a._ts - b._ts);
}

// Huella CO2 diario → mensual
function transformCO2ToMonthly(series) {
  if (!Array.isArray(series)) return [];
  const byMonth = {};
  series.forEach((p) => {
    // Nuevo shape backend v2: { _time, _value, _field }
    // Compat shape antiguo:   { time,  co2_kg_total }
    const iso = p._time || p.time;
    const val = Number.isFinite(Number(p._value)) ? Number(p._value)
              : Number.isFinite(Number(p.co2_kg_total)) ? Number(p.co2_kg_total)
              : 0;
    if (!iso) return;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return;
    const k = d.toLocaleDateString("es-ES", { month: "short", year: "2-digit" });
    if (!byMonth[k]) byMonth[k] = { month: k, total: 0, _ts: d.getTime() };
    byMonth[k].total += val;
  });
  return Object.values(byMonth).sort((a, b) => a._ts - b._ts).map((m) => ({
    ...m,
    total: +m.total.toFixed(1),
    punta: +(m.total * 0.4).toFixed(1),
    llano: +(m.total * 0.35).toFixed(1),
    valle: +(m.total * 0.25).toFixed(1),
  }));
}

// Forecast → formato chart. Soporta forecast v2 con band[] separado.
function transformForecastChart(forecastData) {
  if (!forecastData) return [];
  const byDay = new Map();
  (forecastData.historical || []).forEach((h) => {
    const d = new Date(h.time);
    const key = d.toISOString().slice(0, 10);
    const label = d.toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
    const prev = byDay.get(key) || { date: label, real: 0, prediccion: null, intervalo: null, _ts: d.getTime() };
    prev.real += Number(h.consumo_kwh) || 0;
    byDay.set(key, prev);
  });
  const historicalSorted = Array.from(byDay.values()).sort((a, b) => a._ts - b._ts);
  const bandByTime = {};
  (forecastData.band || []).forEach((b) => { bandByTime[b.time] = b; });
  (forecastData.forecast || []).forEach((f, i) => {
    const d = new Date(f.time);
    const iso = d.toISOString().slice(0, 10);
    const band = bandByTime[iso];
    // Fallbacks para compatibilidad con la respuesta antigua
    const p5  = band?.p5  ?? Number(f.intervalo_min);
    const p95 = band?.p95 ?? Number(f.intervalo_max);
    historicalSorted.push({
      date: i === 0 ? "Hoy" : d.toLocaleDateString("es-ES", { day: "2-digit", month: "short" }),
      real: i === 0 ? Number(forecastData.base_avg_kwh) : null,
      prediccion: Number(f.prediccion_kwh),
      intervalo: Number.isFinite(p5) && Number.isFinite(p95) ? [p5, p95] : null,
      _ts: d.getTime(),
    });
  });
  return historicalSorted;
}

// Formatter para ejes Y con kWh: agrupa miles y respeta decimales
// cuando el valor es pequeño (< 10). Para ejes de escala ≥100 basta 0 dec.
const fmtKWh = (v) => {
  if (v === null || v === undefined) return "";
  const n = Number(v);
  const a = Math.abs(n);
  if (a >= 1000) return (n / 1000).toFixed(1) + "k";
  if (a >= 100)  return n.toFixed(0);
  if (a >= 10)   return n.toFixed(1);
  if (a >= 1)    return n.toFixed(2);
  return n.toFixed(3);
};
// Versión "precisa" para tooltips: siempre 3 decimales si <1, 2 si <10, 1 si <100.
const fmtKWhPrecise = (v) => {
  if (v === null || v === undefined) return "0";
  const n = Number(v);
  const a = Math.abs(n);
  if (a >= 1000) return n.toFixed(0);
  if (a >= 100)  return n.toFixed(1);
  if (a >= 10)   return n.toFixed(2);
  return n.toFixed(3);
};

// PVPC tomorrow → chart con `hour` HH
function transformPVPCTomorrow(pvpcT) {
  const series = (pvpcT && pvpcT.series) || [];
  return series.map((p) => {
    const d = new Date(p.time);
    return { hour: d.getHours().toString().padStart(2, "0"), precio: Number(p.precio_eur_kwh) };
  });
}

// ─── COMPONENTS ─────────────────────────────────────────────────

function Card({ title, subtitle, children, dark = false, height, badge }) {
  return (
    <div className="er-card er-fade-up" style={{
      background: dark ? "#406020" : "#fff",
      borderRadius: 16, padding: 22,
      border: dark ? "1px solid #406020" : "1px solid #E4E7E0",
      height: height || "auto",
      display: "flex", flexDirection: "column",
      fontFamily: "var(--font-sans)",
    }}>
      {title && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ fontFamily: "var(--font-serif)", fontSize: 18, fontWeight: 500, letterSpacing: -0.2, color: dark ? "#F7F7F7" : "#283226" }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12.5, color: dark ? "#B9CBA3" : "#7D8478", marginTop: 3 }}>{subtitle}</div>}
          </div>
          {badge && <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, padding: "3px 8px", borderRadius: 3, border: `1px solid ${badge.color}`, color: badge.color, letterSpacing: 0.8, textTransform: "uppercase", whiteSpace: "nowrap" }}>{badge.text}</span>}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

function KPI({ label, value, unit, delta, color, sparkline }) {
  return (
    <div className="er-card" style={{ background: "#fff", borderRadius: 16, padding: "18px 20px", border: "1px solid #E4E7E0", position: "relative", overflow: "hidden", fontFamily: "var(--font-sans)" }}>
      <div className="er-eyebrow">{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 10 }}>
        <span className="er-figure" style={{ fontSize: 32, color: "#283226" }}><CountUp value={value} /></span>
        {unit && <span style={{ fontSize: 13, color: "#7D8478" }}>{unit}</span>}
      </div>
      <div aria-hidden style={{ position: "absolute", left: 20, right: 20, bottom: 0, height: 2, background: color || "#406020", opacity: 0.8 }} />
      {delta !== undefined && (
        <div style={{ marginTop: 8, fontSize: 12, color: delta >= 0 ? "#B4532F" : "#6F8F4E", display: "flex", alignItems: "center", gap: 4 }}>
          {delta >= 0 ? <TrendingUp size={14} strokeWidth={1.8} /> : <TrendingDown size={14} strokeWidth={1.8} />} {Math.abs(delta)}% vs ayer
        </div>
      )}
      {sparkline && sparkline.length > 0 && (
        <div style={{ position: "absolute", right: 14, top: 16, width: 70, height: 30, opacity: 0.7 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkline}>
              <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// KPI con comparativa: delta % vs periodo anterior (color según `betterWhenDown`).
// `highlight=true` resalta visualmente (para el coste, el dato que más importa al cliente).
// `note` muestra una nota informativa cuando no hay delta.
function KPIDelta({ label, value, unit, delta, betterWhenDown, color, highlight, note }) {
  const hasDelta = delta !== null && delta !== undefined && !isNaN(delta);
  const isPositive = hasDelta && delta > 0;
  const good = hasDelta && (betterWhenDown ? !isPositive : isPositive);
  return (
    <div className="er-card" style={{
      background: highlight ? "#406020" : "#fff", color: highlight ? "#F7F7F7" : "#283226",
      borderRadius: 16, padding: "18px 20px", border: highlight ? "1px solid #406020" : "1px solid #E4E7E0",
    }}>
      <div className="er-eyebrow" style={{ color: highlight ? "#B9CBA3" : undefined }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 10 }}>
        <span className="er-figure" style={{ fontSize: highlight ? 36 : 30 }}><CountUp value={value} /></span>
        {unit && <span style={{ fontSize: 12.5, color: highlight ? "#B9CBA3" : "#7D8478" }}>{unit}</span>}
      </div>
      {hasDelta ? (
        <div style={{ marginTop: 10, fontSize: 12, display: "flex", alignItems: "center", gap: 5, color: highlight ? (good ? "#B9CBA3" : "#E6C3B3") : (good ? "#6F8F4E" : "#B4532F") }}>
          {isPositive ? <TrendingUp size={14} strokeWidth={1.8} /> : <TrendingDown size={14} strokeWidth={1.8} />}
          {Math.abs(delta).toFixed(1)} % vs mes anterior
        </div>
      ) : (
        <div style={{ marginTop: 10, fontSize: 11.5, color: highlight ? "#B9CBA3" : "#A9AEA2", lineHeight: 1.35 }}>{note || "Sin histórico para comparar"}</div>
      )}
    </div>
  );
}

// Panel de conclusiones/insights automáticos: convierte datos crudos en mensajes accionables.
function ConclusionsPanel({ repartoDominante, dominantFranja, precioMedio, hasHistorico }) {
  const t = useT();
  const PVPC_NACIONAL_MEDIO = 0.16; // aproximado España 2025-2026
  const conclusiones = [];

  if (repartoDominante && dominantFranja) {
    if (dominantFranja.name === "Hora barata") {
      conclusiones.push({ tone: "good", text: "Consumo concentrado en horario económico — patrón óptimo de ahorro." });
    } else if (dominantFranja.name === "Hora cara") {
      conclusiones.push({ tone: "bad", text: "Consumo concentrado en hora punta — desplazar usos a horario valle reduciría coste." });
    } else {
      conclusiones.push({ tone: "warn", text: "Consumo concentrado en horario llano — desplazar parte a valle aumentaría el ahorro." });
    }
  }

  if (precioMedio > 0) {
    if (precioMedio < PVPC_NACIONAL_MEDIO * 0.9) {
      conclusiones.push({ tone: "good", text: `Precio medio €${precioMedio.toFixed(3)}/kWh — por debajo del promedio nacional (€${PVPC_NACIONAL_MEDIO}/kWh).` });
    } else if (precioMedio > PVPC_NACIONAL_MEDIO * 1.1) {
      conclusiones.push({ tone: "warn", text: `Precio medio €${precioMedio.toFixed(3)}/kWh — superior al promedio nacional (€${PVPC_NACIONAL_MEDIO}/kWh).` });
    } else {
      conclusiones.push({ tone: "good", text: `Precio medio en línea con el promedio nacional (€${PVPC_NACIONAL_MEDIO}/kWh).` });
    }
  }

  if (!hasHistorico) {
    conclusiones.push({ tone: "warn", text: "No hay histórico suficiente para detectar tendencias temporales — disponible a partir del segundo mes." });
  } else {
    conclusiones.push({ tone: "good", text: "Datos suficientes para comparativa mensual." });
  }

  conclusiones.push({ tone: "good", text: "No se detectan anomalías de consumo en el periodo actual." });

  return (
    <div style={{ marginTop: 16 }}>
      <Card title={t("conclusions.title")} subtitle={t("conclusions.sub")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "8px 4px" }}>
          {conclusiones.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: "#283226", lineHeight: 1.4 }}>
              <span style={{ paddingTop: 5 }}><StatusMark tone={c.tone} /></span>
              <span>{c.text}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function KPIDark({ label, value, unit, sub, alert }) {
  return (
    <div className="er-card" style={{ background: "#fff", borderRadius: 12, padding: "14px 16px", border: `1px solid ${alert ? "#E6C3B3" : "#E4E7E0"}` }}>
      <div className="er-eyebrow">{label}</div>
      <div className="er-figure" style={{ fontSize: 24, color: alert ? "#B4532F" : "#283226", marginTop: 6 }}>
        <CountUp value={value} />
        {unit && <span style={{ fontFamily: "var(--font-sans)", fontSize: 12, color: "#7D8478", marginLeft: 4 }}>{unit}</span>}
      </div>
      {sub && <div style={{ fontSize: 11, color: "#7D8478", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ─── Selector de periodo temporal (reutilizable) ───────────────
// Píldoras con icono y hover para elegir la ventana de análisis.
// Se usa en ConsumptionPage, ForecastPage y ContractPage.
const RANGE_OPTIONS = [
  { key: "30d",  label: "30 días",   short: "30d", icon: null,  days: 30 },
  { key: "90d",  label: "3 meses",   short: "3M",  icon: null,  days: 90 },
  { key: "180d", label: "6 meses",   short: "6M",  icon: null,  days: 180 },
  { key: "365d", label: "12 meses",  short: "1A",  icon: null,  days: 365 },
  { key: "730d", label: "2 años",    short: "2A",  icon: null,  days: 730 },
];

function RangeSelector({ value, onChange, label = "Periodo", options = RANGE_OPTIONS }) {
  return (
    <div role="tablist" aria-label={label} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
      <span className="er-eyebrow">{label}</span>
      <div className="er-seg">
        {options.map((opt) => {
          const active = value === opt.key;
          return (
            <button key={opt.key} role="tab" aria-selected={active} onClick={() => onChange(opt.key)} className={active ? "on" : ""}>
              {active && <motion.span layoutId={"seg-" + label} className="er-seg-bg" transition={{ type: "spring", stiffness: 420, damping: 36 }} />}
              <span style={{ position: "relative" }}>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LoadingBox({ height = 240, dark = false, label = "Cargando…" }) {
  if (/^(Esperando|Sin )/i.test(String(label))) {
    return (
      <div style={{ height: Math.min(height, 200), display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, background: dark ? "rgba(255,255,255,0.04)" : "#FAFAFA", borderRadius: 12, border: "1px dashed #E4E7E0", textAlign: "center", padding: 16 }}>
        <IconTile icon={Radio} tone="#A9AEA2" bg="#EEF0EB" size={38} />
        <div style={{ fontSize: 13, color: "#4B5347", fontWeight: 500 }}>{/^Esperando/i.test(label) ? "Aún no hay sensores enviando datos" : label}</div>
        <div style={{ fontSize: 12, color: "#A9AEA2", maxWidth: 320, lineHeight: 1.45 }}>{/^Esperando/i.test(label) ? "Aparecerán aquí en cuanto se instalen los equipos del Plan Completo." : ""}</div>
      </div>
    );
  }
  return (
    <div role="status" aria-live="polite" style={{ height, display: "flex", flexDirection: "column", justifyContent: "center", gap: 10, padding: "0 6%", background: dark ? "rgba(255,255,255,0.04)" : "#FAFAFA", borderRadius: 12, fontFamily: "var(--font-sans)" }}>
      {[0.92, 0.7, 0.82, 0.55].map((w, k) => <div key={k} className="er-skel" style={{ width: `${w * 100}%`, height: 10, animationDelay: `${k * 120}ms` }} />)}
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: 0.8, textTransform: "uppercase", color: dark ? "#B9CBA3" : "#A9AEA2", marginTop: 6 }}>{label}</span>
    </div>
  );
}

function ErrorBox({ error, dark = false }) {
  return (
    <div style={{
      padding: 16, borderRadius: 10,
      background: dark ? "rgba(180,83,47,0.15)" : "#F5EBD3",
      border: `1px solid ${dark ? "#B4532F" : "#E8D7AE"}`,
      color: dark ? "#B4532F" : "#7A5A12",
      fontSize: 12,
    }}>
      <strong style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><AlertTriangle size={14} strokeWidth={1.8} /> Error</strong>: {error}
    </div>
  );
}

function LeafletMap({ markers, onOpen }) {
  const pts = (markers || []).map((m) => ({
    id: m.locationID,
    name: m.nombre,
    lat: m.lat,
    lon: m.lon,
    city: m.municipio || "",
    dir: m.direccion || "",
    cp: m.cp || "",
    plan: (m.plan || "").toLowerCase(),
  }));
  const ptsJson = JSON.stringify(pts);
  // Auto-fit bounds cuando hay > 1 marker; centro individual si es solo 1
  const html = `<!DOCTYPE html><html><head>
<meta charset="utf-8"/>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<style>
  html,body{margin:0;height:100%;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif}
  #map{width:100%;height:100vh}
  .leaflet-popup-content{margin:8px 12px;font-size:12px;line-height:1.5}
  .er-popup .er-title{font-size:13px;font-weight:700;color:#283226;margin-bottom:2px}
  .er-popup .er-city{font-size:11px;color:#7D8478;margin-bottom:6px}
  .er-popup .er-badge{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px}
  .er-popup .er-badge.completo{background:#EBF2E4;color:#406020}
  .er-popup .er-badge.basico{background:#F5EBD3;color:#7A5A12}
  .er-popup .er-badge.borrador{background:#EEF0EB;color:#7D8478;border:1px dashed #A9AEA2}
  .er-popup .er-open{display:block;margin-top:8px;padding:6px 10px;text-align:center;background:#406020;color:#fff;border-radius:6px;text-decoration:none;font-weight:600;font-size:11px}
</style>
</head><body><div id="map"></div><script>
var pts = ${ptsJson};
var map=L.map('map',{scrollWheelZoom:true});
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{attribution:'© OpenStreetMap',maxZoom:19}).addTo(map);
if(pts.length===0){map.setView([40.4,-3.7],5);}
else if(pts.length===1){map.setView([pts[0].lat,pts[0].lon],11);}
else{var g=L.featureGroup(pts.map(function(p){return L.marker([p.lat,p.lon])}));map.fitBounds(g.getBounds(),{padding:[40,40]});}
var iconFor=function(plan){
  var color = plan==='completo'?'green' : plan==='basico'?'gold' : 'grey';
  return L.icon({iconUrl:'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-'+color+'.png',shadowUrl:'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',iconSize:[25,41],iconAnchor:[12,41],popupAnchor:[1,-34],shadowSize:[41,41]});
};
var esc=function(s){return String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');};
pts.forEach(function(p){
  var content = '<div class="er-popup">'
    + '<div class="er-title">'+esc(p.name)+'</div>'
    + '<div class="er-city">'+esc(p.city)+(p.cp?' · '+esc(p.cp):'')+'</div>'
    + (p.dir?'<div style="font-size:11px;color:#4B5347">'+esc(p.dir)+'</div>':'')
    + '<div style="margin-top:6px"><span class="er-badge '+p.plan+'">'+(p.plan==='borrador'?'Borrador':'Plan '+p.plan)+'</span></div>'
    + (p.plan!=='borrador'?'<a class="er-open" href="#" data-loc="'+esc(p.id)+'" onclick="window.parent.postMessage({type:\\'openLoc\\',id:this.dataset.loc},\\'*\\');return false;">Abrir dashboard →</a>':'')
    + '</div>';
  var m = L.marker([p.lat,p.lon],{icon:iconFor(p.plan)}).addTo(map).bindPopup(content);
  m.on('mouseover',function(){this.openPopup();});
  // No cerramos con mouseout: dejamos que el user click en el link "Abrir dashboard"
});
</script></body></html>`;
  // Escuchar mensajes del iframe (click "Abrir dashboard")
  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.type === "openLoc" && onOpen) onOpen(e.data.id);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onOpen]);
  return <iframe srcDoc={html} style={{ width: "100%", height: "100%", border: "none" }} title="map" />;
}

// ─── PAGES ──────────────────────────────────────────────────

function MapPage({ onNavigate, allowedClients, isAdmin }) {
  const t = useT();
  const { data: geoloc, loading, error } = useApi(api.getGeoloc, []);
  // Merge markers reales con los borradores demo que tengan lat/lon
  const backendMarkers = geoloc?.markers || [];
  const backendIds = new Set(backendMarkers.map(m => m.locationID));
  const demoMarkers = (allowedClients || [])
    .filter(c => c.__demo && c.ubicacion?.latitud && c.ubicacion?.longitud && !backendIds.has(c.locationID))
    .map(c => ({
      locationID: c.locationID, nombre: c.nombre,
      lat: c.ubicacion.latitud, lon: c.ubicacion.longitud,
      plan: "borrador",
      direccion: c.ubicacion.direccion, cp: c.ubicacion.cp, municipio: c.ubicacion.municipio,
    }));
  const rawMarkers = [...backendMarkers, ...demoMarkers];
  // Filtramos por permisos: admin ve TODO, resto ve solo los suyos
  const allowedIds = new Set((allowedClients || []).map(c => c.locationID));
  const markers = isAdmin ? rawMarkers : rawMarkers.filter(m => allowedIds.has(m.locationID));

  // Enriquecemos con el plan real del cliente (por si getGeoloc no lo devuelve)
  const planOf = (id) => {
    const c = (allowedClients || []).find(x => x.locationID === id);
    return c?.plan_contratado || null;
  };
  const isBorrador = (m) => {
    const p = (m.plan || planOf(m.locationID) || "").toLowerCase();
    return !p || p === "borrador" || p === "draft";
  };
  const isPruebaUPC = (m) => {
    const est = (m.estado || "").toLowerCase();
    return est === "prueba_upc" || est === "prueba";
  };

  return (
    <div style={{ padding: 32 }}>
      <h2 style={S.title}>{isAdmin ? t("map.globalTitle") : t("map.title")}</h2>
      <p style={S.sub}>
        {isAdmin
          ? t("map.globalSubtitle", { n: markers.length })
          : t("map.mineSubtitle", { n: markers.length })}
      </p>
      {error && <ErrorBox error={error} />}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20 }}>
        <div style={{ borderRadius: 12, overflow: "hidden", height: 480, boxShadow: "none", border: "1px solid #E4E7E0" }}>
          {loading ? <LoadingBox height={480} /> : <LeafletMap markers={markers} onOpen={(id) => onNavigate("dashboard", id)} />}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {markers.map((a) => (
            <div key={a.locationID} style={{ background: "#fff", borderRadius: 16, padding: 22, boxShadow: "none", border: "1px solid #E4E7E0", fontFamily: "var(--font-sans)", transition: "all 0.2s ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(46,81,57,0.08)"; e.currentTarget.style.borderColor = "#D2DFC3"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; e.currentTarget.style.borderColor = "#E4E7E0"; }}>
              <div style={{ fontWeight: 700, fontSize: 17, color: "#283226", marginBottom: 12 }}>{a.nombre || a.locationID}</div>
              <div style={{ fontSize: 13, color: "#4B5347", lineHeight: 1.7, display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}><MapPin size={13} strokeWidth={1.7} /> {a.direccion || "—"}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Building2 size={13} strokeWidth={1.7} /> {a.municipio || "—"} ({a.cp || "—"})</div>
                <div style={{ color: "#7D8478", fontSize: 12 }}>{a.lat?.toFixed(4)}, {a.lon?.toFixed(4)}</div>
                <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {isPruebaUPC(a) && (
                    <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, background: "#E2ECF2", color: "#2C5470", border: "1px solid #BCD2E0" }}>
                      Prueba UPC
                    </span>
                  )}
                  {isBorrador(a) ? (
                    <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, background: "#EEF0EB", color: "#7D8478", border: "1px dashed #A9AEA2" }}>
                      {t("map.badge.draft")}
                    </span>
                  ) : (
                    <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
                      background: a.plan === "completo" ? "#EBF2E4" : "#F5EBD3",
                      color: a.plan === "completo" ? "#406020" : "#7A5A12",
                      border: `1px solid ${a.plan === "completo" ? "#D2DFC3" : "#E8D7AE"}` }}>
                      {t("map.badge.plan", { plan: a.plan || planOf(a.locationID) })}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                {!isBorrador(a) && (
                  <button onClick={() => onNavigate("dashboard", a.locationID)}
                    style={{ background: "#406020", color: "#fff", border: "1px solid #406020", borderRadius: 10, padding: "7px 20px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)", textTransform: "uppercase", letterSpacing: 0.5, transition: "all 0.18s ease" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#406020"; e.currentTarget.style.borderColor = "#406020"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#406020"; e.currentTarget.style.borderColor = "#406020"; }}>
                    Dashboard
                  </button>
                )}
                <button onClick={() => onNavigate("contract", a.locationID)}
                  style={{ background: "transparent", color: "#406020", border: "1px solid #406020", borderRadius: 10, padding: "7px 20px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)", transition: "all 0.18s ease" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#406020"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#406020"; }}>
                  Ver contrato
                </button>
              </div>
            </div>
          ))}
          {!loading && markers.length === 0 && (
            <ErrorBox error={t("map.noCoords")} />
          )}
        </div>
      </div>
    </div>
  );
}

function DashboardPage({ acc }) {
  const id = acc.locationID;
  // Plan tier: "borrador" | "basico" | "completo"
  const planRaw = String(acc.plan_contratado || acc.plan || "").toLowerCase();
  const planTier = (!planRaw || planRaw === "borrador" || acc.estado === "borrador")
    ? "borrador"
    : (planRaw === "completo" || planRaw === "plan completo") ? "completo" : "basico";
  const noClima = planTier !== "completo";            // sensores IoT solo con plan completo
  const noDatadis = planTier === "borrador";           // huella + consumo Datadis solo si hay CUPS/plan
  const noHuella = planTier === "borrador";            // reparto y huella históricos requieren consumo real

  // Cargar datos en paralelo — SKIP los que no aplican al plan
  const { data: sensorsClima, loading: lClima } = useApi(() => api.getSensors(id, "clima", 24), [id], { pollMs: 60000, skip: noClima });
  const { data: sensorsEnergia, loading: lEnergia } = useApi(() => api.getSensors(id, "energia", 24), [id], { pollMs: 60000, skip: noClima });
  const { data: precios, loading: lPrec } = useApi(api.getPrecios, [], { pollMs: 300000 });
  const { data: emisiones, loading: lEm } = useApi(api.getEmisiones, [], { pollMs: 300000 });
  const { data: meteo, loading: lMet } = useApi(() => api.getMeteo(id), [id], { pollMs: 3600000 });
  const { data: co2, loading: lCO2 } = useApi(() => api.getCO2(id, 240), [id], { skip: noHuella });
  const { data: sensorsAgua } = useApi(() => api.getSensors(id, "agua", 24), [id], { skip: noClima });

  const climaData = useMemo(() => transformSensorsClimaToHourly(sensorsClima?.series), [sensorsClima]);
  const energiaData = useMemo(() => transformSensorsEnergiaToHourly(sensorsEnergia?.series), [sensorsEnergia]);
  const pvpcData = useMemo(() => mergePreciosEmisiones(precios, emisiones), [precios, emisiones]);
  const meteoData = useMemo(() => transformMeteo(meteo?.days), [meteo]);
  const co2Monthly = useMemo(() => transformCO2ToMonthly(co2?.series), [co2]);

  const pvpcAvg = pvpcData.length ? (pvpcData.reduce((a, b) => a + (b.precio || 0), 0) / pvpcData.length).toFixed(4) : "—";
  const pvpcMax = pvpcData.length ? Math.max(...pvpcData.map((p) => p.precio || 0)).toFixed(4) : "—";
  const pvpcMin = pvpcData.length ? Math.min(...pvpcData.filter((p) => p.precio).map((p) => p.precio)).toFixed(4) : "—";

  // KPIs en tiempo real desde el último punto
  const lastClima = sensorsClima?.series?.[sensorsClima.series.length - 1] || {};
  const lastEnergia = sensorsEnergia?.series?.[sensorsEnergia.series.length - 1] || {};
  const lastAgua = sensorsAgua?.series?.[sensorsAgua.series.length - 1] || {};
  const hayDatosSensores = [sensorsClima, sensorsEnergia, sensorsAgua].some(x => (x?.series || []).length > 0);

  return (
    <div style={{ padding: 32, minHeight: "100%", color: "#283226", fontFamily: "var(--font-sans)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <h2 style={{ margin: 0 }}>{acc.nombre || acc.locationID}</h2>
          <p style={{ color: "#7D8478", fontSize: 14, margin: "6px 0 0" }}>Lo que está pasando ahora mismo en el alojamiento</p>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <StatusMark tone={hayDatosSensores ? "good" : planTier === "completo" ? "warn" : "off"} label={hayDatosSensores ? "Sensores enviando datos" : planTier === "completo" ? "Sensores pendientes de instalar" : "Solo datos oficiales"} />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: 0.8, textTransform: "uppercase", padding: "5px 9px", borderRadius: 4,
            border: `1px solid ${planTier === "completo" ? "#406020" : planTier === "basico" ? "#C08B2C" : "#A9AEA2"}`,
            color: planTier === "completo" ? "#406020" : planTier === "basico" ? "#9A6E1D" : "#7D8478" }}>
            {planTier === "completo" ? "Plan completo" : planTier === "basico" ? "Plan básico" : "Borrador · sin plan"}
          </span>
        </div>
      </div>

      {/* AVISO — dashboard limitado por plan */}
      {planTier !== "completo" && (
        <div style={{ marginBottom: 24, background: "#EBF2E4", border: "1px dashed #D2DFC3", borderRadius: 12, padding: "16px 20px", color: "#4B5347" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#283226", marginBottom: 4 }}>
            {planTier === "borrador" ? "Este alojamiento aún no tiene plan contratado" : "Plan básico"}
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            {planTier === "borrador"
              ? "Solo se muestran datos públicos del municipio (precio de la luz, emisiones nacionales y previsión meteorológica). En cuanto se contrate un plan y se instalen los sensores, aparecerán aquí temperatura, humedad, consumo real y huella de carbono personalizada."
              : "Este plan incluye APIs externas (precio, CO₂, meteo, consumo Datadis y huella histórica). No hay sensores IoT instalados en el alojamiento; para verlos en tiempo real hay que subir al plan completo."}
          </div>
        </div>
      )}

      {/* RESUMEN sensores — solo Plan completo */}
      {planTier === "completo" && (
        <div style={{ marginBottom: 16 }}>
          <div style={S.rowTitle}>RESUMEN DE TU ALOJAMIENTO</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
            <KPIDark label="Temperatura" value={!lastClima.temperature ? "--" : lastClima.temperature.toFixed(1)} unit="°C" sub={lastClima.sensorID || "—"} />
            <KPIDark label="Humedad" value={!lastClima.humidity ? "--" : Math.round(lastClima.humidity)} unit="%H" sub={lastClima.sensorID || "—"} />
            <KPIDark label="Potencia actual" value={!lastEnergia.power ? "--" : Math.round(lastEnergia.power)} unit="W" sub="Trifásica" />
            <KPIDark label="Nivel depósito" value={!lastAgua.nivel_porcentaje ? "--" : Math.round(lastAgua.nivel_porcentaje)} unit="%" sub={lastAgua.litros ? `${lastAgua.litros} litros` : "—"} />
            <KPIDark label="Batería sensores" value={!lastClima.battery ? "--" : Math.round(lastClima.battery)} unit="%" sub={lastClima.sensorID || "—"} />
          </div>
        </div>
      )}

      {/* CLIMA */}
      {!noClima && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <Card title="Temperatura 24h" subtitle="Comparativa entre sensores de las zonas" height={280}>
            {lClima ? <LoadingBox height={240} /> : climaData.length === 0 ? <LoadingBox height={240} label="Esperando datos de sensores…" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={climaData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" />
                  <XAxis dataKey="hour" tick={{ fill: "#7D8478", fontSize: 10 }} interval={3} />
                  <YAxis tick={{ fill: "#7D8478", fontSize: 10 }} unit="°C" />
                  <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#7D8478" }} />
                  {(Object.keys(climaData[0] || {}).filter((k) => !["hour", "_ts"].includes(k) && !k.startsWith("hum_"))).map((k, i) => (
                    <Line key={k} type="monotone" dataKey={k} stroke={i === 0 ? "#6F8F4E" : "#C08B2C"} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
          <Card title="Humedad 24h" subtitle="Humedad relativa medida por los sensores" height={280}>
            {lClima ? <LoadingBox height={240} /> : climaData.length === 0 ? <LoadingBox height={240} label="Esperando datos…" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={climaData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" />
                  <XAxis dataKey="hour" tick={{ fill: "#7D8478", fontSize: 10 }} interval={3} />
                  <YAxis tick={{ fill: "#7D8478", fontSize: 10 }} unit="%" />
                  <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#7D8478" }} />
                  {(Object.keys(climaData[0] || {}).filter((k) => k.startsWith("hum_"))).map((k, i) => (
                    <Line key={k} type="monotone" dataKey={k} name={k.replace("hum_", "")} stroke={i === 0 ? "#3D6E8E" : "#C08B2C"} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      )}

      {/* ENERGÍA */}
      {!noClima && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <Card title="Potencia monofásica 24h" subtitle="Consumo eléctrico instantáneo" height={280}>
            {lEnergia ? <LoadingBox height={240} /> : energiaData.length === 0 ? <LoadingBox height={240} label="Esperando datos…" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={energiaData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="powerMono" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#C08B2C" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#C08B2C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" />
                  <XAxis dataKey="hour" tick={{ fill: "#7D8478", fontSize: 10 }} interval={3} />
                  <YAxis tick={{ fill: "#7D8478", fontSize: 10 }} unit="W" />
                  <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#7D8478" }} />
                  <Area type="monotone" dataKey="monofasica" name="Consumo (W)" stroke="#C08B2C" fill="url(#powerMono)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>
          <Card title="Potencia trifásica por fase" subtitle="Consumo por línea trifásica" height={280}>
            {lEnergia ? <LoadingBox height={240} /> : energiaData.length === 0 ? <LoadingBox height={240} label="Esperando datos…" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={energiaData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" />
                  <XAxis dataKey="hour" tick={{ fill: "#7D8478", fontSize: 10 }} interval={3} />
                  <YAxis tick={{ fill: "#7D8478", fontSize: 10 }} unit="W" />
                  <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#7D8478" }} />
                  <Line type="monotone" dataKey="fase_a" name="Línea 1" stroke={COLORS.faseA} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="fase_b" name="Línea 2" stroke={COLORS.faseB} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="fase_c" name="Línea 3" stroke={COLORS.faseC} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      )}

      {/* PRECIO LUZ */}
      <div style={S.rowTitle}>PRECIO DE LA LUZ</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 12 }}>
        <KPIDark label="Precio máximo hoy" value={"€" + pvpcMax} unit="/kWh" sub="—" />
        <KPIDark label="Precio medio hoy" value={"€" + pvpcAvg} unit="/kWh" sub="24h" />
        <KPIDark label="Precio mínimo hoy" value={"€" + pvpcMin} unit="/kWh" sub="—" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <Card title="Precio de la luz a lo largo del día" subtitle="Estilo ESIOS: barra por hora, ahora destacado" height={360} badge={{ text: "EN VIVO", color: "#6F8F4E" }}>
          {lPrec ? <LoadingBox height={300} /> : pvpcData.length === 0 ? <LoadingBox height={300} label="Sin datos PVPC" /> : (
            <PVPCChart data={pvpcData} field="precio" unit="€/kWh" precios={precios} />
          )}
        </Card>
        <Card title="Emisiones de CO₂ a lo largo del día" subtitle="kg CO₂ por kWh — color verde = más limpio" height={360}>
          {lEm ? <LoadingBox height={300} /> : pvpcData.length === 0 ? <LoadingBox height={300} label="Sin datos CO2" /> : (
            <CO2HourlyChart data={pvpcData} />
          )}
        </Card>
      </div>

      {/* AEMET */}
      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <Card title="Previsión meteorológica — 7 días" subtitle={`Previsión del tiempo en ${acc.municipio_id || ""}`} height={280}>
          {lMet ? <LoadingBox height={240} /> : meteoData.length === 0 ? <LoadingBox height={240} label="Sin datos de previsión" /> : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={meteoData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" />
                <XAxis dataKey="date" tick={{ fill: "#7D8478", fontSize: 10 }} />
                <YAxis yAxisId="left" tick={{ fill: "#7D8478", fontSize: 10 }} unit="°C" />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: "#3D6E8E", fontSize: 10 }} unit="%" />
                <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#7D8478" }} />
                <Bar yAxisId="left" dataKey="tMax" fill="#8E3C20" name="Temp. máxima" />
                <Bar yAxisId="left" dataKey="tMin" fill="#3D6E8E" name="Temp. mínima" />
                <Line yAxisId="right" type="monotone" dataKey="humMax" name="Humedad máxima" stroke="#6F8F4E" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* HUELLA CO2 — solo si NO es borrador (requiere consumo Datadis) */}
      {!noHuella && (
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 12 }}>
          <Card title="Huella de carbono — meses" subtitle="CO₂ emitido cada mes" height={300}>
            {lCO2 ? <LoadingBox height={260} /> : co2Monthly.length === 0 ? <LoadingBox height={260} label="Sin histórico de CO₂ todavía" /> : (
              <CO2MonthlyChart data={co2Monthly} />
            )}
          </Card>
          <Card title="Reparto del CO₂ del último mes" subtitle="por franja tarifaria" height={300}>
            <CO2BreakdownPie id={id} />
          </Card>
        </div>
      )}
    </div>
  );
}

// Chart PVPC estilo ESIOS: barras horarias 0-23h, banda verde para horas más baratas, roja para más caras,
// hora actual destacada, y resumen hora más barata · hora más cara · ahora debajo.
function PVPCChart({ data, precios }) {
  const stats = precios?.stats;
  const sortedByPrice = [...data].filter((d) => d.precio !== undefined).sort((a, b) => a.precio - b.precio);
  const cheapestHours = new Set(sortedByPrice.slice(0, 3).map((d) => d.hourNum));
  const expensiveHours = new Set(sortedByPrice.slice(-3).map((d) => d.hourNum));
  const realNow = new Date().getHours();
  // Fallback: si la hora actual no tiene dato (lag del cron ESIOS),
  // usar la última hora disponible para "AHORA"
  const dataWithPrice = data.filter((d) => d.precio !== undefined);
  const directMatch = dataWithPrice.find((d) => d.hourNum === realNow);
  const nowEntry = directMatch || (dataWithPrice.length ? dataWithPrice[dataWithPrice.length - 1] : null);
  const nowHour = nowEntry ? nowEntry.hourNum : realNow;
  const isLagged = nowEntry && nowEntry.hourNum !== realNow;

  const colorFor = (h) => {
    if (cheapestHours.has(h)) return "#6F8F4E";
    if (expensiveHours.has(h)) return "#B4532F";
    return "#C08B2C";
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" vertical={false} />
            <XAxis dataKey="hour" tick={{ fill: "#7D8478", fontSize: 10 }} interval={1} />
            <YAxis tick={{ fill: "#7D8478", fontSize: 10 }} tickFormatter={(v) => "€" + v.toFixed(2)} width={50} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} formatter={(v) => "€" + Number(v).toFixed(4) + "/kWh"} labelFormatter={(h) => h + ":00"} />
            <ReferenceLine x={String(nowHour).padStart(2, "0")} stroke="#3D6E8E" strokeDasharray="4 2" />
            <Bar dataKey="precio" radius={[2, 2, 0, 0]}>
              {data.map((d, i) => <Cell key={i} fill={colorFor(d.hourNum)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, padding: "8px 0 0", fontSize: 11 }}>
        <div style={{ background: "rgba(111,143,78,0.15)", padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(111,143,78,0.3)" }}>
          <div style={{ color: "#6F8F4E", fontWeight: 700, fontSize: 10 }}>MÁS BARATA</div>
          <div style={{ color: "#283226", fontSize: 14, fontWeight: 700, marginTop: 2 }}>
            {stats && stats.cheapest_hour !== null && stats.cheapest_hour !== undefined ? String(stats.cheapest_hour).padStart(2, "0") + ":00" : "—"}
          </div>
          <div style={{ color: "#7D8478", fontSize: 11, marginTop: 1 }}>€{stats?.min ? stats.min.toFixed(4) : "—"}/kWh</div>
        </div>
        <div style={{ background: "rgba(61,110,142,0.15)", padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(61,110,142,0.3)" }}>
          <div style={{ color: "#3D6E8E", fontWeight: 700, fontSize: 10 }}>{isLagged ? "ÚLT. DATO" : "AHORA"}</div>
          <div style={{ color: "#283226", fontSize: 14, fontWeight: 700, marginTop: 2 }}>{String(nowHour).padStart(2, "0")}:00</div>
          <div style={{ color: "#7D8478", fontSize: 11, marginTop: 1 }}>€{nowEntry?.precio !== undefined ? nowEntry.precio.toFixed(4) : "—"}/kWh</div>
        </div>
        <div style={{ background: "rgba(180,83,47,0.15)", padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(180,83,47,0.3)" }}>
          <div style={{ color: "#B4532F", fontWeight: 700, fontSize: 10 }}>MÁS CARA</div>
          <div style={{ color: "#283226", fontSize: 14, fontWeight: 700, marginTop: 2 }}>
            {stats && stats.most_expensive_hour !== null && stats.most_expensive_hour !== undefined ? String(stats.most_expensive_hour).padStart(2, "0") + ":00" : "—"}
          </div>
          <div style={{ color: "#7D8478", fontSize: 11, marginTop: 1 }}>€{stats?.max ? stats.max.toFixed(4) : "—"}/kWh</div>
        </div>
      </div>
    </div>
  );
}

// Chart CO2 horario: barras con escala de color verde→amarillo→rojo según intensidad
function CO2HourlyChart({ data }) {
  const valid = data.filter((d) => d.co2 !== undefined);
  if (valid.length === 0) return <LoadingBox height={260} label="Sin datos CO2" />;
  const values = valid.map((d) => d.co2);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const sorted = [...valid].sort((a, b) => a.co2 - b.co2);
  const cleanest = sorted[0];
  const dirtiest = sorted[sorted.length - 1];
  const realNow = new Date().getHours();
  // Fallback: si la hora actual no tiene dato, usa la última hora con dato disponible
  const directMatch = valid.find((d) => d.hourNum === realNow);
  const nowEntry = directMatch || valid[valid.length - 1];
  const nowHour = nowEntry ? nowEntry.hourNum : realNow;
  const isLagged = nowEntry && nowEntry.hourNum !== realNow;
  const colorFor = (v) => {
    if (maxV === minV) return "#C08B2C";
    const pct = (v - minV) / (maxV - minV);
    if (pct < 0.33) return "#6F8F4E";
    if (pct < 0.66) return "#C08B2C";
    return "#B4532F";
  };
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" vertical={false} />
            <XAxis dataKey="hour" tick={{ fill: "#7D8478", fontSize: 10 }} interval={1} />
            <YAxis tick={{ fill: "#7D8478", fontSize: 10 }} tickFormatter={(v) => v.toFixed(2)} width={50} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} formatter={(v) => Number(v).toFixed(3) + " kgCO₂/kWh"} labelFormatter={(h) => h + ":00"} />
            <ReferenceLine x={String(nowHour).padStart(2, "0")} stroke="#3D6E8E" strokeDasharray="4 2" />
            <Bar dataKey="co2" radius={[2, 2, 0, 0]}>
              {data.map((d, i) => <Cell key={i} fill={d.co2 !== undefined ? colorFor(d.co2) : "#283226"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, padding: "8px 0 0", fontSize: 11 }}>
        <div style={{ background: "rgba(111,143,78,0.15)", padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(111,143,78,0.3)" }}>
          <div style={{ color: "#6F8F4E", fontWeight: 700, fontSize: 10 }}>MÁS LIMPIA</div>
          <div style={{ color: "#283226", fontSize: 14, fontWeight: 700, marginTop: 2 }}>{String(cleanest.hourNum).padStart(2, "0")}:00</div>
          <div style={{ color: "#7D8478", fontSize: 11, marginTop: 1 }}>{cleanest.co2.toFixed(3)} kg/kWh</div>
        </div>
        <div style={{ background: "rgba(61,110,142,0.15)", padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(61,110,142,0.3)" }}>
          <div style={{ color: "#3D6E8E", fontWeight: 700, fontSize: 10 }}>{isLagged ? "ÚLT. DATO" : "AHORA"}</div>
          <div style={{ color: "#283226", fontSize: 14, fontWeight: 700, marginTop: 2 }}>{String(nowHour).padStart(2, "0")}:00</div>
          <div style={{ color: "#7D8478", fontSize: 11, marginTop: 1 }}>{nowEntry?.co2 !== undefined ? nowEntry.co2.toFixed(3) : "—"} kg/kWh</div>
        </div>
        <div style={{ background: "rgba(180,83,47,0.15)", padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(180,83,47,0.3)" }}>
          <div style={{ color: "#B4532F", fontWeight: 700, fontSize: 10 }}>MÁS SUCIA</div>
          <div style={{ color: "#283226", fontSize: 14, fontWeight: 700, marginTop: 2 }}>{String(dirtiest.hourNum).padStart(2, "0")}:00</div>
          <div style={{ color: "#7D8478", fontSize: 11, marginTop: 1 }}>{dirtiest.co2.toFixed(3)} kg/kWh</div>
        </div>
      </div>
    </div>
  );
}

// Chart Huella CO2: barras por mes con total + Δ% vs mes anterior + valor encima de cada barra
function CO2MonthlyChart({ data }) {
  // Calcula total por mes y delta vs mes anterior
  const enriched = data.map((d, i) => {
    const total = (Number(d.punta) || 0) + (Number(d.llano) || 0) + (Number(d.valle) || 0);
    const prev = i > 0 ? ((Number(data[i - 1].punta) || 0) + (Number(data[i - 1].llano) || 0) + (Number(data[i - 1].valle) || 0)) : null;
    const delta = prev !== null && prev > 0 ? ((total - prev) / prev) * 100 : null;
    return { ...d, total, delta };
  });
  const last = enriched[enriched.length - 1];
  const trend = last && last.delta !== null ? last.delta : null;
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "4px 8px 12px" }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#283226" }}>
            {last ? fmtKWh(last.total) + " kg" : "—"}
          </div>
          <div style={{ fontSize: 10, color: "#7D8478", textTransform: "uppercase", letterSpacing: 1 }}>último mes</div>
        </div>
        {trend !== null && (
          <div style={{ fontSize: 14, fontWeight: 700, color: trend > 0 ? "#B4532F" : "#6F8F4E" }}>
            {trend > 0 ? "↑" : "↓"} {Math.abs(trend).toFixed(1)}% vs mes anterior
          </div>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={enriched} margin={{ top: 18, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: "#7D8478", fontSize: 10 }} />
            <YAxis tick={{ fill: "#7D8478", fontSize: 10 }} tickFormatter={fmtKWh} width={50} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} formatter={(v) => fmtKWh(v) + " kg CO₂"} />
            <Bar dataKey="total" fill={COLORS.primaryLight} radius={[2, 2, 0, 0]} maxBarSize={48}>
              <LabelList dataKey="total" position="top" formatter={(v) => fmtKWh(v)} fill="#4B5347" fontSize={10} fontWeight={700} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Reparto CO2: barras horizontales con valor + % al lado (mejor que donut cuando hay dominante)
function CO2BreakdownPie({ id }) {
  const { data, loading } = useApi(() => api.getCO2Breakdown(id), [id]);
  if (loading) return <LoadingBox height={260} />;
  const c = data?.co2_kg;
  const total = c ? c.punta + c.llano + c.valle : 0;
  if (!c || total === 0) return <LoadingBox height={260} label="Sin datos suficientes" />;
  const rows = [
    { name: "Hora cara",   value: c.punta, color: COLORS.punta },
    { name: "Hora normal", value: c.llano, color: COLORS.llano },
    { name: "Hora barata", value: c.valle, color: COLORS.valle },
  ].sort((a, b) => b.value - a.value);
  const maxValue = rows[0].value;
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 8px", gap: 14 }}>
      <div style={{ fontSize: 11, color: "#7D8478", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
        Total {fmtKWh(total)} kg
      </div>
      {rows.map((r) => {
        const pct = (r.value / total) * 100;
        const barWidth = (r.value / maxValue) * 100;
        return (
          <div key={r.name}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#283226", marginBottom: 4 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color }} />
                {r.name}
              </span>
              <span style={{ fontWeight: 700 }}>{pct.toFixed(1)}% · {fmtKWh(r.value)} kg</span>
            </div>
            <div style={{ background: "#EEF0EB", borderRadius: 4, height: 10, overflow: "hidden" }}>
              <div style={{ width: barWidth + "%", height: "100%", background: r.color, transition: "width 0.4s" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══ Panel de sostenibilidad + bosque virtual + logros (backlog #9 + #10 + #12) ═══
// SVG de árbol simple para el bosque
function TreeSVG({ size = 40, opacity = 1, index = 0, animated = true }) {
  const delay = `${Math.min(index, 20) * 60}ms`;
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      className={animated ? "er-tree" : undefined}
      style={{ opacity, animationDelay: delay }}
      aria-hidden="true">
      <defs>
        <linearGradient id="er-tree-leaves" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6F8F4E" />
          <stop offset="100%" stopColor="#406020" />
        </linearGradient>
      </defs>
      <path d="M12 2 L7 10 L9 10 L5 16 L8 16 L4 22 L20 22 L16 16 L19 16 L15 10 L17 10 Z" fill="url(#er-tree-leaves)" />
      <rect x="10.5" y="20" width="3" height="4" fill="#7A5A12" />
    </svg>
  );
}

function SustainabilityPanel({ data }) {
  if (!data) return null;
  const { actual, ahorro, bosque, equivalencias, nivel, logros = [] } = data;

  // No mostramos el panel si el histórico es muy corto — cálculos absurdos
  // (eficiencia -311%, bosque 0, nivel bronce por defecto, etc.).
  const diasAnalizados = actual?.dias_analizados || 0;
  if (diasAnalizados < 60) {
    return (
      <div style={{ marginBottom: 24, background: "#fff", borderRadius: 16, padding: "22px 26px", border: "1px dashed #D2DFC3", fontFamily: "var(--font-sans)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#EBF2E4", color: "#406020", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#283226" }}>Panel de sostenibilidad</div>
            <div style={{ fontSize: 13, color: "#7D8478", marginTop: 4 }}>
              Disponible con al menos 60 días de histórico ({diasAnalizados}/60 acumulados). El bosque virtual, tu nivel eco-anfitrión y las equivalencias
              aparecerán automáticamente cuando Datadis publique más datos.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar bosque: N árboles llenos + 1 en crecimiento (opacity proporcional)
  const arboles = bosque?.arboles || 0;
  const proximoPct = bosque?.proximo_arbol_pct || 0;
  const arbolesArray = Array.from({ length: Math.min(arboles, 12) }, (_, i) => i);
  const cardStyle = {
    background: "#fff", borderRadius: 12, padding: 18,
    boxShadow: "none", border: "1px solid #E4E7E0",
    fontFamily: "var(--font-sans)"
  };
  const labelStyle = { fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: "#7D8478", fontWeight: 700, marginBottom: 8 };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: "#283226", margin: 0, fontFamily: "var(--font-sans)" }}>
          Sostenibilidad · <span style={{ color: "#7D8478", fontWeight: 500 }}>impacto ambiental de tu consumo</span>
        </h3>
        <div style={{ fontSize: 11, color: "#7D8478" }}>{actual?.dias_analizados} días analizados</div>
      </div>

      {/* Fila 1: Nivel eco-anfitrión GRANDE + bosque virtual */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: 14, marginBottom: 14 }}>
        {/* Nivel eco-anfitrión */}
        <div className="er-card-lift er-fade-up" style={{...cardStyle, background: "#fff", borderTop: `2px solid ${nivel?.color}` }}>
          <div style={labelStyle}>Tu nivel eco-anfitrión</div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}><IconTile icon={{ platino: Trophy, oro: Award, plata: Medal, bronce: Medal }[nivel?.id] || Medal} tone={nivel?.color} bg={(nivel?.color || "#406020") + "1A"} size={60} /></motion.div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 700, color: nivel?.color, textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1 }}>{nivel?.id}</div>
              <div style={{ fontSize: 12, color: "#7D8478", marginTop: 4 }}>
                <b style={{ color: "#283226" }}>{ahorro?.eficiencia_pct >= 0 ? "+" : ""}{ahorro?.eficiencia_pct}%</b> eficiencia vs media
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#4B5347", marginTop: 12, lineHeight: 1.4 }}>{nivel?.descripcion}</div>
        </div>

        {/* Bosque virtual */}
        <div className="er-card-lift er-fade-up" style={{ ...cardStyle, animationDelay: "80ms" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div style={labelStyle}>Tu bosque virtual</div>
            <div style={{ fontSize: 11, color: "#7D8478" }}>1 árbol ≈ 21 kg CO₂/año</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <div className="er-pop-in" style={{ fontSize: 44, fontWeight: 700, color: "#406020", lineHeight: 1 }}>{arboles}</div>
            <div style={{ fontSize: 13, color: "#7D8478" }}>árboles equivalentes al año</div>
          </div>
          {/* Grid de árboles con animación de crecimiento escalonada */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 12, padding: 12, background: "#EBF2E4", borderRadius: 10, minHeight: 60, perspective: "600px" }}>
            {arbolesArray.map(i => <TreeSVG key={i} size={36} index={i} />)}
            {arboles < 12 && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 2 }} title={`${proximoPct}% al siguiente árbol`}>
                <TreeSVG size={36} index={arbolesArray.length} opacity={0.15 + proximoPct * 0.008} />
              </div>
            )}
          </div>
          {/* Progreso al siguiente árbol con shimmer */}
          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#7D8478", marginBottom: 4 }}>
              <span>Progreso al árbol #{arboles + 1}</span>
              <span>{bosque?.proximo_arbol_kg_restantes} kg CO₂ para el siguiente</span>
            </div>
            <div style={{ background: "#EBF2E4", borderRadius: 10, height: 8, overflow: "hidden" }}>
              <div
                className="er-progress-bar"
                style={{ width: proximoPct + "%", height: "100%", ["--er-target-w"]: proximoPct + "%" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Fila 2: Equivalencias tangibles */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
        {[
          { icon: Fuel, value: `${equivalencias?.litros_gasolina_evitados} L`, label: "de gasolina evitados al año", color: "#283226" },
          { icon: Car, value: `${equivalencias?.km_coche_evitados} km`, label: "de coche equivalentes/año", color: "#283226" },
          { icon: Globe2, value: `${ahorro?.co2_kg_anual} kg`, label: "CO₂ ahorrado al año", color: "#406020" },
        ].map((eq, i) => (
          <div key={i} className="er-card-lift er-fade-up" style={{ ...cardStyle, animationDelay: `${120 + i * 80}ms` }}>
            <div style={{ marginBottom: 12 }}><IconTile icon={eq.icon} size={36} /></div>
            <div className="er-pop-in" style={{ fontSize: 24, fontWeight: 700, color: eq.color, lineHeight: 1, animationDelay: `${180 + i * 80}ms` }}>{eq.value}</div>
            <div style={{ fontSize: 12, color: "#7D8478", marginTop: 4 }}>{eq.label}</div>
          </div>
        ))}
      </div>

      {/* Fila 3: Logros conseguidos */}
      {logros.length > 0 && (
        <div className="er-card-lift er-fade-up" style={{ ...cardStyle, animationDelay: "280ms" }}>
          <div style={labelStyle}>Logros conseguidos · {logros.length}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            {logros.map((l, i) => (
              <div key={i}
                className="er-logro"
                style={{
                  display: "flex", alignItems: "center", gap: 10, padding: 12,
                  background: "#F3F5EE",
                  border: "1px solid #D2DFC3", borderRadius: 10,
                  transition: "transform .2s ease, box-shadow .2s ease",
                  animationDelay: `${340 + i * 70}ms`,
                }}>
                <IconTile icon={{ arbol_1: Sprout, arbol_5: TreeDeciduous, arbol_10: Trees, plata: Medal, oro: Award, platino: Trophy, un_ano: CalendarCheck }[l.id] || Award} size={34} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#283226" }}>{l.titulo}</div>
                  <div style={{ fontSize: 11, color: "#7D8478", lineHeight: 1.3, marginTop: 2 }}>{l.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ Certificado de eficiencia energética A-G (backlog #8) ═══
// Muestra la banda de eficiencia estilo etiqueta energética oficial.
function EfficiencyBadge({ data }) {
  if (!data || !data.disponible) {
    return (
      <div style={{ background: "#fff", borderRadius: 12, padding: 20, border: "1px dashed #D2DFC3", textAlign: "center", fontFamily: "var(--font-sans)" }}>
        <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}><IconTile icon={FileText} /></div>
        <div style={{ fontSize: 13, color: "#7D8478" }}>
          {data?.razon || "Certificado disponible al acumular ≥14 días de datos."}
        </div>
      </div>
    );
  }
  const cert = data.certificado;
  const bands = data.todas_bandas || [];
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: 20, boxShadow: "none", border: "1px solid #E4E7E0", fontFamily: "var(--font-sans)" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: "#7D8478", fontWeight: 700 }}>Certificado energético</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#283226", marginTop: 2 }}>
            {data.consumo_anual_estimado_kwh} kWh/año · {data.superficie_m2} m² · <span style={{ color: cert.color }}>{data.ratio_ajustado} kWh/m²·año</span>
          </div>
        </div>
        <div style={{ fontSize: 10, color: "#7D8478", fontStyle: "italic", textAlign: "right", maxWidth: 220, lineHeight: 1.3 }}>
          Zona {data.zona_climatica} · Aproximado, no oficial
        </div>
      </div>

      {/* Escalera A-G con la banda actual destacada */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {bands.map(b => {
          const activa = b.es_actual;
          const anchura = 40 + (bands.length - bands.indexOf(b)) * 6; // A más ancha, G más estrecha
          return (
            <div key={b.letra} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: `${anchura}%`, minWidth: 100, height: activa ? 46 : 28,
                background: b.color, borderRadius: "4px 22px 22px 4px",
                display: "flex", alignItems: "center", padding: "0 14px",
                color: "#fff", fontWeight: 700, fontSize: activa ? 26 : 15,
                transition: "all .3s ease",
                boxShadow: "none",
                border: activa ? "2px solid #283226" : "none"
              }}>
                {b.letra}
              </div>
              <div style={{ fontSize: 12, color: activa ? "#283226" : "#7D8478", fontWeight: activa ? 700 : 500 }}>
                {b.min}{b.max ? `–${b.max}` : "+"} kWh/m²·año
                {activa && (
                  <span style={{ marginLeft: 8, padding: "2px 10px", background: cert.color, color: "#fff", borderRadius: 10, fontSize: 10, fontWeight: 700, letterSpacing: 0.4 }}>
                    TU ALOJAMIENTO
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Descripción de la letra actual */}
      <div style={{ marginTop: 16, padding: "14px 16px", background: cert.color + "15", borderLeft: `4px solid ${cert.color}`, borderRadius: 12 }}>
        <div style={{ fontSize: 14, color: "#283226", fontWeight: 700 }}>{cert.descripcion}</div>
        {data.recomendaciones && data.recomendaciones.length > 0 && (
          <ul style={{ margin: "10px 0 0", paddingLeft: 20, fontSize: 12.5, color: "#4B5347", lineHeight: 1.5 }}>
            {data.recomendaciones.slice(0, 3).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

// ═══ Panel de proyecciones y comparativas de costes (backlog #4) ═══
// Muestra: coste actual mes + progreso, proyección fin de mes, proyección fin de año,
// delta vs mes anterior (kWh/coste/precio), comparativa año anterior si hay datos.
function CostsProjectionPanel({ data }) {
  // El backend v2 devuelve:
  //   current_month: { month:"YYYY-MM", kwh, cost_eur, days_with_data, days_in_month, mean_daily_kwh }
  //   projected_month: { kwh, cost_eur }
  //   projected_year: { kwh, cost_eur, ytd_days }
  //   delta_vs_prev_month: { prev_month, prev_kwh, prev_cost_eur, delta_kwh, delta_cost_eur, delta_pct }
  //   price_eur_kwh
  const cm = data.current_month || {};
  const pm = data.projected_month || {};
  const py = data.projected_year || {};
  const dv = data.delta_vs_prev_month || {};

  // Normalizar a las claves que el resto del componente usa
  const cur = {
    kwh: cm.kwh,
    coste_eur: cm.cost_eur,
    daily_avg_kwh: cm.mean_daily_kwh,
    dias_transcurridos: cm.days_with_data,
    dias_totales: cm.days_in_month,
    year: parseInt((cm.month || "0-0").split("-")[0], 10) || null,
    month: parseInt((cm.month || "0-0").split("-")[1], 10) || null,
  };
  const proy = { kwh: pm.kwh, coste_eur: pm.cost_eur };
  const proyY = { kwh: py.kwh, coste_eur: py.cost_eur, ytd_dias_datos: py.ytd_days };
  const prev = dv.prev_month ? {
    kwh: dv.prev_kwh,
    coste_eur: dv.prev_cost_eur,
    year: parseInt(dv.prev_month.split("-")[0], 10),
    month: parseInt(dv.prev_month.split("-")[1], 10),
  } : {};
  const d = {
    coste_pct: dv.delta_pct,
    coste_abs: dv.delta_cost_eur,
    kwh_pct: dv.delta_pct,
    kwh_abs: dv.delta_kwh,
  };
  data.precio_medio_eur_kwh = data.price_eur_kwh;

  const yoy = null; // el backend v2 aún no calcula YoY para el mes

  const progresoMes = cur.dias_totales > 0 ? Math.min(100, (cur.dias_transcurridos / cur.dias_totales) * 100) : 0;
  const monthName = cur.year && cur.month
    ? new Date(cur.year, cur.month - 1, 1).toLocaleDateString("es-ES", { month: "long", year: "numeric" })
    : "—";
  const prevMonthName = prev.year && prev.month
    ? new Date(prev.year, prev.month - 1, 1).toLocaleDateString("es-ES", { month: "long" })
    : "mes anterior";

  const isUp = (v) => v !== null && v !== undefined && v > 0;
  const isDown = (v) => v !== null && v !== undefined && v < 0;
  const pctColor = (pct) => isUp(pct) ? "#8E3C20" : isDown(pct) ? "#406020" : "#7D8478";
  const arrow = (pct) => isUp(pct) ? "▲" : isDown(pct) ? "▼" : "•";
  const fmt = (n, dec = 2) => (n === null || n === undefined || isNaN(n)) ? "—" : Number(n).toFixed(dec);

  const cardStyle = {
    background: "#fff", borderRadius: 12, padding: 18,
    boxShadow: "none", border: "1px solid #E4E7E0",
    fontFamily: "var(--font-sans)"
  };
  const labelStyle = { fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: "#7D8478", fontWeight: 700, marginBottom: 8 };

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Header del panel */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#283226", margin: 0, fontFamily: "var(--font-sans)" }}>Proyecciones y comparativa</h3>
          <div style={{ fontSize: 12, color: "#7D8478", marginTop: 2 }}>
            Mes en curso: <b style={{ color: "#283226" }}>{monthName}</b> · Día {cur.dias_transcurridos}/{cur.dias_totales}
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#7D8478" }}>
          Precio medio aplicado: <b style={{ color: "#283226" }}>€{fmt(data.precio_medio_eur_kwh, 4)}/kWh</b>
        </div>
      </div>

      {/* Barra de progreso del mes */}
      <div style={{ background: "#F7F7F7", borderRadius: 10, height: 6, marginBottom: 20, overflow: "hidden" }}>
        <div style={{ width: progresoMes + "%", height: "100%", background: "#6F8F4E", transition: "width .5s" }} />
      </div>

      {/* 4 tarjetas: acumulado / fin de mes / fin de año / delta */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14 }}>

        {/* Coste acumulado del mes */}
        <div style={cardStyle}>
          <div style={labelStyle}>Coste acumulado</div>
          <div style={{ fontSize: 30, fontWeight: 700, color: "#283226", lineHeight: 1 }}>€{fmt(cur.coste_eur, 2)}</div>
          <div style={{ fontSize: 12, color: "#7D8478", marginTop: 6 }}>{fmt(cur.kwh, 1)} kWh este mes</div>
          <div style={{ fontSize: 11, color: "#7D8478", marginTop: 4 }}>Media diaria: {fmt(cur.daily_avg_kwh, 2)} kWh</div>
        </div>

        {/* Proyección fin de mes */}
        <div style={cardStyle}>
          <div style={labelStyle}>Previsto fin de mes</div>
          <div style={{ fontSize: 30, fontWeight: 700, color: "#8E3C20", lineHeight: 1 }}>€{fmt(proy.coste_eur, 2)}</div>
          <div style={{ fontSize: 12, color: "#7D8478", marginTop: 6 }}>{fmt(proy.kwh, 1)} kWh si mantienes ritmo</div>
          <div style={{ fontSize: 11, color: "#7D8478", marginTop: 4 }}>Extrapolación linear</div>
        </div>

        {/* Proyección fin de año */}
        <div style={cardStyle}>
          <div style={labelStyle}>Previsto fin de año</div>
          <div style={{ fontSize: 30, fontWeight: 700, color: "#283226", lineHeight: 1 }}>€{fmt(proyY.coste_eur, 2)}</div>
          <div style={{ fontSize: 12, color: "#7D8478", marginTop: 6 }}>{fmt(proyY.kwh, 0)} kWh anuales</div>
          <div style={{ fontSize: 11, color: "#7D8478", marginTop: 4 }}>Basado en YTD ({proyY.ytd_dias_datos} d)</div>
        </div>

        {/* Delta vs mes anterior */}
        <div style={cardStyle}>
          <div style={labelStyle}>vs {prevMonthName || "mes anterior"}</div>
          <div style={{ fontSize: 30, fontWeight: 700, color: pctColor(d.coste_pct), lineHeight: 1, display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 16 }}>{arrow(d.coste_pct)}</span>
            {d.coste_pct !== null ? Math.abs(d.coste_pct).toFixed(0) + "%" : "—"}
          </div>
          <div style={{ fontSize: 12, color: pctColor(d.coste_abs), marginTop: 6 }}>
            {d.coste_abs !== null ? (isUp(d.coste_abs) ? "+" : "") + "€" + fmt(Math.abs(d.coste_abs), 2) : "—"} de diferencia
          </div>
          <div style={{ fontSize: 11, color: "#7D8478", marginTop: 4, textTransform: "capitalize" }}>
            {prevMonthName}: €{fmt(prev.coste_eur, 2)} · {fmt(prev.kwh, 0)} kWh
          </div>
        </div>
      </div>

      {/* Fila inferior: comparativa año anterior + resumen de análisis */}
      {(yoy || d.ahorro_esperado_eur !== null) && (
        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {yoy ? (
            <div style={cardStyle}>
              <div style={labelStyle}>Mismo mes año anterior</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: "#283226" }}>€{fmt(yoy.coste_eur, 2)}</div>
                <div style={{ fontSize: 13, color: "#7D8478" }}>· {fmt(yoy.kwh, 0)} kWh</div>
              </div>
              <div style={{ fontSize: 12, color: "#7D8478", marginTop: 8 }}>Compara con lo que estás gastando ahora.</div>
            </div>
          ) : (
            <div style={{...cardStyle, borderStyle: "dashed", background: "#FAFAFA"}}>
              <div style={labelStyle}>Mismo mes año anterior</div>
              <div style={{ fontSize: 14, color: "#7D8478", fontStyle: "italic", marginTop: 6 }}>Aún sin datos del año pasado. La comparativa aparecerá el próximo aniversario del alta.</div>
            </div>
          )}
          <div style={cardStyle}>
            <div style={labelStyle}>Diagnóstico</div>
            {d.ahorro_esperado_eur !== null ? (
              d.ahorro_esperado_eur > 0 ? (
                <div style={{ color: "#406020", fontSize: 14, marginTop: 4 }}>
                  <b>Ahorrando €{fmt(d.ahorro_esperado_eur, 2)}</b> este mes respecto al anterior si mantienes el ritmo.
                </div>
              ) : (
                <div style={{ color: "#8E3C20", fontSize: 14, marginTop: 4 }}>
                  <b>▲ Sobrecoste €{fmt(Math.abs(d.ahorro_esperado_eur), 2)}</b> previsto respecto al mes pasado si sigues igual.
                </div>
              )
            ) : (
              <div style={{ color: "#7D8478", fontSize: 13, marginTop: 4 }}>Necesitamos otro mes de datos para dar un diagnóstico fiable.</div>
            )}
            {d.kwh_pct !== null && (
              <div style={{ fontSize: 11, color: "#7D8478", marginTop: 8 }}>
                Consumo: {isUp(d.kwh_pct) ? "+" : ""}{fmt(d.kwh_pct, 1)}% · Precio kWh: {isUp(d.precio_pct) ? "+" : ""}{fmt(d.precio_pct, 1)}%
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DatadisPeriodViewer: visor Diario/Semanal/Mensual/Anual estilo Booking.
// Sin emojis, tipografía grande, iconos SVG, animaciones fade-up escalonadas,
// hover con lift 3D y separación clara entre bloques.
// Llama al endpoint on-demand /consumption/history con cache backend.

const SVG_ICON = {
  day:   (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>),
  week:  (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M3 10h18"/><path d="M8 14h8"/><path d="M8 18h5"/></svg>),
  month: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M3 10h18"/><path d="M7 14h.01"/><path d="M11 14h.01"/><path d="M15 14h.01"/><path d="M7 18h.01"/><path d="M11 18h.01"/></svg>),
  year:  (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M7 15l4-4 4 4 5-5"/></svg>),
  prev:  (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>),
  next:  (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>),
  bolt:  (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>),
  euro:  (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10h12"/><path d="M4 14h9"/><path d="M19 6a7.5 7.5 0 0 0-5.5-2A7.5 7.5 0 0 0 6 12a7.5 7.5 0 0 0 7.5 8 7.5 7.5 0 0 0 5.5-2"/></svg>),
  scale: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><path d="M18 20a6 6 0 0 0-12 0"/><path d="M12 4v2"/><circle cx="12" cy="10" r="1"/></svg>),
  hash:  (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>),
};

// ErrorBoundary para que un fallo de Recharts no deje toda la página en blanco.
class ChartErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    if (typeof console !== "undefined") console.error("Chart crashed:", error, info);
  }
  componentDidUpdate(prev) {
    // Resetear cuando el resetKey cambia (evita quedarse pillado tras cambio de granularity)
    if (this.props.resetKey !== prev.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, background: "#F3E1D8", borderRadius: 12, border: "1px solid #E6C3B3", color: "#8E3C20", fontFamily: "var(--font-sans)" }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>El gráfico no pudo renderizarse.</div>
          <div style={{ fontSize: 13, color: "#8E3C20" }}>Detalle: {String(this.state.error.message || this.state.error)}</div>
          <button onClick={() => this.setState({ error: null })}
            style={{ marginTop: 12, background: "#8E3C20", color: "#fff", border: "none", borderRadius: 12, padding: "8px 16px", cursor: "pointer", fontFamily: "var(--font-sans)", fontWeight: 600 }}>
            Reintentar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Boundary a nivel de sección — recupera páginas enteras si algo dentro tira.
// Evita el "página blanca" cuando el error escapa del ChartErrorBoundary interno.
class SectionErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    if (typeof console !== "undefined") console.error("Section crashed:", error, info);
  }
  componentDidUpdate(prev) {
    if (this.props.resetKey !== prev.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 48, background: "#fff", borderRadius: 16, border: "1px solid #E4E7E0", color: "#283226", fontFamily: "var(--font-sans)", textAlign: "center", boxShadow: "none" }}>
          <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 10 }}>Este apartado no se pudo cargar.</div>
          <div style={{ fontSize: 14, color: "#7D8478", marginBottom: 18 }}>Puede ser un dato inesperado del proveedor. Prueba a reintentar o cambiar de periodo.</div>
          <div style={{ fontSize: 12, color: "#8E3C20", background: "#F3E1D8", display: "inline-block", padding: "8px 14px", borderRadius: 12, marginBottom: 18 }}>{String(this.state.error.message || this.state.error).substring(0, 200)}</div>
          <br />
          <button onClick={() => this.setState({ error: null })}
            style={{ background: "#406020", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", cursor: "pointer", fontFamily: "var(--font-sans)", fontWeight: 700, fontSize: 14 }}>
            Reintentar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function DatadisPeriodViewer({ locationID, initialGranularity = "day", title = "Consumo por periodo" }) {
  // Diario = 24 horas del día seleccionado (backend granularity=hour).
  // Semanal = 7 días de la semana del anchor.
  // Mensual = mes concreto del anchor (barras diarias).
  // Anual = 12 meses del año del anchor (barras mensuales, como Datadis).
  const GRANS = [
    { key: "day",   label: "Diario",   monthsBack: 0,  stepUnit: "day",   stepAmount: 1, apiGran: "hour"          },
    { key: "week",  label: "Semanal",  monthsBack: 2,  stepUnit: "week",  stepAmount: 1, apiGran: "day"           },
    { key: "month", label: "Mensual",  monthsBack: 0,  stepUnit: "month", stepAmount: 1, apiGran: "day"           },
    { key: "year",  label: "Anual",    monthsBack: 11, stepUnit: "year",  stepAmount: 1, apiGran: "year_by_month" },
  ];
  const [granularity, setGranularity] = useState(initialGranularity);
  // Anchor por defecto: hace 15 días (mientras carga summary con latest_day).
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 15);
    return d;
  });
  const anchorInitialized = useRef(false);
  const [summary, setSummary] = useState(null);
  // Al montar, pide summary para conocer el último día real con datos publicados.
  useEffect(() => {
    let cancel = false;
    api.getConsumptionSummary(locationID).then(s => {
      if (cancel) return;
      setSummary(s);
      if (!anchorInitialized.current && s && s.latest_day) {
        const [y, m, d] = s.latest_day.split("-").map(Number);
        setAnchor(new Date(y, m - 1, d));
        anchorInitialized.current = true;
      }
    }).catch(() => {});
    return () => { cancel = true; };
  }, [locationID]);
  // Al cambiar de tab, si el nuevo periodo cae en fecha futura sin datos,
  // saltar automáticamente al último periodo con datos.
  const jumpToLatest = () => {
    if (!summary?.latest_day) return;
    const [y, m, d] = summary.latest_day.split("-").map(Number);
    setAnchor(new Date(y, m - 1, d));
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const grConf = GRANS.find(g => g.key === granularity) || GRANS[0];

  // Compute rango que enviamos al endpoint según granularidad.
  const fmtYM  = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const fmtYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const range = useMemo(() => {
    if (granularity === "day") {
      const ym = fmtYM(anchor);
      return { start: ym, end: ym, day: fmtYMD(anchor) };
    }
    if (granularity === "month") {
      // Mensual = solo el mes del anchor con barras diarias
      const ym = fmtYM(anchor);
      return { start: ym, end: ym, day: null };
    }
    if (granularity === "year") {
      // Anual = 12 meses del año del anchor
      const y = anchor.getFullYear();
      return { start: `${y}-01`, end: `${y}-12`, day: null };
    }
    // week: 2 meses de margen
    const end = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = new Date(end);
    start.setMonth(start.getMonth() - grConf.monthsBack);
    return { start: fmtYM(start), end: fmtYM(end), day: null };
  }, [anchor, granularity, grConf.monthsBack]);

  // focus_from/focus_to para que el backend filtre el hourly_profile a la ventana visible.
  // Sin este filtro el hourly_profile siempre mostraba el mismo patrón (calculado sobre 3+ meses).
  const focusRange = useMemo(() => {
    if (granularity === "day") return { from: fmtYMD(anchor), to: fmtYMD(anchor) };
    if (granularity === "week") {
      const dow = (anchor.getDay() + 6) % 7;
      const mon = new Date(anchor); mon.setDate(mon.getDate() - dow);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
      return { from: fmtYMD(mon), to: fmtYMD(sun) };
    }
    if (granularity === "month") {
      const startM = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const endM = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
      return { from: fmtYMD(startM), to: fmtYMD(endM) };
    }
    // year: no aplica hourly-focus (ya es amplio)
    return { from: null, to: null };
  }, [anchor, granularity]);

  // Llamada al backend
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(null);
    const opts = { start: range.start, end: range.end, granularity: grConf.apiGran };
    if (range.day) opts.day = range.day;
    if (focusRange.from) opts.focus_from = focusRange.from;
    if (focusRange.to)   opts.focus_to   = focusRange.to;
    api.getConsumptionHistory(locationID, opts)
      .then(r => { if (!cancel) { setPayload(r); setLoading(false); } })
      .catch(e => { if (!cancel) { setError(e.message || String(e)); setLoading(false); } });
    return () => { cancel = true; };
  }, [locationID, range.start, range.end, range.day, grConf.apiGran, focusRange.from, focusRange.to]);

  // Pre-warm: al montar, en background disparamos fetches de 12m y 24m
  // para poblar la cache del backend. Así cuando el usuario cambia entre
  // pestañas Diario/Semanal/Mensual/Anual, todo carga desde cache
  // (instantáneo, sin golpear Datadis).
  useEffect(() => {
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const start12 = new Date(end); start12.setMonth(start12.getMonth() - 11);
    const start24 = new Date(end); start24.setMonth(start24.getMonth() - 23);
    // Fire-and-forget; el backend cachea y responde rápido las siguientes.
    api.getConsumptionHistory(locationID, { start: fmt(start12), end: fmt(end), granularity: "month" }).catch(() => {});
    api.getConsumptionHistory(locationID, { start: fmt(start24), end: fmt(end), granularity: "year"  }).catch(() => {});
  }, [locationID]);

  const nav = (dir) => {
    const d = new Date(anchor);
    const amt = dir * grConf.stepAmount;
    if (grConf.stepUnit === "day")   d.setDate(d.getDate() + amt);
    if (grConf.stepUnit === "week")  d.setDate(d.getDate() + amt * 7);
    if (grConf.stepUnit === "month") d.setMonth(d.getMonth() + amt);
    if (grConf.stepUnit === "year")  d.setFullYear(d.getFullYear() + amt);
    if (d > new Date()) return; // no futuro
    setAnchor(d);
  };

  // Etiqueta del anchor según granularidad
  const anchorLabel = (() => {
    if (granularity === "day")   return anchor.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    if (granularity === "week") {
      const dow = (anchor.getDay() + 6) % 7;
      const mon = new Date(anchor); mon.setDate(mon.getDate() - dow);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
      const fmt = (d) => d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
      return `${fmt(mon)} – ${fmt(sun)}`;
    }
    if (granularity === "month") return anchor.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
    return String(anchor.getFullYear());
  })();

  // Filtrar series según granularidad + anchor.
  // - hour (Diario): backend ya devolvió 24 barras del day
  // - week (Semanal): filtro por semana del anchor (Lu-Do)
  // - month (Mensual): backend devuelve solo mes del anchor
  // - year (Anual): backend devuelve 12 meses del año del anchor
  const filteredSeries = useMemo(() => {
    const rawIn = Array.isArray(payload?.series) ? payload.series : [];
    // Sanitizar CADA fila: sin objetos, sin números NaN
    const raw = rawIn
      .filter(x => x && typeof x === "object" && typeof x.time === "string" && x.time.length > 0)
      .map(x => ({
        time: x.time,
        value_kwh: Number.isFinite(Number(x.value_kwh)) ? Number(x.value_kwh) : 0,
        coste_eur: Number.isFinite(Number(x.coste_eur)) ? Number(x.coste_eur) : null,
        valle_kwh: Number.isFinite(Number(x.valle_kwh)) ? Number(x.valle_kwh) : 0,
        llano_kwh: Number.isFinite(Number(x.llano_kwh)) ? Number(x.llano_kwh) : 0,
        punta_kwh: Number.isFinite(Number(x.punta_kwh)) ? Number(x.punta_kwh) : 0,
      }));
    if (granularity === "day" || granularity === "month" || granularity === "year") return raw;
    // week: Lu-Do de la semana del anchor
    try {
      const dow = (anchor.getDay() + 6) % 7;
      const mon = new Date(anchor); mon.setDate(mon.getDate() - dow); mon.setHours(0,0,0,0);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6); sun.setHours(23,59,59,999);
      return raw.filter(x => {
        const parts = x.time.split("-").map(Number);
        if (parts.length < 3 || !Number.isFinite(parts[0])) return false;
        const t = new Date(parts[0], (parts[1] || 1) - 1, parts[2] || 1).getTime();
        if (!Number.isFinite(t)) return false;
        return t >= mon.getTime() && t <= sun.getTime();
      });
    } catch { return raw; }
  }, [payload, granularity, anchor]);

  const series = filteredSeries;
  const total = series.reduce((s, x) => s + (Number.isFinite(x.value_kwh) ? x.value_kwh : 0), 0);
  // Coste con el precio de cada franja (punta/llano/valle) que calcula el backend; si no viene, precio medio
  const costeTotal = series.some(x => x.coste_eur !== null)
    ? series.reduce((s, x) => s + (x.coste_eur || 0), 0)
    : total * 0.156;
  const mean = series.length ? total / series.length : 0;

  // Fallback universal: si el anchor cae en periodo sin datos, saltar al último
  // periodo con datos según granularidad. Para week/month usamos el propio series
  // ya cargado; para day/hour usamos summary.latest_day.
  useEffect(() => {
    if (loading) return;
    // Diario (hour): si TODAS las barras a 0 y summary tiene latest_day distinto → saltar
    if (granularity === "day") {
      const anyData = (payload?.series || []).some(x => (x.value_kwh || 0) > 0);
      if (anyData) return;
      if (!summary?.latest_day) return;
      const ymdAnchor = `${anchor.getFullYear()}-${String(anchor.getMonth()+1).padStart(2,"0")}-${String(anchor.getDate()).padStart(2,"0")}`;
      if (ymdAnchor === summary.latest_day) return;
      const [y, m, d] = summary.latest_day.split("-").map(Number);
      setAnchor(new Date(y, m - 1, d));
      return;
    }
    // Week/Month/Year: si series filtered está vacío pero hay datos raw, saltar al último
    if (series.length > 0) return;
    // El payload puede ser aún el de la pestaña anterior (p.ej. el horario "00:00" del Diario):
    // solo saltamos con datos de la granularidad que se está mostrando.
    const expectedGran = grConf.apiGran === "year_by_month" ? "month" : grConf.apiGran;
    if (payload?.granularity && payload.granularity !== expectedGran) return;
    const raw = payload?.series || [];
    if (raw.length === 0) return;
    const lastWithData = raw[raw.length - 1];
    if (!lastWithData?.time || !/^\d{4}-\d{2}/.test(lastWithData.time)) return;
    let target;
    if (granularity === "month" || granularity === "year") {
      const parts = lastWithData.time.split("-").map(Number);
      target = new Date(parts[0], (parts[1] || 1) - 1, 15);
    } else { // week
      const [y, m, d] = lastWithData.time.split("-").map(Number);
      target = new Date(y, (m || 1) - 1, d || 1);
    }
    if (!target || isNaN(target.getTime())) return;
    // Evita bucle infinito
    const anchorTs = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()).getTime();
    if (Math.abs(target.getTime() - anchorTs) < 3 * 86400000) return;
    setAnchor(target);
  }, [granularity, loading, series.length, payload, anchor, summary, grConf.apiGran]);

  // Input directo para elegir día/mes/año sin usar flechas
  const directPickerValue = (() => {
    if (granularity === "day")   return fmtYMD(anchor);
    if (granularity === "month") return fmtYM(anchor);
    if (granularity === "year")  return String(anchor.getFullYear());
    // week: date input pero interpretado como semana
    return fmtYMD(anchor);
  })();
  const directPickerType = granularity === "month" ? "month" : granularity === "year" ? "number" : "date";
  const onDirectPickerChange = (e) => {
    const v = e.target.value;
    if (!v) return;
    if (granularity === "year") {
      const y = parseInt(v, 10);
      if (!isNaN(y) && y >= 2020 && y <= 2100) setAnchor(new Date(y, 0, 15));
      return;
    }
    if (granularity === "month") {
      const [y, m] = v.split("-").map(Number);
      setAnchor(new Date(y, m - 1, 15));
      return;
    }
    // day or week
    const [y, m, d] = v.split("-").map(Number);
    setAnchor(new Date(y, m - 1, d));
  };

  // Perfil horario típico (viene del backend con la respuesta)
  const hourlyProfile = payload?.hourly_profile || [];
  const showHourly = hourlyProfile.some(h => h.avg_kwh > 0);

  // Fuente de datos legible
  const sourceLabel = {
    "cache_only":   { label: "Cache local",           color: "#406020" },
    "datadis_live": { label: "Datadis en vivo",       color: "#3D6E8E" },
    "cache_hybrid": { label: "Cache + Datadis",       color: "#6F8F4E" },
  }[payload?.source] || null;

  return (
    <section className="er-card-lift er-fade-up" style={{
      background: "#fff",
      borderRadius: 16,
      padding: "28px 30px",
      border: "1px solid #E4E7E0",
      marginBottom: 32,
      boxShadow: "none",
      fontFamily: "var(--font-sans)",
    }}>
      {/* Título grande estilo Booking */}
      <div style={{ marginBottom: 22 }}>
        <h3 style={{
          fontSize: 26, fontWeight: 700, color: "#283226",
          margin: 0, letterSpacing: -0.4, lineHeight: 1.15,
        }}>{title}</h3>
        <p style={{
          fontSize: 14, color: "#7D8478", margin: "4px 0 0",
          fontWeight: 500,
        }}>Datos oficiales Datadis, con cache local para respuestas instantáneas.</p>
      </div>

      {/* Header: pestañas grandes + navegación */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 16, marginBottom: 24,
        paddingBottom: 22, borderBottom: "1px solid #EEF0EB",
      }}>
        <div role="tablist" style={{
          display: "flex", gap: 6, background: "#F7F7F7", padding: 6,
          borderRadius: 12,
        }}>
          {GRANS.map(g => {
            const active = granularity === g.key;
            return (
              <button key={g.key} role="tab" aria-selected={active}
                onClick={() => { setGranularity(g.key); jumpToLatest(); }}
                style={{
                  background: active ? "#fff" : "transparent",
                  color: active ? "#283226" : "#7D8478",
                  border: active ? "1px solid #D2DFC3" : "1px solid transparent",
                  borderRadius: 10,
                  padding: "11px 22px",
                  fontSize: 15, fontWeight: 700,
                  cursor: "pointer", fontFamily: "var(--font-sans)",
                  boxShadow: "none",
                  transition: "background .22s ease, transform .22s ease, box-shadow .22s ease",
                  display: "inline-flex", alignItems: "center", gap: 10,
                  transform: "none",
                }}>
                <span style={{ color: active ? "#406020" : "#7D8478", display: "flex" }}>{SVG_ICON[g.key]}</span>
                {g.label}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => nav(-1)} title="Anterior" aria-label="Periodo anterior"
            className="er-nav-item"
            style={{
              background: "#fff", border: "1px solid #E4E7E0", borderRadius: 12,
              width: 40, height: 44, cursor: "pointer",
              color: "#406020", display: "flex", alignItems: "center", justifyContent: "center",
            }}>{SVG_ICON.prev}</button>
          {/* Contenedor del picker directo con etiqueta encima */}
          <div style={{ position: "relative" }}>
            <div className="er-fade-in" key={anchor.toISOString() + granularity} style={{
              minWidth: 260, textAlign: "center", fontWeight: 700,
              color: "#283226", fontSize: 16, textTransform: "capitalize",
              padding: "10px 18px", background: "#FAFAFA", borderRadius: 12,
              border: "1px solid #E4E7E0",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
            }} onClick={(e) => { const inp = e.currentTarget.querySelector("input"); if (inp) { inp.showPicker ? inp.showPicker() : inp.focus(); } }}>
              <span>{anchorLabel}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7D8478" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              <input
                type={directPickerType}
                value={directPickerValue}
                onChange={onDirectPickerChange}
                aria-label={`Seleccionar ${grConf.label.toLowerCase()}`}
                min={directPickerType === "number" ? 2020 : undefined}
                max={directPickerType === "number" ? new Date().getFullYear() : undefined}
                style={{
                  position: "absolute", inset: 0, opacity: 0, cursor: "pointer",
                  width: "100%", height: "100%", border: "none", background: "transparent",
                }}
              />
            </div>
          </div>
          <button onClick={() => nav(1)} title="Siguiente" aria-label="Periodo siguiente"
            disabled={anchor >= new Date()}
            className="er-nav-item"
            style={{
              background: anchor >= new Date() ? "#EEF0EB" : "#fff",
              border: "1px solid #E4E7E0", borderRadius: 12,
              width: 40, height: 44,
              cursor: anchor >= new Date() ? "default" : "pointer",
              color: anchor >= new Date() ? "#A9AEA2" : "#406020",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{SVG_ICON.next}</button>
        </div>
      </div>

      {/* KPIs grandes con iconos SVG */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 16, marginBottom: 26,
      }}>
        {[
          { icon: SVG_ICON.bolt,  label: "Consumo total",   value: fmtKWh(total),         unit: "kWh",     color: "#406020", bg: "#EBF2E4" },
          { icon: SVG_ICON.euro,  label: "Coste estimado",  value: fmtKWh(costeTotal), unit: "€",       color: "#8E3C20", bg: "#F3E1D8" },
          { icon: SVG_ICON.scale, label: `Media / ${granularity === "day" ? "hora" : granularity === "week" ? "día" : granularity === "month" ? "día" : "mes"}`, value: fmtKWh(mean), unit: "kWh", color: "#3D6E8E", bg: "#E2ECF2" },
          { icon: SVG_ICON.hash,  label: "Registros",       value: String(series.length), unit: "puntos",  color: "#C08B2C", bg: "#F5EBD3" },
        ].map((k, i) => (
          <div key={k.label} className="er-card-lift er-fade-up"
            style={{
              background: "#fff", borderRadius: 16, padding: "18px 20px",
              animationDelay: `${60 + i * 60}ms`, position: "relative", overflow: "hidden",
              border: "1px solid #E4E7E0",
            }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              marginBottom: 12, color: k.color,
            }}>
              {k.icon}
              <span className="er-eyebrow">{k.label}</span>
            </div>
            <div className="er-figure" style={{ fontSize: 34, color: "#283226" }}>
              <CountUp value={k.value} />
              <span style={{ fontFamily: "var(--font-sans)", fontSize: 13, color: "#7D8478", marginLeft: 6 }}>{k.unit}</span>
            </div>
            <div aria-hidden style={{ position: "absolute", left: 20, right: 20, bottom: 0, height: 2, background: k.color, opacity: 0.85 }} />
          </div>
        ))}
      </div>

      {/* Gráfico principal grande */}
      <div style={{
        background: "#fff", borderRadius: 16, padding: "18px 20px",
        minHeight: 380, border: "1px solid #E4E7E0", marginBottom: 22,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontFamily: "var(--font-serif)", fontSize: 18, fontWeight: 500, color: "#283226" }}>Evolución del consumo</div>
          <div style={{ fontSize: 12, color: "#7D8478" }}>{grConf.label.toLowerCase()} · kWh</div>
        </div>
        {loading ? (
          <LoadingBox height={320} label={`Descargando ${grConf.label.toLowerCase()} de Datadis…`} />
        ) : error ? (
          <ErrorBox error={error} />
        ) : series.length === 0 ? (
          // Detectar si el anchor está en periodo futuro sin datos publicados aún
          <div style={{ height: 320, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#FAFAFA", borderRadius: 12, padding: 24, textAlign: "center", fontFamily: "var(--font-sans)" }}>
            <div style={{ width: 68, height: 68, borderRadius: "50%", background: "#EBF2E4", color: "#406020", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#283226", marginBottom: 6 }}>
              {anchor >= new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 3)
                ? "Datadis aún no ha publicado datos para este periodo"
                : "Sin datos para este periodo"}
            </div>
            <div style={{ fontSize: 13, color: "#7D8478", maxWidth: 380, lineHeight: 1.5 }}>
              {anchor >= new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 3)
                ? "Datadis publica cada CUPS con 3-15 días de retraso. Vuelve a probar más adelante o navega a un periodo anterior."
                : "Sin datos disponibles para este rango."}
            </div>
          </div>
        ) : (
          <ChartErrorBoundary resetKey={granularity + "_" + anchor.toISOString().substring(0, 10)}>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={series.map(s => ({
                ...s,
                valle_kwh: Number.isFinite(Number(s.valle_kwh)) ? Number(s.valle_kwh) : 0,
                llano_kwh: Number.isFinite(Number(s.llano_kwh)) ? Number(s.llano_kwh) : 0,
                punta_kwh: Number.isFinite(Number(s.punta_kwh)) ? Number(s.punta_kwh) : 0,
              }))} margin={{ top: 20, right: 12, left: 0, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" vertical={false} />
                <XAxis dataKey="time" tick={{ fontSize: 11, fill: "#4B5347" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 12, fill: "#4B5347" }} tickFormatter={fmtKWh} />
                <Tooltip
                  formatter={(v, name) => {
                    const map = { valle_kwh: "Valle", llano_kwh: "Llano", punta_kwh: "Punta" };
                    const safe = Number.isFinite(Number(v)) ? Number(v) : 0;
                    return [`${fmtKWhPrecise(safe)} kWh`, map[name] || String(name || "")];
                  }}
                  contentStyle={{ background: "#fff", border: "1px solid #D2DFC3", borderRadius: 12, fontFamily: "var(--font-sans)", boxShadow: "none" }}
                  labelStyle={{ color: "#406020", fontWeight: 700, marginBottom: 4 }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: 12, fontSize: 12 }}
                  formatter={(value) => ({ valle_kwh: "Valle", llano_kwh: "Llano", punta_kwh: "Punta" }[value] || String(value || ""))}
                />
                <Bar dataKey="valle_kwh" stackId="a" fill="#6F8F4E" isAnimationActive animationDuration={800} />
                <Bar dataKey="llano_kwh" stackId="a" fill="#C08B2C" isAnimationActive animationDuration={800} />
                <Bar dataKey="punta_kwh" stackId="a" fill="#B4532F" radius={[2, 2, 0, 0]} isAnimationActive animationDuration={800} />
              </BarChart>
            </ResponsiveContainer>
          </ChartErrorBoundary>
        )}
      </div>

      {/* Perfil horario típico — solo en Semanal/Mensual/Anual (en Diario el gráfico principal ya es horario) */}
      {showHourly && granularity !== "day" && (
        <div className="er-fade-up" style={{
          background: "#FFFFFF",
          borderRadius: 16, padding: "18px 20px",
          border: "1px solid #E4E7E0", marginBottom: 8,
          animationDelay: "260ms",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#283226" }}>Perfil horario típico</div>
              <div style={{ fontSize: 12, color: "#7D8478", marginTop: 2 }}>Consumo medio por hora del día en el periodo seleccionado</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hourlyProfile} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="er-hourly-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3D6E8E" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#283226" stopOpacity={0.7} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" vertical={false} />
              <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "#4B5347" }} interval={1} />
              <YAxis tick={{ fontSize: 11, fill: "#4B5347" }} tickFormatter={fmtKWh} />
              <Tooltip
                formatter={(v) => [`${fmtKWh(v)} kWh`, "Media"]}
                contentStyle={{ background: "#fff", border: "1px solid #D2DFC3", borderRadius: 12, fontFamily: "var(--font-sans)" }}
              />
              <Bar dataKey="avg_kwh" fill="url(#er-hourly-grad)" radius={[2, 2, 0, 0]} isAnimationActive animationDuration={700} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Footer: fuente de datos */}
      {sourceLabel && (
        <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#7D8478" }}>
          <span>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: sourceLabel.color, marginRight: 8, verticalAlign: "middle" }} />
            Fuente: <b style={{ color: sourceLabel.color }}>{sourceLabel.label}</b>
            {payload?.cups_used && ` · ${payload.cups_used.length} CUPS`}
          </span>
          {payload?.cache_stats && (
            <span style={{ color: "#A9AEA2" }}>
              {payload.cache_stats.months_cached} meses en cache · {payload.cache_stats.months_fetched} fetches Datadis
            </span>
          )}
        </div>
      )}
    </section>
  );
}

function ConsumptionPage({ acc }) {
  const t = useT();
  const id = acc.locationID;
  const planRaw = String(acc.plan_contratado || acc.plan || "").toLowerCase();
  const isBorrador = !planRaw || planRaw === "borrador" || acc.estado === "borrador";
  if (isBorrador) {
    return (
      <div style={{ padding: 32, maxWidth: 780, margin: "0 auto" }}>
        <h2 style={S.title}>{t("consumption.title")}</h2>
        <p style={S.sub}>{t("consumption.subtitle", { name: acc.nombre || acc.locationID })}</p>
        <div style={{ background: "#fff", borderRadius: 16, padding: 44, border: "1px dashed #D2DFC3", textAlign: "center", boxShadow: "none" }}>
          <div style={{ width: 88, height: 88, borderRadius: 16, background: "#EEF0EB", color: "#7D8478", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 42 }}><BarChart3 size={34} strokeWidth={1.4} /></div>
          <h3 style={{ ...S.title, fontSize: 22, marginBottom: 8 }}>Sin datos de consumo</h3>
          <p style={{ ...S.sub, marginBottom: 20, fontSize: 15, maxWidth: 480, margin: "0 auto 20px" }}>
            Este alojamiento aún no tiene contratado el Plan Básico. Cuando se registre su CUPS en Datadis, aquí verás el consumo horario, coste mensual, franjas y huella de carbono en tiempo real.
          </p>
          <div style={{ background: "#F3F5EE", border: "1px solid #D2DFC3", borderRadius: 10, padding: 14, fontSize: 13, color: "#4B5347", display: "inline-block", textAlign: "left" }}>
            <b>¿Cómo se activa?</b><br />
            1. El dueño facilita CUPS + NIF autorizado a EasyRural<br />
            2. Se marca como Plan Básico desde el panel admin<br />
            3. Al día siguiente aparecen los primeros datos aquí
          </div>
        </div>
      </div>
    );
  }
  // Los datos de InfluxDB solo mantienen 30 días de retención (Cloud FREE).
  // Para vistas anuales/mensuales usamos DatadisPeriodViewer con cache.
  // Aquí solo pedimos los últimos 30 días para la evolución mensual
  // (bloque de comparativa y KPIs de resumen inferior).
  const rangeDays = 365; // solo lo consume yearData que ya viene del summary

  const { data: summary, loading: lSum } = useApi(() => api.getConsumptionSummary(id), [id]);
  const { data: consumption, loading: lCons } = useApi(() => api.getConsumption(id, rangeDays), [id]);
  const { data: precios } = useApi(api.getPrecios, []);
  const { data: emisiones } = useApi(api.getEmisiones, []);
  const { data: breakdown } = useApi(() => api.getCO2Breakdown(id), [id]);
  // Endpoint enriquecido con proyecciones y comparativas (backlog #4)
  const [costsData, setCostsData] = useState(null);
  const [effData, setEffData] = useState(null);
  const [sustData, setSustData] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.getCosts(id).then(r => { if (!cancel) setCostsData(r); }).catch(() => {});
    api.getEfficiency(id).then(r => { if (!cancel) setEffData(r); }).catch(() => {});
    api.getSustainability(id).then(r => { if (!cancel) setSustData(r); }).catch(() => {});
    return () => { cancel = true; };
  }, [id]);

  // Meses con coste por franjas del backend (/costs.monthly, 24 meses); si no llega, serie diaria a 0,156 €/kWh
  const yearData = useMemo(() => {
    const ms = costsData?.monthly;
    if (Array.isArray(ms) && ms.length) {
      const nowYM = new Date().toISOString().substring(0, 7);
      return ms.slice(-13).map(m => {
        const [y, mo] = m.month.split("-").map(Number);
        const dt = new Date(y, mo - 1, 1);
        return { key: m.month, month: dt.toLocaleDateString("es-ES", { month: "short", year: "2-digit" }),
                 consumoKWh: +Number(m.kwh).toFixed(1), coste: +Number(m.coste).toFixed(2), daysWithData: m.days,
                 isCurrent: m.month === nowYM, _ts: dt.getTime() };
      }).filter(m => m.daysWithData >= 3 && m.consumoKWh > 1);
    }
    return transformConsumptionToMonthly(consumption?.series);
  }, [consumption, costsData]);
  const pvpcData = useMemo(() => mergePreciosEmisiones(precios, emisiones), [precios, emisiones]);

  const consumoMes = summary?.consumo_kwh || 0;
  const costeMes = summary?.coste_euros || 0;
  const ratio = summary?.ratio_eficiencia || 0;
  const precioMedio = consumoMes > 0 ? costeMes / consumoMes : 0;

  // Δ% vs mes anterior — SOLO comparamos si tenemos ≥2 meses con datos SUFICIENTES.
  // Un mes "en curso" con pocos días descartaría comparaciones erráticas.
  const lastMonth = yearData.length > 0 ? yearData[yearData.length - 1] : null;
  const prevMonth = yearData.length >= 2 ? yearData[yearData.length - 2] : null;
  // Comparamos solo si el mes anterior está "completo" (≥14 días) o si ambos son parciales similares.
  const validDelta = lastMonth && prevMonth && !lastMonth.isCurrent && prevMonth.daysWithData >= 14;
  const deltaPct = (last, prev) => (prev && prev > 0) ? ((last - prev) / prev) * 100 : null;
  // Comparativa: últimos 30 días frente a los 30 anteriores... aproximada con el backend (/costs):
  // media diaria del mes en curso vs mes anterior completo (no penaliza meses a medias).
  const dv = costsData?.delta_vs_prev_month;
  const cm = costsData?.current_month;
  const prevPrice = dv && dv.prev_kwh > 0 ? dv.prev_cost_eur / dv.prev_kwh : null;
  const curPrice = cm && cm.kwh > 0 ? cm.cost_eur / cm.kwh : null;
  const dConsumo = dv && Number.isFinite(dv.delta_pct) ? dv.delta_pct : (validDelta ? deltaPct(lastMonth.consumoKWh, prevMonth.consumoKWh) : null);
  const dPrecio = prevPrice && curPrice ? deltaPct(curPrice, prevPrice) : (validDelta ? deltaPct(lastMonth.coste / lastMonth.consumoKWh, prevMonth.coste / prevMonth.consumoKWh) : null);
  const dCoste = dConsumo !== null && dPrecio !== null ? ((1 + dConsumo / 100) * (1 + dPrecio / 100) - 1) * 100 : (validDelta ? deltaPct(lastMonth.coste, prevMonth.coste) : null);

  // Benchmarks sector (kWh/m² anual) — referencia genérica vivienda rural
  const BENCH_SECTOR = 100;   // kWh/m² promedio
  const BENCH_OBJETIVO = 80;  // kWh/m² objetivo

  const reparto = breakdown?.consumo;
  const repartoRows = reparto ? [
    { name: "Hora cara",   pct: reparto.punta_pct, value: reparto.punta_kwh, color: COLORS.punta },
    { name: "Hora normal", pct: reparto.llano_pct, value: reparto.llano_kwh, color: COLORS.llano },
    { name: "Hora barata", pct: reparto.valle_pct, value: reparto.valle_kwh, color: COLORS.valle },
  ].sort((a, b) => b.pct - a.pct) : [];
  const repartoDominante = repartoRows.length > 0 && repartoRows[0].pct >= 95;

  return (
    <div style={{ padding: 32 }}>
      <h2 style={S.title}>{t("consumption.title")}</h2>
      <p style={S.sub}>{t("consumption.subtitle", { name: acc.nombre || acc.locationID })}</p>

      {/* Visor Datadis (Diario / Semanal / Mensual / Anual) — on-demand con cache */}
      <SectionErrorBoundary resetKey={id}>
        <DatadisPeriodViewer locationID={id} />
      </SectionErrorBoundary>

      {/* KPIs principales — el coste lo destacamos visualmente (lo que más importa al cliente) */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
        <KPIDelta label={t("consumption.costeMes")} value={"€" + fmtKWh(costeMes)} delta={dCoste} betterWhenDown color="#8E3C20" highlight />
        <KPIDelta label={t("consumption.consumoMes")} value={fmtKWh(consumoMes)} unit="kWh" delta={dConsumo} betterWhenDown color="#283226" />
        <KPIDelta label={t("consumption.precioMedio")} value={"€" + precioMedio.toFixed(3)} unit="/kWh" delta={dPrecio} betterWhenDown color="#3D6E8E" />
        <KPIDelta label={t("consumption.efficiency")} value={ratio ? ratio.toFixed(1) : "—"} unit="kWh/m²" color={COLORS.primaryLight} note={t("consumption.efficiencyNote")} />
      </div>

      {/* Panel de proyecciones movido a Predicción */}

      {/* ═══════ CERTIFICADO EFICIENCIA A-G (backlog #8) ═══════ */}
      {effData && (
        <div style={{ marginBottom: 24 }}>
          <EfficiencyBadge data={effData} />
        </div>
      )}

      {/* ═══════ PANEL SOSTENIBILIDAD + BOSQUE VIRTUAL (backlog #9+#10) ═══════ */}
      {sustData && <SustainabilityPanel data={sustData} />}

      {/* Centro: si hay 1 mes solo → resumen sin gráfico. Si hay ≥2 → barras evolución */}
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16, marginBottom: 16 }}>
        {yearData.length <= 1 ? (
          <Card title="Resumen del mes" subtitle="Datadis publica datos con 1 mes de retraso. Histórico mensual disponible en breve.">
            <div style={{ padding: "20px 8px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
              <div style={{ textAlign: "center", padding: 16, background: "#F7F7F7", borderRadius: 10 }}>
                <div style={{ fontSize: 11, color: "#7D8478", textTransform: "uppercase", letterSpacing: 0.5 }}>Consumo</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: "#283226", margin: "6px 0 2px" }}>{fmtKWh(consumoMes)}</div>
                <div style={{ fontSize: 11, color: "#7D8478" }}>kWh este mes</div>
              </div>
              <div style={{ textAlign: "center", padding: 16, background: "#F3E1D8", borderRadius: 10, border: "1px solid #8C2E1C33" }}>
                <div style={{ fontSize: 11, color: "#8E3C20", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Coste</div>
                <div style={{ fontSize: 30, fontWeight: 700, color: "#8E3C20", margin: "6px 0 2px" }}>€{fmtKWh(costeMes)}</div>
                <div style={{ fontSize: 11, color: "#7D8478" }}>facturado este mes</div>
              </div>
              <div style={{ textAlign: "center", padding: 16, background: "#E2ECF2", borderRadius: 10 }}>
                <div style={{ fontSize: 11, color: "#3D6E8E", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Precio medio</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: "#3D6E8E", margin: "6px 0 2px" }}>€{precioMedio.toFixed(3)}</div>
                <div style={{ fontSize: 11, color: "#7D8478" }}>por kWh</div>
              </div>
            </div>
            <div style={{ padding: "12px 16px", background: "#FAFAFA", borderRadius: 10, fontSize: 12, color: "#7D8478", marginTop: 8 }}>
              La evolución mensual aparecerá automáticamente a medida que se acumulen más periodos de facturación.
            </div>
          </Card>
        ) : (
          <Card title="Consumo y coste mensuales" subtitle={yearData.length === 1 ? "Primer mes con datos completos" : `Últimos ${yearData.length} meses con datos`}>
            {lCons ? <LoadingBox /> : (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={yearData} margin={{ top: 18, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#4B5347" }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#4B5347" }} tickFormatter={fmtKWh} width={50} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#8E3C20" }} tickFormatter={(v) => "€" + fmtKWh(v)} width={56} />
                  <Tooltip formatter={(v, name) => name && name.startsWith("Coste") ? "€" + fmtKWh(v) : fmtKWh(v) + " kWh"} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="consumoKWh" name="Consumo (kWh)" fill={COLORS.primaryLight} radius={[2, 2, 0, 0]} maxBarSize={64}>
                    {yearData.length <= 3 && <LabelList dataKey="consumoKWh" position="top" formatter={(v) => fmtKWh(v)} fill="#283226" fontSize={11} fontWeight={700} />}
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="coste" name="Coste (€)" stroke="#8E3C20" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Card>
        )}

        <Card title="Cuándo consumes más" subtitle="% consumo por franja tarifaria">
          {repartoRows.length === 0 ? <LoadingBox label="Sin datos suficientes" /> :
            repartoDominante ? (() => {
              const dom = repartoRows[0];
              const esBarata = dom.name === "Hora barata";
              const ahorroEstimado = esBarata ? costeMes * 0.35 : 0;
              return (
                <div style={{ height: 280, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 16 }}>
                  <IconTile icon={Target} size={56} />
                  <div style={{ fontSize: 28, fontWeight: 700, color: dom.color, margin: "12px 0 4px" }}>{dom.pct.toFixed(1)}%</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "#283226" }}>en {dom.name.toLowerCase()}</div>
                  {esBarata && ahorroEstimado > 0 && (
                    <div style={{ fontSize: 13, color: "#6F8F4E", marginTop: 10, fontWeight: 700 }}>
                      Ahorro estimado: <strong>€{fmtKWh(ahorroEstimado)}</strong> este mes
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: "#6F8F4E", marginTop: 8, fontWeight: 600 }}>
                    {esBarata ? "Patrón óptimo de ahorro" : dom.name === "Hora cara" ? "Oportunidad de mejora" : "Patrón estándar"}
                  </div>
                </div>
              );
            })() : (
              <div style={{ padding: "20px 16px", display: "flex", flexDirection: "column", gap: 18 }}>
                {repartoRows.map((r) => (
                  <div key={r.name}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#283226", fontWeight: 600 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: r.color }} />
                        {r.name}
                      </span>
                      <span style={{ fontWeight: 700, color: "#283226" }}>{r.pct.toFixed(1)}%</span>
                    </div>
                    <div style={{ background: "#EEF0EB", borderRadius: 4, height: 12, overflow: "hidden" }}>
                      <div style={{ width: r.pct + "%", height: "100%", background: r.color, transition: "width 0.4s" }} />
                    </div>
                  </div>
                ))}
              </div>
            )
          }
        </Card>
      </div>

      {/* Panel conclusiones automáticas */}
      <ConclusionsPanel
        repartoDominante={repartoDominante}
        dominantFranja={repartoRows[0]}
        precioMedio={precioMedio}
        hasHistorico={yearData.length >= 2}
      />

      <Card title="Precio de la luz y emisiones de hoy" subtitle="Relación entre precio y CO₂">
        {pvpcData.length === 0 ? <LoadingBox label="Sin datos PVPC/CO2" /> : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={pvpcData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" />
              <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#4B5347" }} interval={2} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => "€" + v.toFixed(2)} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit=" kg" />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area yAxisId="left" type="monotone" dataKey="precio" name="Precio (€/kWh)" stroke="#C08B2C" fill="#C08B2C" fillOpacity={0.3} strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="co2" name="kgCO₂/kWh" stroke="#B4532F" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}

// ─── Panel de análisis avanzado del forecast v4 ───────────────
// Muestra: tendencia, comparativa MoM/YoY, anomalías detectadas,
// meses outlier y ranking días de la semana. Todo animado.
function ForecastAnalyticsPanel({ analytics }) {
  if (!analytics) return null;
  const { stats = {}, trend = {}, comparatives = {}, anomalies = [], outlier_months = [], dow_ranking = [] } = analytics;
  const DOW = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
  const arrowFor = (n) => (n == null ? "—" : n > 0 ? "▲" : n < 0 ? "▼" : "▬");
  const colorFor = (n, betterDown = true) => {
    if (n == null) return "#7D8478";
    const bad = betterDown ? n > 0 : n < 0;
    return bad ? "#8E3C20" : "#406020";
  };
  const trendColor = { up: "#8E3C20", down: "#406020", stable: "#7D8478" }[trend.direction] || "#7D8478";
  const trendArrow = { up: "▲", down: "▼", stable: "▬" }[trend.direction] || "▬";

  return (
    <section className="er-fade-up" style={{
      background: "#fff", borderRadius: 16, padding: "26px 30px",
      border: "1px solid #E4E7E0", marginBottom: 24, boxShadow: "none",
      fontFamily: "var(--font-sans)",
    }}>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 22, fontWeight: 700, color: "#283226", margin: 0 }}>Análisis avanzado</h3>
        <p style={{ fontSize: 13, color: "#7D8478", margin: "4px 0 0" }}>Detección de anomalías (Z-score), comparativas MoM/YoY y detección de meses fuera de control (1.5 IQR).</p>
      </div>

      {/* Row 1: 3 KPIs de comparativas */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 20 }}>
        <div className="er-card-lift er-pop-in" style={{ background: "#F5EBD3", borderRadius: 12, padding: "18px 20px", animationDelay: "80ms" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#4B5347", textTransform: "uppercase", letterSpacing: 0.8 }}>Tendencia 30 días</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: trendColor, marginTop: 6, letterSpacing: -0.4 }}>
            {trendArrow} {trend.trend_pct_30d !== null ? Math.abs(trend.trend_pct_30d).toFixed(1) + "%" : "—"}
          </div>
          <div style={{ fontSize: 12, color: "#7D8478", marginTop: 4 }}>{trend.direction === "up" ? "Subida gradual" : trend.direction === "down" ? "Bajada gradual" : "Estable"} · regresión lineal</div>
        </div>
        <div className="er-card-lift er-pop-in" style={{ background: "#F3E1D8", borderRadius: 12, padding: "18px 20px", animationDelay: "160ms" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#4B5347", textTransform: "uppercase", letterSpacing: 0.8 }}>Últimas 4 sem. vs 4 previas</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: colorFor(comparatives.mom_pct), marginTop: 6, letterSpacing: -0.4 }}>
            {arrowFor(comparatives.mom_pct)} {comparatives.mom_pct !== null ? Math.abs(comparatives.mom_pct).toFixed(1) + "%" : "—"}
          </div>
          <div style={{ fontSize: 12, color: "#7D8478", marginTop: 4 }}>Δ Consumo mes actual vs anterior</div>
        </div>
        <div className="er-card-lift er-pop-in" style={{ background: "#E2ECF2", borderRadius: 12, padding: "18px 20px", animationDelay: "240ms" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#4B5347", textTransform: "uppercase", letterSpacing: 0.8 }}>Año sobre año</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: colorFor(comparatives.yoy_pct), marginTop: 6, letterSpacing: -0.4 }}>
            {arrowFor(comparatives.yoy_pct)} {comparatives.yoy_pct !== null ? Math.abs(comparatives.yoy_pct).toFixed(1) + "%" : "—"}
          </div>
          <div style={{ fontSize: 12, color: "#7D8478", marginTop: 4 }}>{comparatives.prev_year_month ? `vs ${comparatives.prev_year_month}` : "Sin datos año anterior"}</div>
        </div>
      </div>

      {/* Row 2: Anomalías + Meses fuera de control */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div className="er-card-lift er-fade-up" style={{ background: "#FAFAFA", borderRadius: 12, padding: "18px 20px", border: "1px solid #E4E7E0", animationDelay: "300ms" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#283226", marginBottom: 10 }}>Anomalías detectadas (últimos 60 días)</div>
          {anomalies.length === 0 ? (
            <div style={{ fontSize: 13, color: "#406020", padding: 8 }}>Sin anomalías. Consumo dentro del patrón habitual (|Z| ≤ 2).</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto" }}>
              {anomalies.map((a, i) => (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 12px", background: "#fff", borderRadius: 12,
                  borderLeft: `4px solid ${a.severity === "critical" ? "#8E3C20" : "#C08B2C"}`,
                }}>
                  <span style={{ fontSize: 13, color: "#283226", fontWeight: 600 }}>{a.time}</span>
                  <span style={{ fontSize: 12, color: "#4B5347" }}>{a.kwh.toFixed(1)} kWh</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: a.severity === "critical" ? "#8E3C20" : "#9A6E1D" }}>
                    Z = {a.z > 0 ? "+" : ""}{a.z}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="er-card-lift er-fade-up" style={{ background: "#FAFAFA", borderRadius: 12, padding: "18px 20px", border: "1px solid #E4E7E0", animationDelay: "380ms" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#283226", marginBottom: 10 }}>Meses fuera de control (1.5 IQR)</div>
          {outlier_months.length === 0 ? (
            <div style={{ fontSize: 13, color: "#406020", padding: 8 }}>Ningún mes en el histórico se sale del rango típico.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {outlier_months.map((m, i) => (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 12px", background: "#fff", borderRadius: 12,
                  borderLeft: `4px solid ${m.direction === "high" ? "#8E3C20" : "#3D6E8E"}`,
                }}>
                  <span style={{ fontSize: 13, color: "#283226", fontWeight: 600 }}>{m.month}</span>
                  <span style={{ fontSize: 12, color: "#4B5347" }}>{m.total.toFixed(0)} kWh</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: m.direction === "high" ? "#8E3C20" : "#3D6E8E" }}>
                    {m.direction === "high" ? "▲ Alto" : "▼ Bajo"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Ranking días semana + stats técnicas */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <div className="er-card-lift er-fade-up" style={{ background: "#FAFAFA", borderRadius: 12, padding: "18px 20px", border: "1px solid #E4E7E0", animationDelay: "460ms" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#283226", marginBottom: 12 }}>Ranking de consumo por día de la semana</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {dow_ranking.map((d, i) => {
              const max = dow_ranking[0]?.kwh || 1;
              const pct = (d.kwh / max) * 100;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 80, fontSize: 13, fontWeight: 600, color: "#283226" }}>{DOW[d.dow]}</span>
                  <div style={{ flex: 1, background: "#EEF0EB", borderRadius: 10, height: 10, position: "relative", overflow: "hidden" }}>
                    <div className="er-progress-bar" style={{
                      width: pct + "%", height: "100%",
                      background: `linear-gradient(90deg, #6F8F4E 0%, ${i === 0 ? "#8E3C20" : "#406020"} 100%)`,
                    }} />
                  </div>
                  <span style={{ width: 60, textAlign: "right", fontSize: 12, color: "#4B5347", fontWeight: 600 }}>{d.kwh.toFixed(1)} kWh</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="er-card-lift er-fade-up" style={{ background: "#EBF2E4", borderRadius: 12, padding: "18px 20px", animationDelay: "540ms" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#4B5347", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Base estadística</div>
          <div style={{ fontSize: 12, color: "#4B5347", lineHeight: 1.8 }}>
            <div><b>Media:</b> {stats.mean_kwh} kWh/día</div>
            <div><b>Mediana:</b> {stats.median_kwh} kWh/día</div>
            <div><b>Desv. típica:</b> ±{stats.stdev_kwh} kWh</div>
            <div><b>Días analizados:</b> {stats.days_analyzed}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ForecastPage({ acc }) {
  const t = useT();
  const id = acc.locationID;
  const planRaw = String(acc.plan_contratado || acc.plan || "").toLowerCase();
  const isBorrador = !planRaw || planRaw === "borrador" || acc.estado === "borrador";
  if (isBorrador) {
    return (
      <div style={{ padding: 32, maxWidth: 780, margin: "0 auto" }}>
        <h2 style={S.title}>{t("forecast.title")}</h2>
        <p style={S.sub}>{t("forecast.subtitle", { name: acc.nombre || acc.locationID })}</p>
        <div style={{ background: "#fff", borderRadius: 16, padding: 44, border: "1px dashed #D2DFC3", textAlign: "center", boxShadow: "none" }}>
          <div style={{ width: 88, height: 88, borderRadius: 16, background: "#EEF0EB", color: "#7D8478", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 42 }}><TrendingUp size={34} strokeWidth={1.4} /></div>
          <h3 style={{ ...S.title, fontSize: 22, marginBottom: 8 }}>Sin histórico para predicción</h3>
          <p style={{ ...S.sub, marginBottom: 20, fontSize: 15, maxWidth: 480, margin: "0 auto" }}>
            La predicción requiere al menos 14 días de datos Datadis. En cuanto se contrate el Plan Básico y llegue el primer histórico, aquí verás la predicción de consumo por día de la semana con banda de confianza y coste proyectado.
          </p>
        </div>
      </div>
    );
  }
  const { data: forecast, loading: lFor } = useApi(() => api.getForecast(id), [id]);
  const { data: pvpcT, loading: lTom } = useApi(api.getPreciosTomorrow, [], { pollMs: 1800000 });
  const { data: summary } = useApi(() => api.getConsumptionSummary(id), [id]);
  const { data: costsData } = useApi(() => api.getCosts(id), [id]);

  const forecastChart = useMemo(() => transformForecastChart(forecast), [forecast]);
  const pvpcTomorrowChart = useMemo(() => transformPVPCTomorrow(pvpcT), [pvpcT]);

  const stats = pvpcT?.stats;
  const cheapestHour = stats?.cheapest_hour ?? "—";
  const expensiveHour = stats?.most_expensive_hour ?? "—";
  const cheapestPrice = stats?.min;
  const expensivePrice = stats?.max;
  const diffPct = stats?.diff_percent;

  const consumoMes = summary?.consumo_kwh || 0;
  const costeProj = +(consumoMes * 0.156).toFixed(2);
  const co2Annual = +((consumoMes * 12 * 0.15) / 1000).toFixed(2);

  return (
    <div style={{ padding: 32 }}>
      <h2 style={S.title}>{t("forecast.title")}</h2>
      <p style={S.sub}>{t("forecast.subtitle", { name: acc.nombre || acc.locationID })}</p>

      {/* ─────────────────────────────────────────────────────── */}
      {/* SECCIÓN 1 · PREDICCIÓN — grande, hero, animada           */}
      {/* ─────────────────────────────────────────────────────── */}
      <section className="er-fade-up" style={{
        background: "#F3F5EE",
        borderRadius: 16, padding: "28px 32px", marginBottom: 24,
        border: "1px solid #D2DFC3", boxShadow: "none",
      }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
          <div>
            <h3 style={{ fontSize: 26, fontWeight: 700, color: "#283226", margin: 0, letterSpacing: -0.5 }}>Predicción de consumo</h3>
            <p style={{ fontSize: 14, color: "#4B5347", margin: "4px 0 0" }}>Próximos 7 días · 3 modelos validados sobre {forecast?.cache_stats?.days_available || "—"} días de histórico Datadis{forecast?.analytics?.precision ? ` · error medio ${forecast.analytics.precision.mae_kwh} kWh/día` : ""}</p>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", background: "#fff", padding: "6px 12px", borderRadius: 16, border: "1px solid #D2DFC3", fontSize: 12, color: "#4B5347" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#6F8F4E" }} />
            Banda de confianza P5–P95
          </div>
        </div>

        {lFor ? <LoadingBox height={420} label="Calculando predicción…" /> : forecastChart.length === 0 ? <LoadingBox label="Sin histórico suficiente" height={420} /> : (
          <ChartErrorBoundary>
            <ResponsiveContainer width="100%" height={420}>
              <ComposedChart data={forecastChart} margin={{ top: 20, right: 20, left: 10, bottom: 10 }}>
                <defs>
                  <linearGradient id="predBandGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6F8F4E" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#6F8F4E" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#283226", fontWeight: 600 }} />
                <YAxis tick={{ fontSize: 12, fill: "#4B5347" }} tickFormatter={fmtKWh} width={56} label={{ value: "kWh", angle: -90, position: "insideLeft", fill: "#7D8478", fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #D2DFC3", borderRadius: 10, boxShadow: "none" }}
                  formatter={(v, name) => [fmtKWhPrecise(v) + " kWh", name]}
                />
                <Legend wrapperStyle={{ fontSize: 13, paddingTop: 8 }} />
                <ReferenceLine x="Hoy" stroke="#B4532F" strokeDasharray="5 5" strokeWidth={2} label={{ value: "Hoy", fill: "#B4532F", fontSize: 12, fontWeight: 700, position: "top" }} />
                <Area type="monotone" dataKey="intervalo" name="Intervalo de confianza" stroke="none" fill="url(#predBandGrad)" isAnimationActive={true} animationDuration={1200} />
                <Line type="monotone" dataKey="real" name="Consumo real" stroke="#406020" strokeWidth={3.2} dot={{ r: 5, fill: "#406020", strokeWidth: 2, stroke: "#fff" }} activeDot={{ r: 7 }} isAnimationActive={true} animationDuration={1400} />
                <Line type="monotone" dataKey="prediccion" name="Predicción" stroke="#3D6E8E" strokeWidth={3.2} strokeDasharray="8 5" dot={{ r: 5, fill: "#3D6E8E", strokeWidth: 2, stroke: "#fff" }} activeDot={{ r: 7 }} isAnimationActive={true} animationDuration={1600} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartErrorBoundary>
        )}

        <div style={{ marginTop: 14, background: "rgba(255, 248, 225, 0.7)", border: "1px solid #E8D7AE", borderRadius: 12, padding: "12px 18px", color: "#7A5A12", fontSize: 12.5, lineHeight: 1.5 }}>
          <b>Modelo:</b> {forecast?.method_text || "Mediana por día de la semana + suavizado exponencial + regresión. Banda P5-P95 empírica sobre los residuos."}
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────── */}
      {/* SECCIÓN 1.5 · PROYECCIONES Y COMPARATIVAS                */}
      {/* ─────────────────────────────────────────────────────── */}
      {costsData && (
        <SectionErrorBoundary resetKey={id + "_costs"}>
          <CostsProjectionPanel data={costsData} />
        </SectionErrorBoundary>
      )}

      {/* ─────────────────────────────────────────────────────── */}
      {/* SECCIÓN 2 · PVPC MAÑANA + KPIs proyección                */}
      {/* ─────────────────────────────────────────────────────── */}
      <div style={{ background: "#283226", borderRadius: 16, padding: 24, marginBottom: 20, color: "#fff", boxShadow: "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: "#B9CBA3", textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700 }}>Precio de la luz mañana</div>
          {pvpcT?.source && (
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: pvpcT.source === "ree_official" ? "#6F8F4E" : "#C08B2C", color: pvpcT.source === "ree_official" ? "#fff" : "#7A5A12", fontWeight: 700, letterSpacing: 0.4 }}>
              {pvpcT.source === "ree_official" ? "OFICIAL REE" : "ESTIMADO"}
            </span>
          )}
        </div>
        {lTom ? <LoadingBox dark height={80} label="Cargando PVPC mañana…" /> : !pvpcT?.available && !pvpcT?.estimated ? (
          <div style={{ fontSize: 13, color: "#B9CBA3" }}>{pvpcT?.note || "PVPC mañana aún no disponible (se publica a las 20:15)"}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20 }}>
            <div>
              <div style={{ fontSize: 11, color: "#B9CBA3" }}>Hora más barata</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#6F8F4E" }}>{cheapestHour}h</div>
              <div style={{ fontSize: 13, color: "#EBF2E4" }}>€{cheapestPrice?.toFixed(4)}/kWh</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#B9CBA3" }}>Hora más cara</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#E6C3B3" }}>{expensiveHour}h</div>
              <div style={{ fontSize: 13, color: "#F3E1D8" }}>€{expensivePrice?.toFixed(4)}/kWh</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#B9CBA3" }}>Diferencia máx./mín.</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#C08B2C" }}>{diffPct}%</div>
              <div style={{ fontSize: 13, color: "#F5EBD3" }}>ahorro potencial</div>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <Card title="Curva PVPC mañana" subtitle={pvpcT?.source === "ree_official" ? "Datos oficiales publicados por REE" : `${pvpcT?.method || "Estimación a partir de ESIOS"}${pvpcT?.backtest ? ` · error medio aplicado a hoy: ${(pvpcT.backtest.mae_eur_kwh * 100).toFixed(1)} c€/kWh` : ""}`}>
          {lTom ? <LoadingBox /> : pvpcTomorrowChart.length === 0 ? <LoadingBox label="Sin datos disponibles" /> : (
            <ChartErrorBoundary>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={pvpcTomorrowChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="forecastGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3D6E8E" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#3D6E8E" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" />
                  <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#4B5347" }} interval={2} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => "€" + v.toFixed(2)} />
                  <Tooltip formatter={(v) => "€" + Number(v).toFixed(4) + "/kWh"} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {cheapestPrice && <ReferenceLine y={cheapestPrice} stroke="#6F8F4E" strokeDasharray="3 3" label={{ value: "Mín", fill: "#6F8F4E", fontSize: 10, position: "right" }} />}
                  {expensivePrice && <ReferenceLine y={expensivePrice} stroke="#B4532F" strokeDasharray="3 3" label={{ value: "Máx", fill: "#B4532F", fontSize: 10, position: "right" }} />}
                  <Area type="monotone" dataKey="precio" name={pvpcT?.source === "ree_official" ? "PVPC oficial (€/kWh)" : "PVPC estimado (€/kWh)"} stroke="#3D6E8E" fill="url(#forecastGrad)" strokeWidth={2.5} isAnimationActive={true} animationDuration={1200} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartErrorBoundary>
          )}
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 32 }}>
        <Card title="Coste mes proyectado">
          <div style={{ textAlign: "center", padding: 10 }}>
            <div style={{ fontSize: 36, fontWeight: 700, color: "#8E3C20" }}>€{costeProj}</div>
            <div style={{ fontSize: 12, color: "#7D8478", marginTop: 4 }}>Si mantienes el consumo actual</div>
            <div style={{ marginTop: 12, padding: 8, background: "#F7F7F7", borderRadius: 12, fontSize: 11, color: "#4B5347" }}>
              Precio medio aplicado: €0.156/kWh
            </div>
          </div>
        </Card>
        <Card title="Huella CO₂ anual proyectada">
          <div style={{ textAlign: "center", padding: 10 }}>
            <div style={{ fontSize: 36, fontWeight: 700, color: COLORS.primary }}>{co2Annual} t</div>
            <div style={{ fontSize: 12, color: "#7D8478", marginTop: 4 }}>tCO₂/año</div>
            <div style={{ marginTop: 12, padding: 8, background: "#EBF2E4", borderRadius: 12, fontSize: 11, color: "#406020" }}>
              Equivalente a {Math.round((consumoMes * 12 * 0.15) / 22)} árboles/año
            </div>
          </div>
        </Card>
        <Card title="Consumo base diario">
          <div style={{ textAlign: "center", padding: 10 }}>
            <div style={{ fontSize: 36, fontWeight: 700, color: forecast?.base_avg_kwh ? COLORS.primary : "#7D8478" }}>
              {forecast?.base_avg_kwh ? forecast.base_avg_kwh.toFixed(1) : "--"}
            </div>
            <div style={{ fontSize: 12, color: "#7D8478", marginTop: 4 }}>kWh/día promedio</div>
            <div style={{ marginTop: 12, padding: 8, background: "#F7F7F7", borderRadius: 12, fontSize: 11, color: "#4B5347" }}>
              Calculado sobre últimos 30 días
            </div>
          </div>
        </Card>
      </div>

      {/* ─────────────────────────────────────────────────────── */}
      {/* SECCIÓN 3 · ANÁLISIS AVANZADO — título grande separado   */}
      {/* ─────────────────────────────────────────────────────── */}
      {forecast?.analytics && (
        <>
          <div style={{ margin: "40px 0 20px", padding: "20px 0 14px", borderTop: "2px solid #D2DFC3" }}>
            <h2 style={{ fontSize: 30, fontWeight: 900, color: "#283226", margin: 0, letterSpacing: -0.6 }}>Análisis avanzado</h2>
            <p style={{ fontSize: 15, color: "#4B5347", margin: "6px 0 0" }}>Estadísticas del histórico, anomalías detectadas y comparativas mes/año</p>
          </div>
          <ForecastAnalyticsPanel analytics={forecast.analytics} />
        </>
      )}

      {/* ─────────────────────────────────────────────────────── */}
      {/* SECCIÓN 4 · CONTEXTO HISTÓRICO                           */}
      {/* ─────────────────────────────────────────────────────── */}
      <div style={{ margin: "40px 0 20px", padding: "20px 0 14px", borderTop: "2px solid #D2DFC3" }}>
        <h2 style={{ fontSize: 30, fontWeight: 900, color: "#283226", margin: 0, letterSpacing: -0.6 }}>Contexto histórico</h2>
        <p style={{ fontSize: 15, color: "#4B5347", margin: "6px 0 0" }}>Explora tus datos Datadis por día, semana, mes o año para entender la predicción</p>
      </div>
      <SectionErrorBoundary resetKey={id + "_hist"}>
        <DatadisPeriodViewer locationID={id} initialGranularity="week" title="" />
      </SectionErrorBoundary>
    </div>
  );
}

// ─── AHORRO: potencia contratada, placas solares y tarifas ───────────────
function SavingsCard({ title, subtitle, badge, children, metodologia }) {
  return (
    <section className="er-card-lift er-fade-up" style={{ background: "#fff", borderRadius: 16, padding: "24px 26px", border: "1px solid #E4E7E0", marginBottom: 22, boxShadow: "none", fontFamily: "var(--font-sans)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ fontSize: 22, fontWeight: 700, color: "#283226", margin: 0 }}>{title}</h3>
          {subtitle && <p style={{ fontSize: 14, color: "#4B5347", margin: "6px 0 0", lineHeight: 1.5 }}>{subtitle}</p>}
        </div>
        {badge}
      </div>
      {children}
      {metodologia && (
        <details style={{ marginTop: 14, fontSize: 12.5, color: "#7D8478" }}>
          <summary style={{ cursor: "pointer", fontWeight: 700, color: "#406020" }}>¿Cómo lo calculamos?</summary>
          <div style={{ marginTop: 8, lineHeight: 1.6 }}>
            {typeof metodologia === "string" ? <p style={{ margin: 0 }}>{metodologia}</p>
              : Object.values(metodologia).map((v, i) => <p key={i} style={{ margin: "0 0 6px" }}>{v}</p>)}
          </div>
        </details>
      )}
    </section>
  );
}
const SavingsBadge = ({ eur, label = "al año" }) => eur > 0 ? (
  <div style={{ borderLeft: "2px solid #6F8F4E", padding: "2px 0 2px 14px", textAlign: "left" }}>
    <div className="er-eyebrow">Ahorro estimado</div>
    <div className="er-figure" style={{ fontSize: 30, color: "#406020", marginTop: 6 }}><CountUp value={`${Math.round(eur).toLocaleString("es-ES")} €`} /> <span style={{ fontFamily: "var(--font-sans)", fontSize: 13, color: "#7D8478" }}>{label}</span></div>
  </div>
) : null;
const kv = (k, v, strong) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "7px 0", borderBottom: "1px solid #EEF0EB", fontSize: 14 }}>
    <span style={{ color: "#7D8478" }}>{k}</span><span style={{ color: "#283226", fontWeight: strong ? 800 : 600, textAlign: "right" }}>{v}</span>
  </div>
);

function SavingsPage({ acc }) {
  const id = acc?.locationID;
  const { data: pot, loading: lPot } = useApi(() => api.getAhorroPotencia(id), [id]);
  const { data: sol, loading: lSol } = useApi(() => api.getAhorroSolar(id), [id]);
  const [ofertas, setOfertas] = useState({ fija: "", fija_p1: "", punta: "", llano: "", valle: "", per_p1: "" });
  const [tarParams, setTarParams] = useState({});
  const { data: tar, loading: lTar } = useApi(() => api.getAhorroTarifas(id, tarParams), [id, JSON.stringify(tarParams)]);
  const [kwpSel, setKwpSel] = useState(null);
  const opcionSolar = sol?.opciones?.find(o => o.kwp === (kwpSel ?? sol?.recomendada?.kwp)) || sol?.recomendada;
  const fmtE = (v) => `${Math.round(v).toLocaleString("es-ES")} €`;
  const input = (k, ph) => (
    <input value={ofertas[k]} onChange={e => setOfertas({ ...ofertas, [k]: e.target.value })} placeholder={ph} inputMode="decimal"
      style={{ width: "100%", padding: "8px 10px", borderRadius: 10, border: "1px solid #D2DFC3", fontFamily: "var(--font-sans)", fontSize: 14 }} />
  );
  return (
    <div style={{ padding: 32 }}>
      <h2 style={S.title}>Ahorro</h2>
      <p style={S.sub}>Dónde puede ahorrar {acc?.nombre || id}, calculado con su consumo real hora a hora.</p>

      {/* 1. POTENCIA CONTRATADA */}
      <SavingsCard title="Potencia contratada" subtitle={lPot ? "Analizando tu maxímetro…" : pot?.resumen}
        badge={<SavingsBadge eur={pot?.ahorro_total_eur_anual || 0} />} metodologia={pot?.metodologia}>
        {lPot ? <LoadingBox /> : (pot?.suministros || []).map(su => (
          <div key={su.cups} style={{ marginBottom: 18 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 18, alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 12, color: "#7D8478", marginBottom: 4 }}>Suministro {su.cups}</div>
                {kv("Contratada (P1 / P2)", `${su.contratada_kw[0]} / ${su.contratada_kw[1]} kW`)}
                {kv("Máximo en 12 meses", su.maximo_12m?.p1 ? `${su.maximo_12m.p1.kw} kW · ${su.maximo_12m.p1.date}` : "—")}
                {kv("Recomendada (P1 / P2)", `${su.recomendada_kw[0]} / ${su.recomendada_kw[1]} kW`, true)}
                <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 12, fontSize: 13.5, lineHeight: 1.45,
                  background: su.accion === "bajar" ? "#EBF2E4" : su.accion === "subir" ? "#F5EBD3" : "#F7F7F7",
                  color: su.accion === "subir" ? "#8E3C20" : "#283226" }}>
                  {su.accion === "bajar" ? `Puedes bajar la potencia. ${su.texto} Ahorro: ${fmtE(su.ahorro_eur_anual)}/año.` : su.accion === "subir" ? su.texto : su.texto}
                </div>
              </div>
              <div style={{ height: 220, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={su.serie} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E7E0" />
                    <XAxis dataKey="mes" tick={{ fontSize: 10 }} tickFormatter={m => m.substring(5) + "/" + m.substring(2, 4)} />
                    <YAxis tick={{ fontSize: 11 }} unit=" kW" width={52} domain={[0, (dataMax) => Math.ceil(Math.max(dataMax, su.contratada_kw[0], su.recomendada_kw[0]) * 1.12)]} allowDecimals={false} />
                    <Tooltip formatter={(v, n) => [`${v} kW`, n]} />
                    <Bar dataKey="p1_kw" name="Máximo P1 (punta y llano)" fill="#C08B2C" radius={[2, 2, 0, 0]} isAnimationActive={false} />
                    <Bar dataKey="p2_kw" name="Máximo P2 (valle)" fill="#6F8F4E" radius={[2, 2, 0, 0]} isAnimationActive={false} />
                    <ReferenceLine y={su.contratada_kw[0]} stroke="#B4532F" strokeDasharray="5 4" label={{ value: "Contratada", position: "insideTopRight", fontSize: 11, fill: "#B4532F" }} />
                    {su.accion === "bajar" && <ReferenceLine y={su.recomendada_kw[0]} stroke="#406020" strokeDasharray="2 3" label={{ value: "Recomendada", position: "insideBottomLeft", fontSize: 11, fill: "#406020" }} />}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        ))}
      </SavingsCard>

      {/* 2. PLACAS SOLARES */}
      <SavingsCard title="Placas solares" subtitle={lSol ? "Simulando tu tejado con datos de la Comisión Europea…" : (sol?.resumen || sol?.razon)}
        badge={<SavingsBadge eur={opcionSolar?.ahorro_eur_anual || 0} />} metodologia={sol?.metodologia}>
        {lSol ? <LoadingBox /> : sol?.disponible && (
          <>
            <div style={{ fontSize: 13, color: "#7D8478", marginBottom: 8 }}>Elige el tamaño de la instalación:</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
              {sol.opciones.map(o => {
                const act = o.kwp === opcionSolar?.kwp, rec = o.kwp === sol.recomendada?.kwp;
                return (
                  <button key={o.kwp} onClick={() => setKwpSel(o.kwp)}
                    style={{ padding: "7px 12px", borderRadius: 10, cursor: "pointer", fontFamily: "var(--font-sans)", fontWeight: 700, fontSize: 13,
                      border: act ? "2px solid #406020" : "1px solid #D2DFC3", background: act ? "#EBF2E4" : "#fff", color: "#283226" }}>
                    {o.kwp} kWp{rec ? " · recomendado" : ""}
                  </button>
                );
              })}
            </div>
            {opcionSolar && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                {[["Paneles", `≈ ${opcionSolar.paneles_aprox}`], ["Cubre tu consumo", `${opcionSolar.cobertura_pct} %`],
                  ["Inversión aprox.", fmtE(opcionSolar.inversion_eur)], ["Se amortiza en", `${opcionSolar.amortizacion_anos} años`]].map(([k, v]) => (
                  <div key={k} style={{ background: "#FAFAFA", borderRadius: 12, padding: "12px 14px" }}>
                    <div style={{ fontSize: 11, color: "#7D8478", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{k}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#283226" }}>{v}</div>
                  </div>
                ))}
              </div>
            )}
            {opcionSolar && <p style={{ fontSize: 13, color: "#4B5347", margin: "12px 0 0" }}>
              Producirían unos {opcionSolar.produccion_kwh_anual.toLocaleString("es-ES")} kWh al año; aprovecharías directamente el {opcionSolar.autoconsumo_pct} % y el resto se descontaría en la factura como excedente.
              {opcionSolar.kwp === sol.recomendada?.kwp ? " Tamaño recomendado." : ""}</p>}
          </>
        )}
      </SavingsCard>

      {/* 3. TARIFAS */}
      <SavingsCard title="¿Pagas la tarifa más barata?" subtitle={lTar ? "Comparando con tu consumo real…" : (tar?.resumen || tar?.razon)}
        badge={tar?.tarifas && tar.tarifas[0].id !== "pvpc" ? <SavingsBadge eur={-tar.tarifas[0].diferencia_vs_pvpc_eur} label="vs PVPC" /> : null}
        metodologia={tar?.metodologia}>
        {lTar ? <LoadingBox /> : tar?.disponible && (
          <>
            {tar.tarifas.map((t, i) => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", marginBottom: 8, borderRadius: 12,
                background: i === 0 ? "#EBF2E4" : "#FAFAFA", border: i === 0 ? "1px solid #C6D2B5" : "1px solid #E4E7E0" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: "#283226", display: "flex", alignItems: "center", gap: 8 }}>{t.nombre}{i === 0 && <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, letterSpacing: 0.8, textTransform: "uppercase", border: "1px solid #6F8F4E", color: "#406020", padding: "2px 6px", borderRadius: 3 }}>Mejor opción</span>}</div>
                  <div style={{ fontSize: 12, color: "#7D8478" }}>{t.nota}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#283226" }}>{fmtE(t.total_eur_anual)}<span style={{ fontSize: 12, fontWeight: 600 }}>/año</span></div>
                  {t.id !== "pvpc" && <div style={{ fontSize: 12, fontWeight: 700, color: t.diferencia_vs_pvpc_eur <= 0 ? "#406020" : "#8E3C20" }}>
                    {t.diferencia_vs_pvpc_eur <= 0 ? "−" : "+"}{fmtE(Math.abs(t.diferencia_vs_pvpc_eur))} vs PVPC</div>}
                </div>
              </div>
            ))}
            <p style={{ fontSize: 12.5, color: "#7D8478", margin: "8px 0 12px" }}>
              Tu consumo: {tar.consumo_anual_kwh.toLocaleString("es-ES")} kWh/año · {tar.reparto_pct.punta} % en hora cara, {tar.reparto_pct.llano} % normal y {tar.reparto_pct.valle} % barata.
            </p>
            <details>
              <summary style={{ cursor: "pointer", fontWeight: 700, color: "#406020", fontSize: 14 }}>Compara una oferta real (escribe sus precios)</summary>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 12 }}>
                <label style={{ fontSize: 12 }}>Precio fijo €/kWh{input("fija", "0,135")}</label>
                <label style={{ fontSize: 12 }}>Potencia fija €/kW·día{input("fija_p1", "0,095")}</label>
                <span />
                <label style={{ fontSize: 12 }}>Punta €/kWh{input("punta", "0,185")}</label>
                <label style={{ fontSize: 12 }}>Llano €/kWh{input("llano", "0,125")}</label>
                <label style={{ fontSize: 12 }}>Valle €/kWh{input("valle", "0,090")}</label>
                <label style={{ fontSize: 12 }}>Potencia periodos €/kW·día{input("per_p1", "0,095")}</label>
              </div>
              <button onClick={() => setTarParams(Object.fromEntries(Object.entries(ofertas).map(([k, v]) => [k, String(v).replace(",", ".")])))}
                style={{ marginTop: 12, background: "#406020", color: "#fff", border: "none", borderRadius: 10, padding: "9px 20px", fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-sans)" }}>
                Recalcular
              </button>
            </details>
          </>
        )}
      </SavingsCard>
    </div>
  );
}

function ContractPage({ acc }) {
  const t = useT();
  const { data: full, loading } = useApi(() => api.getClient(acc.locationID), [acc.locationID]);
  // Fetch directo del contract con useState/useEffect — los dos useApi consecutivos no siempre
  // disparan el segundo por un bug de re-render tras el loading:true→false del primero.
  const [contractResp, setContractResp] = useState(null);
  const [loadingContract, setLoadingContract] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoadingContract(true);
    api.getContract(acc.locationID)
      .then(r => { if (!cancelled) { setContractResp(r); setLoadingContract(false); } })
      .catch(() => { if (!cancelled) setLoadingContract(false); });
    return () => { cancelled = true; };
  }, [acc.locationID]);
  if (loading) return <div style={{ padding: 32 }}><LoadingBox /></div>;
  const c = full || acc;
  const ubicacion = c.ubicacion || {};
  const datadis = c.datadis || {};
  // El backend devuelve el contrato en plano (available: true); versiones antiguas lo anidaban en .contract
  const dc = contractResp?.contract || (contractResp?.available ? contractResp : null);
  const planRaw = String(c.plan_contratado || c.plan || "").toLowerCase();
  const isBorrador = !planRaw || planRaw === "borrador" || c.estado === "borrador";
  const potenciaText = (dc?.contractedPowerkW && dc.contractedPowerkW.length)
    ? dc.contractedPowerkW.map(p => `${p} kW`).join(" / ")
    : "—";
  const rows = [
    // Sección Alojamiento (info interna)
    ["Nombre", c.nombre || "—"],
    ["Municipio", ubicacion.municipio || "—"],
    ["Código postal", ubicacion.cp || "—"],
    ["Dirección", ubicacion.direccion || "—"],
    ["Superficie", ubicacion.superficie_m2 ? `${ubicacion.superficie_m2} m²` : "—"],
    ["Plan contratado", c.plan_contratado || c.plan || "—"],
    // Sección Contrato eléctrico (datos Datadis reales)
    ["CUPS", dc?.cups || datadis.cups || c.cups || "—"],
    ["Distribuidora", dc?.distributor || "—"],
    ["Comercializadora", dc?.marketer || "—"],
    ["Tensión", dc?.tension || "—"],
    ["Tarifa de acceso", dc?.accessFare || "—"],
    ["Código tarifa", dc?.codeFare || "—"],
    ["Potencia contratada", potenciaText],
    ["Control de potencia", dc?.modePowerControl || "—"],
    ["Discriminación horaria", dc?.timeDiscrimination || "—"],
    ["Inicio contrato", dc?.startDate || "—"],
    ["Fin contrato", dc?.endDate || "En vigor · sin fecha de fin"],
    ["Ubicación según contrato", dc?.municipality ? `${dc.municipality}${dc.postalCode ? ` (${dc.postalCode})` : ""}${dc.province ? `, ${dc.province}` : ""}` : "—"],
  ];
  // Alojamientos con varios suministros (p.ej. Can Orta: 2 CUPS)
  const otros = (contractResp?.contratos || []).filter(k => k.cups !== dc?.cups);
  for (const k of otros) {
    rows.push([`CUPS adicional`, `${k.cups} · ${(k.contractedPowerkW || []).map(p => `${p} kW`).join(" / ") || "—"} · ${k.endDate ? `fin ${k.endDate}` : "en vigor"}`]);
  }
  if (contractResp?.n_cups > 1 && contractResp?.total_contracted_kW) {
    rows.push(["Potencia contratada total", `${contractResp.total_contracted_kW} kW (suma de suministros en vigor)`]);
  }
  return (
    <div style={{ padding: 32 }}>
      <h2 style={S.title}>{t("contract.title")}</h2>
      <p style={S.sub}>{t("contract.subtitle", { name: c.nombre || acc.locationID })}</p>
      {isBorrador && (
        <div style={{ background: "#F5EBD3", border: "1px solid #E8D7AE", borderRadius: 12, padding: "14px 18px", marginBottom: 16, color: "#7A5A12", fontSize: 13, fontFamily: "var(--font-sans)" }}>
          <b>Alojamiento en borrador.</b> Aún no hay CUPS ni contrato eléctrico registrado. Completa el alta desde <b>Panel admin → Completar datos</b>.
        </div>
      )}
      <div style={{ background: "#fff", borderRadius: 16, padding: "8px 28px", boxShadow: "none", border: "1px solid #E4E7E0", fontFamily: "var(--font-sans)" }}>
        {rows.map(([k, v], i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", padding: "16px 0", borderBottom: i < rows.length - 1 ? "1px solid #EEF0EB" : "none" }}>
            <div style={{ width: 240, fontWeight: 600, fontSize: 12, color: "#7D8478", textTransform: "uppercase", letterSpacing: 0.4 }}>{k}</div>
            <div style={{ flex: 1, fontSize: 15, color: "#283226", fontWeight: 600 }}>{v}</div>
          </div>
        ))}
      </div>
      {dc?.lastUpdate && (
        <p style={{ marginTop: 12, fontSize: 12, color: "#7D8478", fontStyle: "italic", fontFamily: "var(--font-sans)" }}>
          Datos Datadis actualizados el {new Date(dc.lastUpdate).toLocaleString("es-ES")}
        </p>
      )}
      {!dc && !loadingContract && !isBorrador && (
        <p style={{ marginTop: 12, fontSize: 12, color: "#7A5A12", fontFamily: "var(--font-sans)" }}>
          Aún no se han descargado los datos del contrato desde Datadis. La descarga se ejecuta cada día a las 05:00 UTC.
        </p>
      )}
    </div>
  );
}

// ─── BRANDING: logo oficial easyrural (PNG) ────────
function EasyruralLogo({ size = "md", showSubtitle = true, dark = false }) {
  const height = size === "lg" ? 56 : size === "md" ? 42 : 32;
  const subColor = dark ? "#E6C3B3" : "#6F8F4E";
  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
      <img
        src="/easyrural-logo.png"
        alt="easyrural"
        style={{ height, width: "auto", display: "block", filter: dark ? "brightness(1.15)" : "none" }}
      />
      {showSubtitle && (
        <div style={{ color: subColor, fontSize: Math.max(9, Math.round(height * 0.22)), fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", fontFamily: "var(--font-sans)" }}>
          Energía
        </div>
      )}
    </div>
  );
}

// Footer estilo SaaS easyrural.net (bg-dark #283226, texto blanco)
function Footer() {
  const t = useT();
  return (
    <footer style={{ background: "#283226", color: "#fff", padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, fontSize: 12, fontFamily: "var(--font-sans)" }}>
      <div style={{ fontWeight: 500 }}>{t("footer.copyright")}</div>
      <div style={{ display: "flex", gap: 20 }}>
        <a href="mailto:info@easyrural.net" style={{ color: "#fff", textDecoration: "none", fontWeight: 600 }}>{t("footer.support")}</a>
        <a href="https://easyrural.net" target="_blank" rel="noreferrer" style={{ color: "#fff", textDecoration: "none", fontWeight: 600 }}>easyrural.net</a>
      </div>
    </footer>
  );
}

// Selector de idiomas — dropdown estilo easyrural.net (Es/Ca/En/Fr capitalize)
function LangSelector() {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    setTimeout(() => document.addEventListener("click", close, { once: true }), 0);
    return () => document.removeEventListener("click", close);
  }, [open]);
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
          background: "transparent", border: "none", cursor: "pointer",
          fontFamily: "var(--font-sans)", fontSize: 17, fontWeight: 700, color: "#283226",
        }}>
        <span>{cap(lang)}</span>
        <span style={{ fontSize: 10, color: "#283226" }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 4px)",
          background: "#fff", boxShadow: "none",
          minWidth: 96, padding: "6px 0", zIndex: 100, borderRadius: 4,
        }}>
          {SUPPORTED_LANGS.map((l) => (
            <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 18px", border: "none", cursor: "pointer",
                background: lang === l.code ? "#EBF2E4" : "transparent",
                color: lang === l.code ? "#406020" : "#283226",
                fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 600,
              }}
              onMouseEnter={(e) => { if (lang !== l.code) e.currentTarget.style.background = "#F3F5EE"; }}
              onMouseLeave={(e) => { if (lang !== l.code) e.currentTarget.style.background = "transparent"; }}>
              {cap(l.code)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
// ─── LOGIN (obsoleto: el login real vive en LandingPage → LoginPanel) ─────────────
function LoginPage({ onLogin }) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState(null);

  const submit = (e) => {
    e.preventDefault();
    const userFound = findUser(email, pwd);
    if (userFound) {
      const { password, ...safeUser } = userFound;
      localStorage.setItem("easyrural_user", JSON.stringify(safeUser));
      onLogin();
    } else {
      setErr("Email o contraseña incorrectos");
    }
  };

  return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#283226", fontFamily: "var(--font-sans)" }}>
      <link href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700;800&family=Oooh+Baby&display=swap" rel="stylesheet" />
      <div style={{ background: "#fff", padding: 40, borderRadius: 16, width: 380, boxShadow: "none" }}>
        <div style={{ position: "absolute", top: 24, right: 24 }}>
          <LangSelector />
        </div>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <EasyruralLogo size="lg" dark={false} />
        </div>
        <form onSubmit={submit}>
          {/* NUEVO CAMPO DE EMAIL */}
          <label style={{ fontSize: 12, fontWeight: 600, color: "#4B5347", display: "block", marginBottom: 6 }}>Email del Alojamiento</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
            style={{ width: "100%", padding: "12px 14px", border: "1px solid #E4E7E0", borderRadius: 10, fontSize: 14, marginBottom: 16, boxSizing: "border-box" }} />
          
          {/* CAMPO DE CONTRASEÑA */}
          <label style={{ fontSize: 12, fontWeight: 600, color: "#4B5347", display: "block", marginBottom: 6 }}>{t("login.passwordLabel")}</label>
          <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required
            style={{ width: "100%", padding: "12px 14px", border: "1px solid #E4E7E0", borderRadius: 10, fontSize: 14, marginBottom: 12, boxSizing: "border-box" }} />
          
          {err && <div style={{ color: "#B4532F", fontSize: 12, marginBottom: 12 }}>{err}</div>}
          <button type="submit" style={{ width: "100%", padding: "12px", background: "#406020", color: "#fff", border: "1px solid #406020", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)" }}>{t("login.submit")}</button>
        </form>
      </div>
    </div>
  );
}

// ─── NUEVO CLIENTE / EDITAR ─────────────────────────────────
// editingId != null → modo edición: carga datos y hace PUT.
function NewClientPage({ onCreated, editingId, onDone }) {
  const t = useT();
  const isEdit = !!editingId;
  const [draft, setDraft] = useState(false);
  const [form, setForm] = useState({
    locationID: "", nombre: "", plan_contratado: "basico", cups: "",
    codigo_distribuidora: "2", id_municipio: "", municipio: "",
    latitud: "", longitud: "", direccion: "", cp: "", superficie_m2: "",
    telegram_chat_id: "",
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(null);
  const [err, setErr] = useState(null);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Prefill en modo edición
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const c = await api.getClient(editingId);
        if (cancelled) return;
        const plan = (c.plan_contratado || c.plan || "").toLowerCase();
        const isDraftPlan = !plan || plan === "borrador";
        setDraft(isDraftPlan);
        setForm({
          locationID: c.locationID || editingId,
          nombre: c.nombre || "",
          plan_contratado: isDraftPlan ? "basico" : plan,
          cups: c.datadis?.cups || c.cups || "",
          codigo_distribuidora: c.datadis?.codigo_distribuidora || c.distribuidora || "2",
          id_municipio: c.ubicacion?.id_municipio || c.aemet?.municipio_id || "",
          municipio: c.ubicacion?.municipio || "",
          latitud: c.ubicacion?.latitud ?? "",
          longitud: c.ubicacion?.longitud ?? "",
          direccion: c.ubicacion?.direccion || "",
          cp: c.ubicacion?.cp || "",
          superficie_m2: c.ubicacion?.superficie_m2 ?? "",
          telegram_chat_id: c.telegram_chat_id ?? "",
        });
      } catch (e) {
        if (!cancelled) setErr("No se pudo cargar el alojamiento: " + e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, editingId]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setOk(null); setErr(null);
    try {
      // En modo borrador solo enviamos identificación + ubicación (para que aparezca en el mapa)
      const body = draft
        ? {
            locationID: form.locationID,
            nombre: form.nombre,
            plan_contratado: "borrador",
            estado: "borrador",
            ubicacion: {
              id_municipio: form.id_municipio || null,
              municipio: form.municipio || null,
              latitud: parseFloat(form.latitud) || null,
              longitud: parseFloat(form.longitud) || null,
              direccion: form.direccion || null,
              cp: form.cp || null,
              superficie_m2: parseInt(form.superficie_m2) || null,
            },
          }
        : {
            locationID: form.locationID,
            nombre: form.nombre,
            plan_contratado: form.plan_contratado,
            ubicacion: {
              id_municipio: form.id_municipio,
              municipio: form.municipio,
              latitud: parseFloat(form.latitud) || null,
              longitud: parseFloat(form.longitud) || null,
              direccion: form.direccion,
              cp: form.cp,
              superficie_m2: parseInt(form.superficie_m2) || null,
            },
            datadis: { cups: form.cups, codigo_distribuidora: form.codigo_distribuidora },
            telegram_chat_id: parseInt(form.telegram_chat_id) || null,
          };
      if (isEdit) {
        await api.updateClient(editingId, body);
        setOk(draft
          ? `Alojamiento "${form.locationID}" guardado como borrador.`
          : `Alojamiento "${form.locationID}" actualizado correctamente.`);
      } else {
        await api.createClient(body);
        setOk(draft
          ? `Alojamiento "${form.locationID}" registrado como BORRADOR. Aparecerá en el mapa hasta que se contrate un plan.`
          : `Cliente "${form.locationID}" creado correctamente`);
      }
      onCreated && onCreated();
      if (isEdit && onDone) setTimeout(onDone, 1200);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: "100%", padding: "11px 14px", border: "1px solid #E4E7E0", borderRadius: 10, fontSize: 14, boxSizing: "border-box", outline: "none", fontFamily: "var(--font-sans)", background: "#FAFAFA", transition: "all 0.18s ease" };
  const field = (label, k, opts = {}) => (
    <div style={{ flex: opts.half ? "1 1 calc(50% - 8px)" : "1 1 100%", marginBottom: 16 }}>
      <label style={{ fontSize: 12, fontWeight: 700, color: "#4B5347", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4, fontFamily: "var(--font-sans)" }}>
        {label}{opts.required && <span style={{ color: "#B4532F" }}> *</span>}
      </label>
      <input type={opts.type || "text"} value={form[k]} required={opts.required}
        onChange={(e) => update(k, e.target.value)}
        onFocus={(e) => { e.currentTarget.style.borderColor = "#406020"; e.currentTarget.style.background = "#fff"; }}
        onBlur={(e) => { e.currentTarget.style.borderColor = "#E4E7E0"; e.currentTarget.style.background = "#FAFAFA"; }}
        style={inputStyle} />
    </div>
  );

  if (loading) {
    return <div style={{ padding: 40 }}><LoadingBox height={200} label={t("common.loading") || "Cargando…"} /></div>;
  }

  return (
    <div style={{ padding: 32, maxWidth: 920 }}>
      <h2 style={S.title}>{isEdit ? `Editar ${form.nombre || form.locationID}` : t("newClient.title")}</h2>
      <p style={S.sub}>{isEdit ? "Completa o actualiza los datos del alojamiento." : t("newClient.subtitle")}</p>
      <div style={{ background: "#fff", borderRadius: 16, padding: 32, boxShadow: "none", border: "1px solid #E4E7E0", fontFamily: "var(--font-sans)" }}>
        {/* Toggle Borrador — permite registrar un alojamiento sin plan/CUPS */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: draft ? "#EEF0EB" : "#F3F5EE", borderRadius: 12, border: `1px dashed ${draft ? "#A9AEA2" : "#D2DFC3"}`, marginBottom: 24 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flex: 1 }}>
            <input type="checkbox" checked={draft} onChange={(e) => setDraft(e.target.checked)}
              style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#406020" }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#283226" }}>{t("newClient.draftToggle")}</div>
              <div style={{ fontSize: 12, color: "#7D8478", marginTop: 2 }}>{t("newClient.draftHelp")}</div>
            </div>
          </label>
        </div>
        <form onSubmit={submit}>
          <h4 style={S.rowTitle}>IDENTIFICACIÓN</h4>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {field("locationID (ID único)", "locationID", { required: true, half: true })}
            {field("Nombre", "nombre", { required: true, half: true })}
            {!draft && (
              <div style={{ flex: "1 1 calc(50% - 8px)", marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#4B5347", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4, fontFamily: "var(--font-sans)" }}>Plan <span style={{ color: "#B4532F" }}>*</span></label>
                <select value={form.plan_contratado} onChange={(e) => update("plan_contratado", e.target.value)}
                  style={inputStyle}>
                  <option value="basico">Básico (solo APIs)</option>
                  <option value="completo">Completo (APIs + Sensores)</option>
                </select>
              </div>
            )}
            {!draft && field("Telegram Chat ID", "telegram_chat_id", { half: true })}
          </div>
          <h4 style={{ ...S.rowTitle, marginTop: 20 }}>UBICACIÓN</h4>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {field("Dirección", "direccion")}
            {field("Municipio", "municipio", { half: true })}
            {field("CP", "cp", { half: true })}
            {field("ID municipio INE (5 dígitos)", "id_municipio", { half: true })}
            {field("Superficie (m²)", "superficie_m2", { type: "number", half: true })}
            {field("Latitud", "latitud", { half: true })}
            {field("Longitud", "longitud", { half: true })}
          </div>
          {!draft && (
            <>
              <h4 style={{ ...S.rowTitle, marginTop: 20 }}>DATADIS / CONTRATO</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                {field("CUPS", "cups", { required: true })}
                {field("Código distribuidora", "codigo_distribuidora", { required: true, half: true })}
              </div>
            </>
          )}
          {err && <div style={{ background: "#FEE", color: "#B4532F", padding: 12, borderRadius: 12, marginBottom: 12, fontSize: 13 }}>{err}</div>}
          {ok && <div style={{ background: "#EBF2E4", color: "#406020", padding: 12, borderRadius: 12, marginBottom: 12, fontSize: 13 }}>{ok}</div>}
          <button type="submit" disabled={saving}
            style={{ background: saving ? "#B9CBA3" : (draft ? "#7D8478" : "#406020"), color: "#fff", border: `1px solid ${draft ? "#7D8478" : "#406020"}`, borderRadius: 10, padding: "10px 28px", fontSize: 14, fontWeight: 600, cursor: saving ? "default" : "pointer", fontFamily: "var(--font-sans)" }}>
            {saving
              ? t("newClient.saving")
              : isEdit
                ? (draft ? t("edit.saveDraft") : t("edit.saveChanges"))
                : draft
                  ? t("newClient.saveDraft")
                  : t("newClient.create")}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── ALARMAS ───────────────────────────────────────────────
function AlarmsPage({ acc }) {
  const t = useT();
  const [historyRange, setHistoryRange] = useState("90d");
  const historyDays = (RANGE_OPTIONS.find(o => o.key === historyRange) || RANGE_OPTIONS[1]).days;
  const { data: alarms, loading } = useApi(() => api.getAlarms(acc.locationID, historyDays), [acc.locationID, historyDays], { pollMs: 60000 });
  // Alertas inteligentes en vivo (backlog #5): consumo anómalo, precio, meteorología
  const [smartAlerts, setSmartAlerts] = useState(null);
  useEffect(() => {
    let cancel = false;
    const fetch = () => api.getAlerts(acc.locationID).then(r => { if (!cancel) setSmartAlerts(r); }).catch(() => {});
    fetch();
    const iv = setInterval(fetch, 300000); // refrescar cada 5 min
    return () => { cancel = true; clearInterval(iv); };
  }, [acc.locationID]);
  const sevColor = { critical: "#B4532F", high: "#B4532F", medium: "#C08B2C", low: "#3D6E8E", info: "#6F8F4E", alta: "#B4532F", media: "#C08B2C", baja: "#3D6E8E" };
  const list = Array.isArray(alarms) ? alarms : (alarms?.series || []);
  const smart = smartAlerts?.alerts || [];
  const iconoTipo = { consumo: Zap, potencia: Plug, economica: Euro, predicción: TrendingUp, meteorologica: CloudSun, agua: Droplets, mantenimiento: Wrench };
  return (
    <div style={{ padding: 32 }}>
      <h2 style={S.title}>{t("alarms.title")}</h2>
      <p style={S.sub}>{t("alarms.subtitle", { name: acc.nombre || acc.locationID })}</p>

      {/* ═══ ALERTAS INTELIGENTES EN VIVO (backlog #5) ═══ */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#283226", margin: 0, fontFamily: "var(--font-sans)" }}>
            Alertas activas · <span style={{ color: "#7D8478", fontWeight: 500 }}>análisis automático</span>
          </h3>
          {smartAlerts && (
            <div style={{ fontSize: 11, color: "#7D8478", fontFamily: "var(--font-sans)" }}>
              {smartAlerts.count} detectada{smartAlerts.count !== 1 ? "s" : ""} · {smartAlerts.detectors_run} detectores activos
            </div>
          )}
        </div>
        {smart.length === 0 ? (
          <div style={{ background: "#EBF2E4", border: "1px solid #D2DFC3", borderRadius: 12, padding: 20, textAlign: "center", fontFamily: "var(--font-sans)" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><IconTile icon={CheckCircle2} /></div>
            <div style={{ fontSize: 14, color: "#406020", fontWeight: 600 }}>Todo bajo control</div>
            <div style={{ fontSize: 12, color: "#4B5347", marginTop: 4 }}>Ningún patrón anómalo detectado en tu consumo, precios ni meteorología.</div>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {smart.map((a, i) => {
              const sev = String(a.severidad || "media").toLowerCase();
              const color = sevColor[sev] || "#7D8478";
              const bg = "#fff";
              const border = "#E4E7E0";
              return (
                <div key={i} style={{ background: bg, border: `1px solid ${border}`, borderLeft: `3px solid ${color}`, borderRadius: 12, padding: "16px 20px", fontFamily: "var(--font-sans)" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        {(() => { const C = iconoTipo[a.tipo] || AlertTriangle; return <C size={17} strokeWidth={1.7} color={color} />; })()}
                        <span style={{ fontSize: 15, fontWeight: 600, color: "#283226" }}>{a.titulo}</span>
                        <span style={{ padding: "2px 7px", borderRadius: 3, fontFamily: "var(--font-mono)", fontSize: 9.5, border: `1px solid ${color}`, color, textTransform: "uppercase", letterSpacing: 0.8 }}>
                          {sev}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: "#4B5347", lineHeight: 1.5 }}>{a.mensaje}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ HISTÓRICO DE ALARMAS (original) ═══ */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 12 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: "#283226", margin: 0, fontFamily: "var(--font-sans)" }}>
          Histórico de eventos <span style={{ color: "#7D8478", fontWeight: 500 }}>· últimos {historyDays} días</span>
        </h3>
      </div>
      <RangeSelector value={historyRange} onChange={setHistoryRange} label="Ventana" />

      <div style={{ background: "#fff", borderRadius: 16, padding: 28, boxShadow: "none", border: "1px solid #E4E7E0" }}>
        {loading ? <LoadingBox /> : list.length === 0 ? (
          <div style={{ textAlign: "center", padding: 56, color: "#7D8478", fontFamily: "var(--font-sans)" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#EBF2E4", color: "#406020", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 14px" }}><CheckCircle2 size={30} strokeWidth={1.4} /></div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#283226" }}>{t("alarms.none")}</div>
            <div style={{ fontSize: 13, color: "#7D8478", marginTop: 4 }}>Sistema funcionando con normalidad</div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #E4E7E0", textAlign: "left", color: "#7D8478", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
                <th style={{ padding: "14px 12px" }}>{t("alarms.col.date")}</th>
                <th style={{ padding: "14px 12px" }}>{t("alarms.col.severity")}</th>
                <th style={{ padding: "14px 12px" }}>{t("alarms.col.type")}</th>
                <th style={{ padding: "14px 12px" }}>{t("alarms.col.detail")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((a, i) => {
                const t = new Date(a._time || a.time);
                const sev = String(a.severity || a.gravedad || "info").toLowerCase();
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #EEF0EB", background: i % 2 === 0 ? "transparent" : "#FAFAFA" }}>
                    <td style={{ padding: "14px 12px", color: "#4B5347", whiteSpace: "nowrap", fontSize: 13 }}>{t.toLocaleString("es-ES")}</td>
                    <td style={{ padding: "14px 12px" }}>
                      <span style={{ display: "inline-block", padding: "4px 12px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: (sevColor[sev] || "#7D8478") + "22", color: sevColor[sev] || "#7D8478", textTransform: "uppercase", letterSpacing: 0.5 }}>{sev}</span>
                    </td>
                    <td style={{ padding: "14px 12px", color: "#283226", fontWeight: 600, fontSize: 14 }}>{a.type || a.tipo || a.alarma || "—"}</td>
                    <td style={{ padding: "14px 12px", color: "#4B5347", fontSize: 14 }}>{a.message || a.detalle || a.descripcion || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── ACTUADORES ────────────────────────────────────────────
function ActuatorsPage({ acc }) {
  const t = useT();
  const { data: full } = useApi(() => api.getClient(acc.locationID), [acc.locationID]);
  const actuadores = (full && full.actuadores) || {};
  const list = Object.entries(actuadores);
  const [busy, setBusy] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const fire = async (actuador, orden) => {
    setBusy(actuador + "_" + orden);
    setFeedback(null);
    try {
      await api.postActuator(acc.locationID, actuador, orden);
      setFeedback({ type: "ok", text: `Orden enviada: ${actuador} → ${orden}. Se aplica en unos segundos.` });
    } catch (e) {
      setFeedback({ type: "err", text: e.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ padding: 32 }}>
      <h2 style={S.title}>{t("actuators.title")}</h2>
      <p style={S.sub}>{t("actuators.subtitle", { name: acc.nombre || acc.locationID })}</p>
      {feedback && (
        <div style={{ background: feedback.type === "ok" ? "#EBF2E4" : "#FEE", color: feedback.type === "ok" ? "#406020" : "#B4532F", padding: "12px 18px", borderRadius: 10, marginBottom: 16, fontSize: 13, fontFamily: "var(--font-sans)", fontWeight: 600, display: "inline-block", border: `1px solid ${feedback.type === "ok" ? "#D2DFC3" : "#F3E1D8"}` }}>{feedback.text}</div>
      )}
      {list.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 16, padding: 56, textAlign: "center", color: "#7D8478", boxShadow: "none", border: "1px solid #E4E7E0", fontFamily: "var(--font-sans)", fontSize: 15 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><IconTile icon={Wrench} tone="#A9AEA2" bg="#EEF0EB" size={56} /></div>
          {t("actuators.none")}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
          {list.map(([nombre, device]) => (
            <div key={nombre} style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "none", border: "1px solid #E4E7E0", fontFamily: "var(--font-sans)", transition: "all 0.2s ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(46,81,57,0.08)"; e.currentTarget.style.borderColor = "#D2DFC3"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; e.currentTarget.style.borderColor = "#E4E7E0"; }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 4 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: "#EBF2E4", color: "#406020", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: 22, height: 22 }}>{I.tool}</div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: "#283226", fontSize: 16 }}>{nombre.replace(/_/g, " ")}</div>
                  <div style={{ fontSize: 11, color: "#7D8478", fontFamily: "monospace", marginTop: 2 }}>{device}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button onClick={() => fire(nombre, "ON")} disabled={busy === nombre + "_ON"}
                  style={{ flex: 1, padding: "10px", background: busy === nombre + "_ON" ? "#B9CBA3" : "#406020", color: "#fff", border: "1px solid #406020", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)" }}>
                  {busy === nombre + "_ON" ? "..." : t("actuators.activate")}
                </button>
                <button onClick={() => fire(nombre, "OFF")} disabled={busy === nombre + "_OFF"}
                  style={{ flex: 1, padding: "10px", background: busy === nombre + "_OFF" ? "#E6C3B3" : "#B4532F", color: "#fff", border: "1px solid #B4532F", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)" }}>
                  {busy === nombre + "_OFF" ? "..." : t("actuators.deactivate")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── USUARIOS Y ACCESOS (admin) ────────────────────────────
function UsersAccessPage({ clients }) {
  const t = useT();
  const clientMap = Object.fromEntries((clients || []).map(c => [c.locationID, c]));
  const roleMeta = {
    admin:   { label: t("users.role.admin")   || "Administrador", color: "#8E3C20", bg: "#F3E1D8", icon: "" },
    cliente: { label: t("users.role.cliente") || "Cliente",       color: "#406020", bg: "#EBF2E4", icon: "●" },
  };

  // Preferimos la lista real del backend authDB. Si /api/users falla
  // (backend caído / sin admin token), caemos a los DEMO_USERS hardcoded
  // como último recurso para no dejar la pantalla vacía.
  const { data: backendUsers, loading: loadingUsers, error: usersError, refetch: refetchUsers } = useApi(api.listUsers, [], { pollMs: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // email o null
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);           // { kind: "ok"|"err", text }

  // Deduplicar por email — el backend gana sobre el demo.
  const usersList = useMemo(() => {
    const byEmail = new Map();
    (DEMO_USERS || []).forEach(u => byEmail.set(u.email.toLowerCase(), { ...u, source: "demo" }));
    (backendUsers || []).forEach(u => {
      if (!u?.email) return;
      byEmail.set(u.email.toLowerCase(), {
        ...(byEmail.get(u.email.toLowerCase()) || {}),
        ...u,
        password: undefined, // el backend no la devuelve, no la exponemos
        source: "backend",
      });
    });
    return Array.from(byEmail.values());
  }, [backendUsers]);

  const stats = {
    total: usersList.length,
    admin: usersList.filter(u => u.role === "admin").length,
    multi: usersList.filter(u => Array.isArray(u.locationIDs) && u.locationIDs.length > 1 && !u.locationIDs.includes("*")).length,
    single: usersList.filter(u => Array.isArray(u.locationIDs) && u.locationIDs.length === 1).length,
  };

  const handleDelete = async (email) => {
    setBusy(true);
    setFeedback(null);
    try {
      await api.deleteUser(email);
      setFeedback({ kind: "ok", text: `Usuario ${email} eliminado.` });
      refetchUsers();
    } catch (e) {
      setFeedback({ kind: "err", text: `No se pudo borrar ${email}: ${e.message}` });
    } finally {
      setBusy(false);
      setConfirmDelete(null);
    }
  };

  const Kpi = ({ label, value, color = "#406020", sub }) => (
    <div style={{ background: "#fff", borderRadius: 16, padding: 20, border: "1px solid #E4E7E0", flex: "1 1 180px", minWidth: 160 }}>
      <div style={{ fontSize: 11, color: "#7D8478", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color, fontFamily: "var(--font-sans)", lineHeight: 1.05 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#7D8478", marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding: 32, maxWidth: 1400 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={S.title}>{t("users.title") || "Usuarios y accesos"}</h2>
          <p style={S.sub}>Cuentas registradas en el backend + los locationIDs a los que pueden acceder.</p>
        </div>
        <button onClick={() => setModalOpen(true)}
          style={{ background: "#406020", color: "#fff", border: "1px solid #406020", borderRadius: 10, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-sans)" }}>
          + Alta usuario
        </button>
      </div>

      {feedback && (
        <div className="er-fade-in" style={{
          background: feedback.kind === "ok" ? "#EBF2E4" : "#F3E1D8",
          color:      feedback.kind === "ok" ? "#406020" : "#8E3C20",
          border: `1px solid ${feedback.kind === "ok" ? "#D2DFC3" : "#E6C3B3"}`,
          borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13,
          fontFamily: "var(--font-sans)",
        }}>{feedback.text}</div>
      )}

      {loadingUsers && !backendUsers && (
        <div style={{ marginBottom: 16 }}><LoadingBox height={80} label="Cargando usuarios del backend…" /></div>
      )}
      {usersError && (
        <div style={{ background: "#F5EBD3", border: "1px solid #E8D7AE", borderRadius: 10, padding: "10px 14px", marginBottom: 16, color: "#7A5A12", fontSize: 13 }}>
          El backend no respondió. Se muestran las cuentas hardcoded del frontend (fallback dev). Detalle: {String(usersError)}
        </div>
      )}

      <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        <Kpi label={t("users.kpi.total") || "Usuarios"} value={stats.total} sub={t("users.kpi.totalSub") || "en la plataforma"} />
        <Kpi label={t("users.kpi.admin") || "Administradores"} value={stats.admin} sub={t("users.kpi.adminSub") || "Ven todos los alojamientos"} color="#8E3C20" />
        <Kpi label={t("users.kpi.multi") || "Multi-alojamiento"} value={stats.multi} sub={t("users.kpi.multiSub") || "Dueños de ≥2 casas"} color="#3D6E8E" />
        <Kpi label={t("users.kpi.single") || "Individuales"} value={stats.single} sub={t("users.kpi.singleSub") || "Una única casa"} color="#6F8F4E" />
      </div>

      <div style={{ background: "#fff", borderRadius: 16, padding: 20, border: "1px solid #E4E7E0", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-sans)", fontSize: 14, minWidth: 780 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #E4E7E0", textAlign: "left", color: "#7D8478", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
              <th style={{ padding: "14px 12px" }}>{t("users.col.name")     || "Nombre"}</th>
              <th style={{ padding: "14px 12px" }}>{t("users.col.email")    || "Email"}</th>
              <th style={{ padding: "14px 12px" }}>{t("users.col.role")     || "Rol"}</th>
              <th style={{ padding: "14px 12px" }}>{t("users.col.access")   || "Alojamientos con acceso"}</th>
              <th style={{ padding: "14px 12px" }}>Origen</th>
              <th style={{ padding: "14px 12px", width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {usersList.map((u, i) => {
              const meta = roleMeta[u.role] || roleMeta.cliente;
              const ids = Array.isArray(u.locationIDs) ? u.locationIDs : [u.locationID].filter(Boolean);
              const isAll = ids.includes("*");
              return (
                <tr key={u.email} style={{ borderBottom: "1px solid #EEF0EB", background: i % 2 === 0 ? "transparent" : "#FAFAFA" }}>
                  <td style={{ padding: "14px 12px", fontWeight: 700, color: "#283226" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: "50%", background: meta.bg, color: meta.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>
                        {(u.nombre || u.email).charAt(0).toUpperCase()}
                      </div>
                      {u.nombre || "—"}
                    </div>
                  </td>
                  <td style={{ padding: "14px 12px", color: "#4B5347", fontFamily: "monospace", fontSize: 13 }}>{u.email}</td>
                  <td style={{ padding: "14px 12px" }}>
                    <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: meta.bg, color: meta.color, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {meta.icon} {isAll ? (t("users.role.adminFull") || "Admin global") : ids.length > 1 ? (t("users.role.multi") || `Multi (${ids.length})`) : meta.label}
                    </span>
                  </td>
                  <td style={{ padding: "14px 12px", color: "#283226" }}>
                    {isAll ? (
                      <span style={{ fontWeight: 700, color: "#8E3C20" }}>{t("users.access.all") || "Todos (incluye borradores)"}</span>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {ids.map(id => {
                          const c = clientMap[id];
                          const label = c?.nombre || id;
                          const plan = (c?.plan_contratado || c?.plan || "").toLowerCase();
                          const isDraft = !plan || plan === "borrador" || c?.estado === "borrador";
                          return (
                            <span key={id}
                              style={{
                                background: isDraft ? "#EEF0EB" : "#EBF2E4",
                                color: isDraft ? "#7D8478" : "#406020",
                                border: isDraft ? "1px dashed #A9AEA2" : "1px solid #D2DFC3",
                                padding: "3px 10px", borderRadius: 10, fontSize: 12, fontWeight: 600,
                              }}
                              title={id}>
                              {label}{isDraft ? " · borrador" : ""}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "14px 12px" }}>
                    {u.source === "backend" ? (
                      <span style={{ background: "#EBF2E4", color: "#406020", border: "1px solid #D2DFC3", padding: "3px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>backend</span>
                    ) : (
                      <span style={{ background: "#F5EBD3", color: "#7A5A12", border: "1px solid #E8D7AE", padding: "3px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>hardcode</span>
                    )}
                  </td>
                  <td style={{ padding: "14px 12px", textAlign: "right" }}>
                    {u.source === "backend" && u.role !== "admin" && (
                      <button
                        onClick={() => setConfirmDelete(u.email)}
                        disabled={busy}
                        title={`Eliminar ${u.email}`}
                        style={{ background: "transparent", border: "1px solid #E6C3B3", color: "#8E3C20", borderRadius: 12, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: busy ? "default" : "pointer", fontFamily: "var(--font-sans)" }}>
                        Eliminar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 20, padding: 16, background: "#F3F5EE", borderRadius: 12, border: "1px dashed #D2DFC3", fontSize: 13, color: "#4B5347", fontFamily: "var(--font-sans)" }}>
        Los usuarios marcados como <b>backend</b> viven en <code>authDB</code> del Node-RED cloud con hash <code>scrypt</code>. Los <b>hardcode</b> están declarados en <code>src/demoUsers.js</code> y sólo aparecen si el backend no responde (fallback dev). Estos últimos se retirarán cuando todos estén creados en backend.
      </div>

      {/* Modal alta usuario */}
      {modalOpen && (
        <NewUserModal
          onClose={() => setModalOpen(false)}
          clients={clients}
          onCreated={(msg) => { setFeedback({ kind: "ok", text: msg }); refetchUsers(); setModalOpen(false); }}
        />
      )}

      {/* Modal confirmación borrado */}
      {confirmDelete && (
        <div role="dialog" aria-modal="true"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, animation: "er-fade-in .2s ease both" }}
          onClick={() => setConfirmDelete(null)}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 16, padding: 24, maxWidth: 420, width: "92vw", fontFamily: "var(--font-sans)", boxShadow: "none" }}>
            <h3 style={{ margin: 0, fontSize: 18, color: "#283226" }}>Eliminar usuario</h3>
            <p style={{ marginTop: 10, marginBottom: 20, color: "#4B5347", fontSize: 14 }}>
              ¿Seguro que quieres eliminar <b>{confirmDelete}</b>? Esta acción no se puede deshacer.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setConfirmDelete(null)} disabled={busy}
                style={{ background: "transparent", border: "1px solid #E4E7E0", color: "#4B5347", borderRadius: 10, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)" }}>
                Cancelar
              </button>
              <button onClick={() => handleDelete(confirmDelete)} disabled={busy}
                style={{ background: "#8E3C20", border: "1px solid #8E3C20", color: "#fff", borderRadius: 10, padding: "8px 18px", fontSize: 13, fontWeight: 700, cursor: busy ? "default" : "pointer", fontFamily: "var(--font-sans)" }}>
                {busy ? "Eliminando…" : "Sí, eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Modal para crear un usuario desde el panel admin ─────────
function NewUserModal({ onClose, onCreated, clients }) {
  const [form, setForm] = useState({
    email: "", nombre: "", nif: "", password: "",
    role: "cliente", locationIDs: [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleLoc = (id) => setForm(f => ({
    ...f,
    locationIDs: f.locationIDs.includes(id) ? f.locationIDs.filter(x => x !== id) : [...f.locationIDs, id],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { setErr("Email y contraseña son obligatorios."); return; }
    if (form.role === "cliente" && form.locationIDs.length === 0) { setErr("Un cliente necesita al menos 1 alojamiento accesible."); return; }
    setBusy(true); setErr(null);
    try {
      const body = form.role === "admin"
        ? { ...form, locationIDs: ["*"] }
        : { ...form };
      await api.createUser(body);
      onCreated(`Usuario ${form.email} creado.`);
    } catch (e) {
      setErr(e.message || "Error creando usuario");
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #E4E7E0", borderRadius: 10, fontSize: 14, boxSizing: "border-box", outline: "none", fontFamily: "var(--font-sans)", background: "#FAFAFA" };

  return (
    <div role="dialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, animation: "er-fade-in .2s ease both" }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 16, padding: 24, maxWidth: 560, width: "94vw", maxHeight: "88vh", overflowY: "auto", fontFamily: "var(--font-sans)", boxShadow: "none" }}>
        <h3 style={{ margin: 0, fontSize: 20, color: "#283226" }}>Alta de usuario</h3>
        <p style={{ marginTop: 6, marginBottom: 16, color: "#7D8478", fontSize: 13 }}>Este usuario se guarda en <code>authDB</code> del backend con hash <code>scrypt</code>.</p>
        <form onSubmit={submit}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#4B5347", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>Email *</label>
          <input type="email" value={form.email} onChange={e => update("email", e.target.value)} required autoFocus style={{ ...inputStyle, marginBottom: 12 }} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#4B5347", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>Nombre completo</label>
              <input value={form.nombre} onChange={e => update("nombre", e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#4B5347", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>NIF (opcional)</label>
              <input value={form.nif} onChange={e => update("nif", e.target.value)} style={inputStyle} />
            </div>
          </div>

          <label style={{ fontSize: 11, fontWeight: 700, color: "#4B5347", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>Contraseña *</label>
          <input type="text" value={form.password} onChange={e => update("password", e.target.value)} required
            placeholder="El dueño podrá cambiarla en su primer login" style={{ ...inputStyle, marginBottom: 12, fontFamily: "monospace" }} />

          <label style={{ fontSize: 11, fontWeight: 700, color: "#4B5347", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>Rol</label>
          <select value={form.role} onChange={e => update("role", e.target.value)} style={{ ...inputStyle, marginBottom: 12 }}>
            <option value="cliente">Cliente (dueño de 1 o varios alojamientos)</option>
            <option value="admin">Admin global (ve todo)</option>
          </select>

          {form.role !== "admin" && (
            <>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#4B5347", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>Alojamientos accesibles</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, padding: 10, background: "#F3F5EE", borderRadius: 10, border: "1px solid #D2DFC3" }}>
                {(clients || []).map(c => {
                  const active = form.locationIDs.includes(c.locationID);
                  return (
                    <button type="button" key={c.locationID} onClick={() => toggleLoc(c.locationID)}
                      style={{
                        background: active ? "#406020" : "#fff",
                        color: active ? "#fff" : "#283226",
                        border: `1px solid ${active ? "#406020" : "#E4E7E0"}`,
                        borderRadius: 10, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-sans)",
                      }}>
                      {c.nombre || c.locationID}
                    </button>
                  );
                })}
                {(!clients || clients.length === 0) && (
                  <span style={{ fontSize: 12, color: "#7D8478" }}>Aún no hay alojamientos. Crea primero uno desde Alta cliente.</span>
                )}
              </div>
            </>
          )}

          {err && <div style={{ background: "#F3E1D8", color: "#8E3C20", padding: 10, borderRadius: 12, marginBottom: 12, fontSize: 13, border: "1px solid #E6C3B3" }}>{err}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" onClick={onClose} disabled={busy}
              style={{ background: "transparent", border: "1px solid #E4E7E0", color: "#4B5347", borderRadius: 10, padding: "9px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)" }}>
              Cancelar
            </button>
            <button type="submit" disabled={busy}
              style={{ background: "#406020", border: "1px solid #406020", color: "#fff", borderRadius: 10, padding: "9px 20px", fontSize: 13, fontWeight: 700, cursor: busy ? "default" : "pointer", fontFamily: "var(--font-sans)" }}>
              {busy ? "Creando…" : "Crear usuario"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── EMPTY STATE ───────────────────────────────────────────
// Pantalla amigable cuando el usuario no tiene alojamientos aún.
function EmptyState({ user, onGoMap, onNewClient }) {
  const t = useT();
  const isAdmin = user?.role === "admin";
  return (
    <div style={{ padding: 48, maxWidth: 860, margin: "0 auto", fontFamily: "var(--font-sans)" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 44, boxShadow: "none", border: "1px solid #E4E7E0", textAlign: "center" }}>
        <div style={{ width: 96, height: 96, borderRadius: "50%", background: "#EBF2E4", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", color: "#406020" }}>
          <svg viewBox="0 0 24 24" width="52" height="52" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        </div>
        <h2 style={{ ...S.title, marginBottom: 10 }}>
          {isAdmin
            ? t("empty.adminTitle")
            : (user?.nombre ? t("empty.clientTitle", { name: user.nombre }) : t("empty.clientTitleFallback"))}
        </h2>
        <p style={{ ...S.sub, marginBottom: 24, maxWidth: 520, marginLeft: "auto", marginRight: "auto", fontSize: 16 }}>
          {isAdmin ? t("empty.adminBody") : t("empty.clientBody")}
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
          {isAdmin && (
            <button onClick={onNewClient}
              style={{ background: "#406020", color: "#fff", border: "1px solid #406020", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-sans)" }}>
              {t("empty.firstClient")}
            </button>
          )}
          <button onClick={onGoMap}
            style={{ background: "#fff", color: "#406020", border: "1px solid #406020", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-sans)" }}>
            {t("empty.goMap")}
          </button>
        </div>
        {!isAdmin && (
          <div style={{ marginTop: 28, padding: 16, background: "#F3F5EE", borderRadius: 12, border: "1px dashed #D2DFC3", fontSize: 13, color: "#4B5347" }}>
            {t("empty.contactHint")} <a href="mailto:info@easyrural.net" style={{ color: "#406020", fontWeight: 700 }}>info@easyrural.net</a>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ADMIN OVERVIEW ────────────────────────────────────────
// Panel único para el administrador: KPIs globales + una card por
// alojamiento con métricas clave y acceso rápido a su dashboard.
function AdminOverviewCard({ acc, onOpen, onEdit }) {
  const t = useT();
  const id = acc.locationID;
  const plan = (acc.plan_contratado || acc.plan || "").toLowerCase();
  const isDraft = !plan || plan === "borrador" || plan === "draft" || acc.estado === "borrador";
  // No pedimos datos para borradores (endpoints devolverían vacío)
  const { data: cons } = useApi(() => api.getConsumptionSummary(id), [id], { pollMs: 300000, skip: isDraft });
  const { data: alarms } = useApi(() => api.getAlarms(id, 1), [id], { pollMs: 60000, skip: isDraft });
  const { data: sensors } = useApi(() => api.getSensors(id, "energia", 1), [id], { pollMs: 60000, skip: isDraft });

  const alarmList = Array.isArray(alarms) ? alarms : (alarms?.series || []);
  const criticals = alarmList.filter(a => ["critical", "high"].includes(String(a.severity || a.gravedad || "").toLowerCase())).length;
  const hasIssues = criticals > 0;

  // El summary devuelve: consumo_kwh (últimos 30d), coste_euros, ratio_eficiencia,
  // precio_medio_eur_kwh. No hay "hoy" por CUPS (Datadis solo trae histórico
  // diario). Aproximamos "hoy" como el consumo diario medio (mes/30).
  const kwhMes = cons?.consumo_kwh ?? cons?.month_kwh ?? cons?.month;
  const eurMes = cons?.coste_euros ?? cons?.month_eur ?? cons?.cost_month;
  const kwhDia = (kwhMes != null && !isNaN(kwhMes)) ? kwhMes / 30 : (cons?.today_kwh ?? cons?.today);
  const powerNow = (() => {
    const s = Array.isArray(sensors) ? sensors : (sensors?.series || []);
    if (!s.length) return null;
    const last = s[s.length - 1];
    return last.power || last.power_a || null;
  })();

  const statusColor = isDraft ? "#A9AEA2" : hasIssues ? "#B4532F" : "#6F8F4E";
  const statusLabel = isDraft
    ? t("card.status.draft")
    : hasIssues
      ? t("card.status.alarm", { n: criticals, s: criticals > 1 ? "s" : "" })
      : t("card.status.ok");
  const municipio = acc.ubicacion?.municipio || acc.municipio || "";
  const planLabel = isDraft ? t("card.plan.none") : (acc.plan_contratado || acc.plan || "básico");

  const fmt = (v, unit, digits = 1) => (v == null || isNaN(v)) ? "—" : `${Number(v).toFixed(digits)} ${unit}`;

  return (
    <div onClick={() => onOpen(id)}
      style={{ background: "#fff", borderRadius: 16, padding: 20, border: "1px solid #E4E7E0", boxShadow: "none", cursor: "pointer", transition: "all 0.18s ease", position: "relative", overflow: "hidden" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(46,81,57,0.12)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; }}>
      <div style={{ position: "absolute", top: 0, left: 0, width: 4, height: "100%", background: statusColor }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#283226", fontFamily: "var(--font-sans)", lineHeight: 1.2 }}>{(acc.nombre || id).toUpperCase()}</div>
          <div style={{ fontSize: 12, color: "#7D8478", marginTop: 2 }}>{municipio || id}</div>
        </div>
        <span style={{ padding: "3px 10px", borderRadius: 10, background: statusColor + "22", color: statusColor, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>{statusLabel}</span>
      </div>

      {isDraft ? (
        <div style={{ background: "#EEF0EB", border: "1px dashed #A9AEA2", borderRadius: 10, padding: "16px", marginBottom: 12, textAlign: "center" }}>
          <div style={{ fontSize: 12, color: "#7D8478", fontStyle: "italic" }}>{t("card.draftNote")}</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <div style={{ background: "#F3F5EE", borderRadius: 10, padding: "10px 12px" }}>
            <div style={{ fontSize: 10, color: "#7D8478", textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700 }}>{t("card.today")}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#283226", fontFamily: "var(--font-sans)" }}>{fmt(kwhDia, "kWh")}</div>
          </div>
          <div style={{ background: "#F3F5EE", borderRadius: 10, padding: "10px 12px" }}>
            <div style={{ fontSize: 10, color: "#7D8478", textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700 }}>{t("card.month")}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#283226", fontFamily: "var(--font-sans)" }}>{fmt(kwhMes, "kWh", 0)}</div>
          </div>
          <div style={{ background: "#F3F5EE", borderRadius: 10, padding: "10px 12px" }}>
            <div style={{ fontSize: 10, color: "#7D8478", textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700 }}>{t("card.costMonth")}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#283226", fontFamily: "var(--font-sans)" }}>{fmt(eurMes, "€", 2)}</div>
          </div>
          <div style={{ background: "#F3F5EE", borderRadius: 10, padding: "10px 12px" }}>
            <div style={{ fontSize: 10, color: "#7D8478", textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700 }}>{t("card.powerNow")}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#283226", fontFamily: "var(--font-sans)" }}>{fmt(powerNow, "W", 0)}</div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#7D8478", gap: 8 }}>
        <span style={{ background: isDraft ? "#EEF0EB" : "#EBF2E4", color: isDraft ? "#7D8478" : "#406020", padding: "3px 10px", borderRadius: 10, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, border: isDraft ? "1px dashed #A9AEA2" : "none" }}>{planLabel}</span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {onEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(id); }}
              title="Editar alojamiento"
              style={{ background: "transparent", border: "1px solid #E4E7E0", color: "#4B5347", borderRadius: 10, padding: "4px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-sans)", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Editar
            </button>
          )}
          <span style={{ color: "#406020", fontWeight: 700, fontFamily: "var(--font-sans)" }}>{isDraft ? t("card.completeData") : t("card.openDash")}</span>
        </div>
      </div>
    </div>
  );
}

function AdminOverviewPage({ clients, onOpen, onNewClient, onEdit }) {
  const t = useT();
  const list = clients || [];
  const isBorradorPlan = (c) => {
    const p = (c.plan_contratado || c.plan || "").toLowerCase();
    return !p || p === "borrador" || p === "draft" || c.estado === "borrador";
  };
  const activos = list.filter(c => !isBorradorPlan(c));
  const borradores = list.filter(isBorradorPlan);
  const planOf = (c) => (c.plan_contratado || c.plan || "").toLowerCase();
  const completos = list.filter(c => planOf(c) === "completo").length;
  const basicos = list.filter(c => planOf(c) === "basico").length;
  const municipiosUnicos = new Set(list.map(c => c.ubicacion?.municipio || c.municipio).filter(Boolean)).size;

  const { data: geoloc } = useApi(api.getGeoloc, []);
  // Mezclamos markers reales con los demo (borradores con lat/lon)
  const demoMarkers = borradores
    .filter(c => c.ubicacion?.latitud && c.ubicacion?.longitud)
    .map(c => ({
      locationID: c.locationID, nombre: c.nombre,
      lat: c.ubicacion.latitud, lon: c.ubicacion.longitud,
      plan: "borrador", municipio: c.ubicacion.municipio,
    }));
  const geoIds = new Set((geoloc?.markers || []).map(m => m.locationID));
  const markers = [...(geoloc?.markers || []), ...demoMarkers.filter(m => !geoIds.has(m.locationID))];

  const Kpi = ({ label, value, sub, color = "#406020" }) => (
    <div style={{ background: "#fff", borderRadius: 16, padding: 20, border: "1px solid #E4E7E0", flex: "1 1 200px", minWidth: 180 }}>
      <div style={{ fontSize: 11, color: "#7D8478", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 34, fontWeight: 700, color, fontFamily: "var(--font-sans)", lineHeight: 1.05 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#7D8478", marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding: 32, maxWidth: 1400 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={S.title}>{t("admin.title")}</h2>
          <p style={S.sub}>{t("admin.subtitle")}</p>
        </div>
        <button onClick={onNewClient}
          style={{ background: "#406020", color: "#fff", border: "1px solid #406020", borderRadius: 10, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-sans)" }}>
          {t("admin.newClient")}
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        <Kpi label={t("admin.kpi.active")}   value={activos.length}    sub={t("admin.kpi.activeSub", { n: municipiosUnicos || 0, s: municipiosUnicos === 1 ? "" : "s" })} />
        <Kpi label={t("admin.kpi.drafts")}   value={borradores.length} sub={t("admin.kpi.draftsSub")} color="#7D8478" />
        <Kpi label={t("admin.kpi.complete")} value={completos}          sub={t("admin.kpi.completeSub")} color="#6F8F4E" />
        <Kpi label={t("admin.kpi.basic")}    value={basicos}            sub={t("admin.kpi.basicSub")} color="#3D6E8E" />
      </div>

      {/* Mini-mapa admin: todos los alojamientos geolocalizados */}
      {markers.length > 0 && (
        <>
          <div style={{ ...S.rowTitle, marginBottom: 16 }}>{t("admin.mapTitle")}</div>
          <div style={{ background: "#fff", borderRadius: 16, padding: 8, border: "1px solid #E4E7E0", marginBottom: 28, height: 320, overflow: "hidden" }}>
            <LeafletMap markers={markers} onOpen={onOpen} />
          </div>
        </>
      )}

      {/* Grid de alojamientos */}
      <div style={{ ...S.rowTitle, marginBottom: 16 }}>{t("admin.listTitle")} ({list.length})</div>
      {list.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 16, padding: 40, textAlign: "center", border: "1px dashed #D2DFC3", color: "#7D8478" }}>
          {t("admin.noHousings")}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
          {activos.map(acc => <AdminOverviewCard key={acc.locationID} acc={acc} onOpen={onOpen} onEdit={onEdit} />)}
          {borradores.map(acc => <AdminOverviewCard key={acc.locationID} acc={acc} onOpen={onOpen} onEdit={onEdit} />)}
        </div>
      )}
    </div>
  );
}

// ─── ROUTING POR HASH (sin dependencia externa) ──────────────
// Mantiene la URL sincronizada con la vista (#/dashboard/Alojamiento1)
// para que el botón atrás/adelante del navegador funcione y las URLs
// sean bookmarkables y compartibles.
const PAGE_TO_ROUTE = {
  map: "mapa",
  dashboard: "dashboard",
  status: "estado",
  consumption: "consumo",
  forecast: "prediccion",
  contract: "contrato",
  alarms: "alarmas",
  actuators: "actuadores",
  install: "instalacion",
  adminOverview: "admin",
  users: "usuarios",
  newClient: "nuevo",
  editClient: "editar",
};
const ROUTE_TO_PAGE = Object.fromEntries(Object.entries(PAGE_TO_ROUTE).map(([k, v]) => [v, k]));
const PAGES_WITH_ID = new Set(["dashboard", "status", "consumption", "forecast", "contract", "alarms", "actuators", "install", "editClient"]);

function parseHash() {
  const h = (window.location.hash || "").replace(/^#\/?/, "");
  if (!h) return { page: null, id: null };
  const parts = h.split("/").filter(Boolean);
  return { page: ROUTE_TO_PAGE[parts[0]] || null, id: parts[1] ? decodeURIComponent(parts[1]) : null };
}

function buildHash(page, accId) {
  const route = PAGE_TO_ROUTE[page] || page;
  if (PAGES_WITH_ID.has(page) && accId) return `#/${route}/${encodeURIComponent(accId)}`;
  return `#/${route}`;
}

// ─── Animaciones globales inyectadas como <style> ──────────
// keyframes reutilizables por toda la app. Se montan una vez al arrancar.
function GlobalAnimations() {
  return (
    <style>{`
      @keyframes er-fade-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
      /* ── Móvil: el contenido usa estilos inline; se adaptan aquí con !important ── */
      .er-mobile .er-main [style*="padding: 32px"] { padding: 14px !important; }
      .er-install-row:hover { background: #FAF8F3; }
      .er-install-nav:hover { background: #F0EDE4 !important; }
      .er-mobile .er-install-tiles { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
      .er-mobile .er-install-two, .er-mobile .er-install-form, .er-mobile .er-install-kit { grid-template-columns: minmax(0,1fr) !important; }
      .er-mobile .er-install-copy { grid-template-columns: minmax(0,1fr) auto !important; }
      .er-mobile .er-install-copy > span:first-child { grid-column: 1 / -1; }
      .er-mobile .er-install-kitrow { grid-template-columns: minmax(0,1fr) auto !important; }
      .er-mobile .er-install-kitrow > input { grid-column: 1 / 2; }
      .er-mobile .er-main [style*="padding: 28px 30px"], .er-mobile .er-main [style*="padding: 28px 32px"] { padding: 16px !important; }
      .er-mobile .er-main [style*="padding: 22px"], .er-mobile .er-main [style*="padding: 20px 22px"] { padding: 14px !important; }
      .er-mobile .er-main [style*="grid-template-columns"] { grid-template-columns: 1fr !important; }
      .er-mobile .er-main .er-diag-tiles { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
      .er-mobile .er-main .er-diag-heat { grid-template-columns: 52px repeat(24, minmax(9px,1fr)) !important; }
      .er-mobile .er-main .er-diag-hist { grid-template-columns: 96px minmax(0,1fr) 36px !important; }
      .er-mobile .er-main .er-cal { grid-template-columns: repeat(7, minmax(0,1fr)) !important; }
      .er-mobile .er-main [style*="gap: 16px"] { gap: 10px !important; }
      .er-mobile .er-main h2 { font-size: 22px !important; line-height: 1.2 !important; }
      .er-mobile .er-main h3 { font-size: 19px !important; line-height: 1.25 !important; }
      .er-mobile .er-main [role="tablist"] { flex-wrap: wrap; width: 100%; }
      .er-mobile .er-main [role="tablist"] button { padding: 8px 12px !important; font-size: 13px !important; flex: 1 1 40%; justify-content: center; }
      .er-mobile .er-main [style*="min-width: 260px"] { min-width: 0 !important; font-size: 14px !important; }
      .er-mobile .er-main [style*="width: 240px"] { width: 120px !important; flex-shrink: 0; }
      .er-mobile .er-main table { display: block; overflow-x: auto; -webkit-overflow-scrolling: touch; }
      .er-mobile .er-main [style*="font-size: 30px"], .er-mobile .er-main [style*="font-size: 32px"] { font-size: 24px !important; }
      .er-mobile .er-main [style*="height: 420"] { height: 300px !important; }
      .er-mobile .er-main [style*="minmax(220px"], .er-mobile .er-main [style*="1.4fr 1fr 1fr 1fr"], .er-mobile .er-main [style*="repeat(4, 1fr)"], .er-mobile .er-main [style*="repeat(5, 1fr)"] { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
      .er-mobile .er-header { overflow: hidden; }
      .er-mobile .er-header > div:nth-child(2) { min-width: 0; flex: 1 1 auto !important; justify-content: flex-start !important; }
      .er-mobile .er-header > div:nth-child(2) > div { padding: 6px 10px !important; min-width: 0; max-width: 100%; }
      .er-mobile .er-header > div:nth-child(3) { gap: 6px !important; }
      .er-mobile .er-header select { font-size: 11px !important; max-width: 110px; text-overflow: ellipsis; letter-spacing: 0 !important; }
      .er-mobile .er-header button[title] { padding: 6px 10px !important; font-size: 12px !important; }
      .er-mobile .er-acc-select > svg { display: none; }
      .er-mobile .er-acc-select select { font-size: 15px !important; max-width: 150px; }
      :root {
        --font-sans: "Baloo 2", system-ui, -apple-system, "Segoe UI", sans-serif;
        --font-serif: "Baloo 2", system-ui, sans-serif;
        --font-script: "Oooh Baby", cursive;
        --font-mono: "Baloo 2", system-ui, sans-serif;
        --ink: #283226; --ink-2: #4B5347; --ink-3: #7D8478; --ink-4: #A9AEA2;
        --paper: #F7F7F7; --surface: #FFFFFF; --surface-2: #FAFAFA; --line: #E4E7E0; --line-2: #EEF0EB;
        --forest: #406020; --forest-2: #406020; --moss: #6F8F4E; --moss-tint: #EBF2E4;
        --ochre: #C08B2C; --clay: #B4532F; --sky: #3D6E8E;
        --ease: cubic-bezier(.2,.7,.2,1);
      }
      html, body, #root { background: var(--paper); }
      body { font-family: var(--font-sans); color: var(--ink); -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; text-rendering: optimizeLegibility; }
      .er-app { font-size: 15px; }
      .er-app, .er-app button, .er-app input, .er-app select, .er-app textarea { font-family: var(--font-sans); }
      .er-main h1, .er-main h2, .er-main h3 { font-family: var(--font-serif) !important; font-weight: 700 !important; letter-spacing: 0 !important; color: #406020; }
      .er-main h2 { font-size: 34px; line-height: 1.1; }
      .er-main h3 { line-height: 1.2; }
      .er-app ::selection { background: #D2DFC3; color: var(--ink); }
      .er-app :focus-visible { outline: 2px solid var(--forest-2); outline-offset: 2px; border-radius: 4px; }
      .er-main { scrollbar-width: thin; scrollbar-color: #D5D9D0 transparent; }
      .er-main ::-webkit-scrollbar { width: 10px; height: 10px; }
      .er-main ::-webkit-scrollbar-thumb { background: #D5D9D0; border-radius: 10px; border: 3px solid var(--paper); }
      .er-app [style*="font-weight: 700"] { letter-spacing: -0.005em; }

      /* Cifras: serif con números tabulares */
      .er-figure { font-family: var(--font-serif); font-weight: 700; letter-spacing: 0; line-height: 1; font-variant-numeric: tabular-nums lining-nums; }
      .er-app [style*="tabular"], .er-app td { font-variant-numeric: tabular-nums; }
      /* Antetítulo de sección */
      .er-eyebrow { font-family: var(--font-sans); font-size: 12px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; color: var(--ink-3); }

      /* Tarjetas: línea fina, sin sombra; al pasar, la línea se oscurece */
      .er-card, .er-card-lift { transition: border-color .2s ease, background-color .2s ease; }
      .er-card:hover, .er-card-lift:hover { border-color: #C9CFC2 !important; }
      .er-hoverline { position: relative; }

      /* Control segmentado (rangos, pestañas) */
      .er-seg { display: inline-flex; gap: 2px; padding: 3px; background: var(--surface); border: 1px solid var(--line); border-radius: 999px; }
      .er-seg button { position: relative; border: 0; background: transparent; color: var(--ink-2); font: 500 13px var(--font-sans); padding: 7px 16px; border-radius: 999px; cursor: pointer; transition: color .2s ease; }
      .er-seg button:hover { color: var(--ink); }
      .er-seg button.on { color: #F7F7F7; }
      .er-seg-bg { position: absolute; inset: 0; background: var(--forest-2); border-radius: 999px; }

      /* Pestañas del visor de consumo (role=tablist con botones inline) */
      .er-main [role="tablist"] > button[aria-selected="true"] { background: var(--forest-2) !important; color: #F7F7F7 !important; border-color: var(--forest-2) !important; }
      .er-main [role="tablist"] > button[aria-selected="true"] svg, .er-main [role="tablist"] > button[aria-selected="true"] span { color: #F7F7F7 !important; }

      /* Esqueletos de carga */
      @keyframes er-skel { 0% { background-position: -280px 0; } 100% { background-position: 280px 0; } }
      .er-skel { border-radius: 3px; background: linear-gradient(90deg, #EDEFEA 0%, #F5F6F3 50%, #EDEFEA 100%); background-size: 280px 100%; animation: er-skel 1.3s linear infinite; }

      /* Entradas: sutiles y rápidas */
      @keyframes er-fade-in { from { opacity: 0; } to { opacity: 1; } }
      @keyframes er-rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
      @keyframes er-progress-fill { from { width: 0; } to { width: var(--er-target-w, 100%); } }
      .er-fade-up { animation: er-rise .5s var(--ease) both; }
      .er-fade-in { animation: er-fade-in .35s ease both; }
      .er-pop-in, .er-logro, .er-tree { animation: er-rise .5s var(--ease) both; }
      .er-medal { display: inline-block; }
      .er-progress-bar { background: var(--forest-2); animation: er-progress-fill 1.1s var(--ease) both; }
      .er-main .er-fade-up:nth-child(2) { animation-delay: 40ms; } .er-main .er-fade-up:nth-child(3) { animation-delay: 80ms; }
      .er-main .er-fade-up:nth-child(4) { animation-delay: 120ms; } .er-main .er-fade-up:nth-child(5) { animation-delay: 160ms; }

      /* Cabecera */
      .er-acc-select:hover { border-color: var(--line) !important; background: var(--surface-2); }
      .er-btn-ghost { transition: border-color .2s ease, background .2s ease; }
      .er-btn-ghost:hover { border-color: #C9CFC2 !important; background: var(--surface-2) !important; }

      /* Botones como en easyrural.net: pastilla y texto en negrita */
      .er-app button { border-radius: 999px !important; }
      .er-app button[style*="background: rgb(64, 96, 32)"], .er-app button[style*="background: #406020"] { font-weight: 700 !important; }
      /* Menú lateral */
      .er-nav-item { transition: color .2s ease; }
      .er-nav-item svg { width: 20px; height: 20px; stroke-width: 1.6; }

      /* Gráficos */
      .recharts-text, .recharts-legend-item-text, .recharts-cartesian-axis-tick-value { font-family: var(--font-sans); fill: var(--ink-3); font-size: 11px; }
      .recharts-legend-item-text { color: var(--ink-2) !important; }
      .recharts-cartesian-grid line { stroke: #EDEFEA; }
      .recharts-tooltip-wrapper { font-family: var(--font-sans); }

      @media (prefers-reduced-motion: reduce) {
        .er-fade-up, .er-fade-in, .er-pop-in, .er-logro, .er-tree, .er-progress-bar, .er-skel { animation: none !important; }
      }
    `}</style>
  );
}

// ─── MAIN APP ──────────────────────────────────────────────
// Envuelto en LangProvider para que useT() funcione en toda la app
export default function EasyRuralAppRoot() {
  return (
    <LangProvider>
      <GlobalAnimations />
      <EasyRuralApp />
    </LangProvider>
  );
}

// ─── JWT helpers ───────────────────────────────────────────
// Decodifica un JWT sin verificar firma (solo lee payload).
function decodeJwt(token) {
  try {
    const [, payload] = String(token || "").split(".");
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch { return null; }
}

// Devuelve true si el token está expirado (exp < ahora, con 30 s de margen).
function isTokenExpired(token) {
  const p = decodeJwt(token);
  if (!p || typeof p.exp !== "number") return false; // sin exp → no lo consideramos expirado
  return (p.exp * 1000) < (Date.now() - 30_000);
}

// Limpia la sesión completa de localStorage y el hash de la URL.
function clearSessionAndHash() {
  ["easyrural_user", "easyrural_token", "easyrural_refresh", "easyrural_admin"].forEach(k => localStorage.removeItem(k));
  if (typeof window !== "undefined" && window.location.hash) {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

function EasyRuralApp() {
  const t = useT();
  const { lang } = useLang();

  // 1. NUEVO ESTADO: Recuperamos el usuario completo de la demo.
  //    Si el JWT está expirado, limpiamos sesión ANTES de leer el user.
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem("easyrural_token");
    if (token && isTokenExpired(token)) {
      clearSessionAndHash();
      return null;
    }
    const saved = localStorage.getItem("easyrural_user");
    return saved ? JSON.parse(saved) : null;
  });
  
  // Admin arranca en el panel global; el cliente entra a su dashboard
  // Si hay hash en la URL (#/dashboard/Alojamiento1) tiene prioridad
  const [page, setPage] = useState(() => {
    const fromHash = parseHash();
    if (fromHash.page) return fromHash.page;
    const saved = localStorage.getItem("easyrural_user");
    const u = saved ? JSON.parse(saved) : null;
    return u?.role === "admin" ? "adminOverview" : "dashboard";
  });
  const [selectedAccId, setSelectedAccId] = useState(() => parseHash().id);
  const [editingId, setEditingId] = useState(null);
  const [hover, setHover] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);

  // Etiquetas de navegación resueltas al idioma activo (filtrando adminOnly)
  const isAdmin = user?.role === "admin";
  const navFallback = { "nav.adminOverview": "Panel admin", "nav.users": "Usuarios", "nav.install": "Instalación" };
  const NAV_ACTIVE = NAV_ACTIVE_ITEMS
    .filter((it) => !it.adminOnly || isAdmin)
    .map((it) => ({ ...it, label: t(it.labelKey) === it.labelKey ? (navFallback[it.labelKey] || it.labelKey) : t(it.labelKey) }));
  const NAV_DECO = NAV_DECO_ITEMS.map((it) => ({ ...it, label: t(it.labelKey) }));

  // Cargar clientes desde la API (skip si no estás logueado)
  const { data: clients, loading: clientsLoading, error: clientsError, refetch: refetchClients } = useApi(api.getClients, [], { pollMs: 300000, skip: !user });

  // 2. LOGOUT: limpia sesión y navega a la raíz.
  //    Usamos window.location.assign("/") en vez de sólo setUser(null) porque:
  //    - garantiza que la URL queda EN "/" (sin hash residual del tipo #/consumo/AlojamientoX)
  //    - fuerza recarga completa, así se sueltan todos los pollers, useApi y refs
  //    - evita cualquier estado inconsistente al mezclar sesión vieja con landing pública
  const logout = () => {
    ["easyrural_user", "easyrural_token", "easyrural_refresh", "easyrural_admin"].forEach(k => localStorage.removeItem(k));
    setUser(null);
    setSelectedAccId(null);
    if (typeof window !== "undefined") {
      window.location.assign("/");
    }
  };

  // 3. PERMISOS: usa filterAccessible (soporta admin / multi-owner / single)
  // Además, mezclamos con los alojamientos demo (borradores) para ilustrar el flujo.
  const allowedClients = useMemo(() => {
    if (!clients) return null;
    const merged = mergeWithDemoClients(clients);
    return filterAccessible(user, merged);
  }, [clients, user]);

  // Pre-seleccionar la primera casa disponible (todos los tipos igual)
  useEffect(() => {
    if (allowedClients && allowedClients.length > 0 && !selectedAccId) {
      setSelectedAccId(allowedClients[0].locationID);
    }
  }, [allowedClients, selectedAccId]);

  // ─── ROUTING: sync page/selectedAccId → URL hash ─────────
  // Mount inicial: replaceState (no crea entrada history).
  // Cambios posteriores: pushState (crea entrada, back button funciona).
  // Nota: escribimos `window.location.hash` en vez de sólo pushState porque
  // esto SÍ dispara `hashchange` en el navegador y garantiza que la barra
  // de direcciones se refresca de forma inmediata en todos los navegadores.
  const routingMountedRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    const target = buildHash(page, selectedAccId);
    if (window.location.hash === target) return;
    if (!routingMountedRef.current) {
      // Primer render: replaceState (silencioso, no crea historial).
      window.history.replaceState(null, "", target);
      routingMountedRef.current = true;
    } else {
      // Navegación por sidebar / botón atrás: asignamos el hash directamente
      // para forzar refresco de la URL en la barra y disparar hashchange
      // sin depender de pushState (que en algunos navegadores no repinta).
      window.location.hash = target.replace(/^#/, "");
    }
  }, [user, page, selectedAccId]);

  // ─── ROUTING: listener back/forward → state ─────────────
  useEffect(() => {
    const onHashChange = () => {
      const parsed = parseHash();
      if (parsed.page) setPage(parsed.page);
      if (parsed.id) setSelectedAccId(parsed.id);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const selectedAcc = allowedClients?.find((c) => c.locationID === selectedAccId);

  const navigate = (p, accId) => {
    if (accId) setSelectedAccId(accId);
    setPage(p);
  };

  // 4. AUTH GATE: Si no hay usuario en sesión, va a la Landing.
  //    Limpiamos el hash de la URL para no dejar rutas fantasma tipo
  //    #/alarmas/AlojamientoX cuando ya no hay sesión activa.
  if (!user) {
    if (typeof window !== "undefined" && window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    return <LandingPage onAuth={() => {
      const saved = localStorage.getItem("easyrural_user");
      if (saved) setUser(JSON.parse(saved));
    }} />;
  }

  // Si la sesión existe pero la API no ha devuelto datos
  if (!allowedClients) {
    return (
      <div style={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center", background: "#F7F7F7" }}>
        <link href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700;800&family=Oooh+Baby&display=swap" rel="stylesheet" />
        <LoadingBox height={120} label="Cargando EasyRural…" />
      </div>
    );
  }

  if (clientsError) {
    return (
      <div style={{ padding: 40, fontFamily: "var(--font-sans)" }}>
        <h2>{t("common.error")}</h2>
        <ErrorBox error={clientsError} />
        <button onClick={refetchClients} style={{ marginTop: 16, background: "#406020", color: "#fff", border: "none", borderRadius: 10, padding: "10px 22px", cursor: "pointer", fontFamily: "var(--font-sans)", fontWeight: 600 }}>{t("common.retry")}</button>
      </div>
    );
  }

  const needsAcc = page !== "newClient" && page !== "editClient" && page !== "map" && page !== "adminOverview" && page !== "users";
  if (needsAcc && allowedClients.length === 0) {
    return (
      <>
        <link href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700;800&family=Oooh+Baby&display=swap" rel="stylesheet" />
        <EmptyState user={user}
          onGoMap={() => setPage("map")}
          onNewClient={() => setPage("newClient")} />
      </>
    );
  }

  if (needsAcc && !selectedAcc) {
    return (
      <div style={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center", background: "#F7F7F7" }}>
        <LoadingBox height={120} label={t("common.loading")} />
      </div>
    );
  }

  const openDash = (accId) => {
    // Si el card es borrador → editar; si no, abrir dashboard
    const acc = allowedClients?.find(c => c.locationID === accId);
    const plan = (acc?.plan_contratado || acc?.plan || "").toLowerCase();
    const isDraftClient = !plan || plan === "borrador" || acc?.estado === "borrador";
    if (isDraftClient) {
      setEditingId(accId);
      setPage("editClient");
      return;
    }
    setSelectedAccId(accId);
    setPage("dashboard");
  };

  const renderPage = () => {
    switch (page) {
      case "adminOverview":
        return isAdmin
          ? <AdminOverviewPage
              clients={allowedClients}
              onOpen={openDash}
              onNewClient={() => setPage("newClient")}
              onEdit={(accId) => { setEditingId(accId); setPage("editClient"); }}
            />
          : <DashboardPage acc={selectedAcc} />;
      case "status": return <DiagnosticoPage acc={selectedAcc} isAdmin={isAdmin} isMobile={isMobile} />;
      case "map": return <MapPage onNavigate={navigate} allowedClients={allowedClients} isAdmin={isAdmin} />;
      case "dashboard": return <DashboardPage acc={selectedAcc} />;
      case "consumption": return <ConsumptionPage acc={selectedAcc} />;
      case "forecast": return <ForecastPage acc={selectedAcc} />;
      case "contract": return <ContractPage acc={selectedAcc} />;
      case "savings": return <SavingsPage acc={selectedAcc} />;
      case "alarms": return <AlarmsPage acc={selectedAcc} />;
      case "actuators": return <ActuatorsPage acc={selectedAcc} />;
      case "install": return <InstallPage acc={selectedAcc} isAdmin={isAdmin} isMobile={isMobile} />;
      case "newClient": return <NewClientPage onCreated={refetchClients} />;
      case "editClient": return <NewClientPage editingId={editingId} onCreated={refetchClients} onDone={() => { setEditingId(null); setPage("adminOverview"); }} />;
      case "users":
        return isAdmin
          ? <UsersAccessPage clients={allowedClients} />
          : <DashboardPage acc={selectedAcc} />;
      default: return <DashboardPage acc={selectedAcc} />;
    }
  };

  const sW = isMobile ? 260 : (collapsed ? 60 : 220);

  return (
    <div className={isMobile ? "er-app er-mobile" : "er-app"} style={{ display: "flex", flexDirection: "column", height: "100dvh", overflow: "hidden", background: "#F7F7F7", fontFamily: "var(--font-sans)" }}>
      <link href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700;800&family=Oooh+Baby&display=swap" rel="stylesheet" />

      {/* HEADER ARRIBA */}
      <header className="er-header" style={{ height: isMobile ? 56 : 68, minHeight: isMobile ? 56 : 68, background: "#fff", borderBottom: "1px solid #E4E7E0", display: "flex", alignItems: "center", padding: isMobile ? "0 10px" : "0 24px", justifyContent: "space-between", flexShrink: 0, gap: isMobile ? 8 : 20, zIndex: 40 }}>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 24, flex: "0 0 auto" }}>
          {isMobile && (
            <button onClick={() => setNavOpen(o => !o)} aria-label={navOpen ? "Cerrar menú" : "Abrir menú"} aria-expanded={navOpen}
              style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid #D2DFC3", background: "#EBF2E4", color: "#406020", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">{navOpen ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> : <><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></>}</svg>
            </button>
          )}
          <img src="/easyrural-logo.png" alt="EasyRural" style={{ height: isMobile ? 30 : 46, width: "auto", display: "block" }} />
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, justifyContent: "center" }}>
          {/* SELECTOR: visible para admin o para dueños con ≥2 alojamientos */}
          {allowedClients && allowedClients.length > 1 ? (
            <label className="er-acc-select" style={{ position: "relative", display: "flex", alignItems: "center", gap: 10, padding: "6px 12px", borderRadius: 12, border: "1px solid transparent", cursor: "pointer" }}>
              {!isMobile && <span className="er-eyebrow">Alojamiento</span>}
              <select value={selectedAccId || ""} onChange={(e) => {
                  const newId = e.target.value;
                  setSelectedAccId(newId);
                  // Si estamos en una vista global (admin / users / mapa / newClient) y cambiamos
                  // de alojamiento, saltamos al Dashboard de ese alojamiento — así el usuario
                  // ve inmediatamente los datos y no queda "atascado" en la vista global.
                  const globalPages = new Set(["adminOverview", "users", "map", "newClient", "editClient"]);
                  if (globalPages.has(page)) setPage("dashboard");
                }}
                aria-label="Seleccionar alojamiento"
                style={{ appearance: "none", WebkitAppearance: "none", background: "transparent", border: "none", color: "#283226", fontFamily: "var(--font-serif)", fontSize: isMobile ? 15 : 18, maxWidth: isMobile ? 140 : undefined, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", fontWeight: 500, cursor: "pointer", outline: "none", letterSpacing: -0.2, paddingRight: 2 }}>
                {allowedClients.map((a) => <option key={a.locationID} value={a.locationID}>{a.nombre || a.locationID}</option>)}
              </select>
              <ChevronDown size={16} strokeWidth={1.8} color="#7D8478" />
            </label>
          ) : (
            // Si es un cliente normal, solo pintamos en texto plano el nombre de su casa sin dejarle cambiar
            <div style={{ color: "#283226", fontFamily: "var(--font-serif)", fontSize: 18, fontWeight: 500 }}>
              {selectedAcc?.nombre || allowedClients?.[0]?.nombre || allowedClients?.[0]?.locationID || user?.nombre}
            </div>
          )}
          {!isMobile && <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "#7D8478", paddingLeft: 14, borderLeft: "1px solid #E4E7E0" }}>{new Date().toLocaleDateString(lang === "ca" ? "ca-ES" : lang === "fr" ? "fr-FR" : lang === "en" ? "en-US" : "es-ES", { day: "numeric", month: "long", year: "numeric" })}</span>}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: "0 0 auto" }}>
          <LangSelector />
          <button onClick={logout} title={t("header.logout")}
            className="er-btn-ghost"
            style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "transparent", border: "1px solid #E4E7E0", borderRadius: 12, padding: "7px 12px", fontSize: 13, color: "#283226", cursor: "pointer", fontWeight: 500, fontFamily: "var(--font-sans)" }}>
            <LogOut size={15} strokeWidth={1.7} />{!isMobile && t("header.logout")}
          </button>
          <div style={{ width: 34, height: 34, borderRadius: 12, background: "#406020", display: isMobile ? "none" : "flex", alignItems: "center", justifyContent: "center", color: "#F7F7F7", fontSize: 14, fontWeight: 500, fontFamily: "var(--font-serif)", cursor: "pointer" }}>
            {user?.role === "admin" ? "A" : "U"}
          </div>
        </div>
      </header>

      {/* CUERPO: sidebar + main */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {isMobile && navOpen && <div onClick={() => setNavOpen(false)} style={{ position: "fixed", inset: "56px 0 0 0", background: "rgba(20,30,15,.45)", zIndex: 30 }} />}
        <aside role="navigation" aria-label="Menú principal" style={isMobile
          ? { position: "fixed", top: 56, bottom: 0, left: 0, width: sW, background: "#406020", display: "flex", flexDirection: "column", transform: navOpen ? "none" : "translateX(-105%)", transition: "transform 0.25s", overflowY: "auto", zIndex: 35, boxShadow: "none" }
          : { width: sW, minWidth: sW, background: "#406020", display: "flex", flexDirection: "column", transition: "all 0.25s", overflow: "hidden" }}>
          <nav style={{ padding: "16px 8px 4px", display: "flex", flexDirection: "column", gap: 4 }}>
            {NAV_ACTIVE.map((item, idx) => {
              const act = page === item.id;
              const newSection = item.section && (idx === 0 || NAV_ACTIVE[idx - 1].section !== item.section);
              const hov = hover === item.id;
              return (
                <React.Fragment key={item.id}>
                {newSection && (collapsed
                  ? (idx > 0 && <div aria-hidden style={{ margin: "8px 14px", borderTop: "1px solid rgba(244,241,234,0.12)" }} />)
                  : <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: 1.3, textTransform: "uppercase", color: "rgba(244,241,234,0.42)", padding: idx === 0 ? "2px 16px 6px" : "14px 16px 6px" }}>{t("nav.sec." + item.section) === "nav.sec." + item.section ? item.section : t("nav.sec." + item.section)}</div>)}
                <button onClick={() => { setPage(item.id); if (isMobile) setNavOpen(false); }}
                  onMouseEnter={() => setHover(item.id)} onMouseLeave={() => setHover(null)}
                  aria-current={act ? "page" : undefined}
                  aria-label={collapsed ? item.label : undefined}
                  className="er-nav-item"
                  style={{ position: "relative", display: "flex", alignItems: "center", gap: 12, padding: collapsed ? "12px" : "11px 16px", borderRadius: 10, border: "none", cursor: "pointer",
                    background: "transparent",
                    color: act ? "#F7F7F7" : hov ? "#F7F7F7" : "rgba(244,241,234,0.62)", textAlign: "left", justifyContent: collapsed ? "center" : "flex-start", fontFamily: "var(--font-sans)" }}
                  title={collapsed ? item.label : undefined}>
                  {act && <motion.div layoutId="nav-active" style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.16)", borderRadius: 999 }} transition={{ type: "spring", stiffness: 380, damping: 34 }} />}
                  <div style={{ position: "relative", width: 20, height: 20, flexShrink: 0 }}>{item.icon}</div>
                  {!collapsed && <span style={{ position: "relative", fontSize: 14, fontWeight: act ? 600 : 450, whiteSpace: "nowrap", letterSpacing: 0.1 }}>{item.label}</span>}
                </button>
                </React.Fragment>
              );
            })}
          </nav>
          <div style={{ margin: "8px 16px", borderTop: "1px solid rgba(255,255,255,0.15)" }} />
          <nav style={{ flex: 1, padding: "4px 8px", display: "flex", flexDirection: "column", gap: 4 }}>
            {NAV_DECO.map((item, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "center", gap: 12, padding: collapsed ? "10px" : "9px 16px", borderRadius: 10, color: "rgba(255,255,255,0.4)", justifyContent: collapsed ? "center" : "flex-start", cursor: "default", fontFamily: "var(--font-sans)" }} title={collapsed ? item.label : undefined}>
                <div style={{ width: 22, height: 22, flexShrink: 0 }}>{item.icon}</div>
                {!collapsed && <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap" }}>{item.label}</span>}
              </div>
            ))}
          </nav>
          <button onClick={() => setCollapsed(!collapsed)} aria-label={collapsed ? "Expandir menú lateral" : "Contraer menú lateral"} aria-expanded={!collapsed} style={{ margin: "12px 8px", padding: 10, background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 10, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: 18, height: 18, transform: collapsed ? "rotate(0)" : "rotate(180deg)", transition: "transform 0.25s" }}>{I.chev}</div>
          </button>
        </aside>
        <main role="main" aria-live="polite" className="er-main" style={{ flex: 1, minWidth: 0, overflow: "auto", display: "flex", flexDirection: "column" }}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={page + "|" + (selectedAccId || "")} style={{ flex: 1, minWidth: 0 }}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: [0.2, 0.7, 0.2, 1] }}>
              {renderPage()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
